import React, { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  Users, UsersRound, Calendar, CalendarDays, BarChart3, Settings, ChevronLeft, Menu,
  Plus, Edit2, Trash2, UserX, UserCheck, Download,
  Clock, X, Save, ArrowUpDown, Car, Train,
  MoreHorizontal, FileText, CalendarCheck, Check, XCircle,
  Calculator, DollarSign, Printer
} from 'lucide-react';
import { supabaseClient } from './lib/supabaseClient';
import { useAuth } from './auth/AuthProvider';
import { sortEmployeesDefault } from './lib/employeeSorter';
import { isActiveInPeriod } from './lib/employeePeriod';
import type { MonthlySummary } from './lib/types';
import { minutesToJa, firstDayOfMonth, toISODate } from './lib/types';
import {
  AdminEmployee,
  AdminAttendanceRecord,
  AttendanceRecord,
  MonthlyRecord,
  AttendanceShiftType,
  AttendanceStatus,
  QualificationMaster,
  LeaveRequest,
  LeaveRequestStatus,
  EmployeeCategory
} from './types/models';
import {
  ATTENDANCE_EVENT_NAME,
  ATTENDANCE_STORAGE_KEY,
  type AttendanceBroadcastDetail
} from './constants/attendanceSync';
import PayrollTab from './payroll/PayrollTab';
import ShiftManagementTab from './shift/ShiftManagementTab';
import SubsidySimulatorTab from './subsidy/SubsidySimulatorTab';
import AdminReportTabbed from './admin-report/AdminReportTabbed';
import MonthlyPrintTab from './monthly-print/MonthlyPrintTab';
import InventoryPage from './inventory/InventoryPage';
import MinuteEmployeeSettings from './attendance-settings/MinuteEmployeeSettings';
import { parsePayrollSourceState } from './payroll/labor-export/submissionRepository';
import { OvertimeScheduleSettings } from './overtime/OvertimeScheduleSettings';
import { OvertimeReviewTab } from './overtime/OvertimeReviewTab';
import {
  calculateAttendanceMinutes, calculateEffectiveBreakMinutes, preserveUnchangedPunchIso,
  resolveMinuteMode, roundAttendanceClockIn, type AttendanceCalculationRule
} from './lib/attendanceCalculation';

const EMPLOYEE_COLUMNS = 'id,employeeId,name,employment_type,job_type,birth_date,hire_date,experience_start_date,retirement_date,qualifications,transportation,night_shift_available,day_meal_provided,night_meal_provided,status,commute_distance_km,commute_cost_yen,employee_category,hourly_rate,night_shift_pay,special_benefit_unit_price,special_allowance_unit_price,part_special_allowance_unit_price,meal_deduction_per_day,commute_cost_monthly_fixed,meal_condition';
const PAYROLL_SETTINGS_COLUMNS = 'id,cutoff,round_clock_in_ceil_30,day_break_minutes,night_break_minutes,day_standard_hours,night_standard_hours';
const fetchPagedRows = async (query: () => any): Promise<Record<string, any>[]> => {
  const rows: Record<string, any>[] = [];
  const pageSize = 500;
  for (let offset = 0; offset < 50000; offset += pageSize) {
    const { data, error } = await query().range(offset, offset + pageSize - 1);
    if (error) throw error;
    if (!Array.isArray(data) || data.length > pageSize) throw new Error('一覧データを正しく取得できませんでした。再読み込みしてください。');
    rows.push(...data);
    if (data.length < pageSize) return rows;
  }
  throw new Error('取得件数が上限に達しました。対象期間を絞って再取得してください。');
};
const readCalculationRules = async (): Promise<AttendanceCalculationRule[]> => {
  if (!supabaseClient) throw new Error('データベースに接続できません。');
  const rows = await fetchPagedRows(() => supabaseClient!.from('attendance_calculation_rules')
    .select('employee_id,effective_from,mode').order('employee_id').order('effective_from'));
  return rows.map(row => {
    if ((row.mode !== 'minute' && row.mode !== 'default') || row.employee_id == null
        || !/^\d+$/.test(String(row.employee_id)) || !/^\d{4}-\d{2}-\d{2}$/.test(row.effective_from)
        || !Number.isFinite(Date.parse(row.effective_from))
        || new Date(row.effective_from).toISOString().slice(0, 10) !== row.effective_from) {
      throw new Error('集計ルールの形式を確認できませんでした。');
    }
    return { employeeId: String(row.employee_id), effectiveFrom: String(row.effective_from), mode: row.mode };
  });
};

type PunchShiftType = 'day' | 'night' | 'dayoff' | 'paid' | 'holiday_work';

type InlineNoteEditState = {
  key: string;
  value: string;
  saving: boolean;
  error: string | null;
};

const SHIFT_TYPE_OPTIONS: { value: PunchShiftType; label: string; needsTime: boolean }[] = [
  { value: 'day', label: '日勤', needsTime: true },
  { value: 'night', label: '夜勤', needsTime: true },
  { value: 'dayoff', label: '公休', needsTime: false },
  { value: 'paid', label: '有給', needsTime: false },
  { value: 'holiday_work', label: '休日出勤', needsTime: true }
];

const yenFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0
});

const formatDistanceKm = (value: number): string => {
  if (!Number.isFinite(value)) return String(value ?? '');
  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
};

const SHIFT_JIS_LEAD_BYTES: number[] = [
  ...Array.from({ length: 0x9F - 0x81 + 1 }, (_, i) => 0x81 + i),
  ...Array.from({ length: 0xEF - 0xE0 + 1 }, (_, i) => 0xE0 + i)
];

const SHIFT_JIS_TRAIL_BYTES: number[] = [
  ...Array.from({ length: 0x7E - 0x40 + 1 }, (_, i) => 0x40 + i),
  ...Array.from({ length: 0xFC - 0x80 + 1 }, (_, i) => 0x80 + i)
];

const SHIFT_JIS_DECODER: TextDecoder | null = (() => {
  if (typeof TextDecoder === 'undefined') {
    return null;
  }
  try {
    return new TextDecoder('shift_jis');
  } catch (error) {
    console.warn('Shift_JIS decoder is not available in this environment. Falling back to UTF-8 with BOM.', error);
    return null;
  }
})();

const SHIFT_JIS_PROBE_BUFFER = new Uint8Array(2);
const SHIFT_JIS_CACHE = new Map<string, number[]>();

const encodeShiftJIS = (input: string): Uint8Array => {
  if (!SHIFT_JIS_DECODER) {
    const encoder = new TextEncoder();
    return encoder.encode(`\uFEFF${input}`);
  }

  const bytes: number[] = [];

  for (const char of input) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) {
      continue;
    }

    if (codePoint <= 0x7F) {
      bytes.push(codePoint);
      continue;
    }

    if (codePoint >= 0xFF61 && codePoint <= 0xFF9F) {
      bytes.push(codePoint - 0xFF61 + 0xA1);
      continue;
    }

    let cached = SHIFT_JIS_CACHE.get(char);
    if (!cached) {
      cached = findShiftJISBytesForChar(char);
      SHIFT_JIS_CACHE.set(char, cached);
    }

    bytes.push(...cached);
  }

  return Uint8Array.from(bytes);
};

const findShiftJISBytesForChar = (char: string): number[] => {
  if (!SHIFT_JIS_DECODER) {
    return [0x3F];
  }

  for (const lead of SHIFT_JIS_LEAD_BYTES) {
    for (const trail of SHIFT_JIS_TRAIL_BYTES) {
      if (trail === 0x7F) {
        continue;
      }

      SHIFT_JIS_PROBE_BUFFER[0] = lead;
      SHIFT_JIS_PROBE_BUFFER[1] = trail;

      const decoded = SHIFT_JIS_DECODER.decode(SHIFT_JIS_PROBE_BUFFER);
      if (decoded === char) {
        return [lead, trail];
      }
    }
  }

  console.warn(`Character "${char}" cannot be represented in Shift_JIS. Using "?".`);
  return [0x3F];
};

const escapeCsvCell = (value: string | number | null | undefined): string => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\r') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const toCsvLine = (values: Array<string | number | null | undefined>): string =>
  values.map(escapeCsvCell).join(',');

const compareEmployeeCode = (
  aCode: string | number | null | undefined,
  bCode: string | number | null | undefined,
  aName: string,
  bName: string
): number => {
  const normalizedA = aCode !== null && aCode !== undefined ? String(aCode) : '';
  const normalizedB = bCode !== null && bCode !== undefined ? String(bCode) : '';

  if (normalizedA && normalizedB) {
    const numericCompare = normalizedA.localeCompare(normalizedB, 'ja', { numeric: true });
    if (numericCompare !== 0) {
      return numericCompare;
    }
  } else if (normalizedA) {
    return -1;
  } else if (normalizedB) {
    return 1;
  }

  return aName.localeCompare(bName, 'ja');
};

const SHIFT_TYPE_LABELS: Record<string, string> = {
  day: '日勤',
  night: '夜勤',
  dayoff: '公休',
  paid: '有給',
  holiday_work: '休日出勤'
};

const getClosingPeriodRange = (
  baseDate: Date,
  closingDay: string
): { start: Date; endExclusive: Date } => {
  const year = baseDate.getFullYear();
  const monthIndex = baseDate.getMonth();

  const fallback = {
    start: new Date(year, monthIndex, 1),
    endExclusive: new Date(year, monthIndex + 1, 1)
  };

  if (closingDay === 'month-end') {
    return fallback;
  }

  const day = Number(closingDay);
  if (Number.isNaN(day) || day < 1) {
    return fallback;
  }

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  if (day >= daysInMonth) {
    return fallback;
  }

  const endExclusive = new Date(year, monthIndex, day + 1);
  const start = new Date(endExclusive);
  start.setMonth(start.getMonth() - 1);

  return { start, endExclusive };
};

const computeMonthRange = (monthKey: string, closingDay: string): { start: string; end: string } => {
  if (/^\d{4}-\d{2}$/.test(monthKey)) {
    const [yearStr, monthStr] = monthKey.split('-');
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    if (!Number.isNaN(year) && !Number.isNaN(monthIndex)) {
      const { start, endExclusive } = getClosingPeriodRange(new Date(year, monthIndex, 1), closingDay);
      const endInclusive = new Date(endExclusive.getTime() - 24 * 60 * 60 * 1000);
      return { start: toISODate(start), end: toISODate(endInclusive) };
    }
  }
  return { start: `${monthKey}-01`, end: `${monthKey}-01` };
};

type CarryInAdjustments = { work: number; night: number };

const computeCarryInAdjustments = (
  rows: Array<Record<string, any>>,
  employeeByCode: Map<string, AdminEmployee>,
  periodStart: string,
  periodEndExclusive: string
): Map<string, CarryInAdjustments> => {
  const carryInMap = new Map<string, CarryInAdjustments>();

  rows.forEach(row => {
    const employeeCode = row.employee_code ? String(row.employee_code) : null;
    if (!employeeCode) return;

    const employee = employeeByCode.get(employeeCode);
    if (!employee) return;

    const workDateIso = String(row.work_date);
    if (!workDateIso || workDateIso >= periodStart) return;

    const shiftType = (row.duty_type ?? row.shift_type ?? 'day') as MonthlyRecord['shiftType'];
    const clockOutRaw = row.clock_out_at ?? row.clock_out ?? null;

    const crossesBoundary = (() => {
      if (clockOutRaw) {
        const clockOutDate = extractISODatePart(clockOutRaw);
        if (clockOutDate) {
          return clockOutDate !== workDateIso;
        }
      }
      return shiftType === 'night';
    })();

    if (!crossesBoundary) return;

    const baseDate = new Date(`${workDateIso}T00:00:00`);
    const nextDateIso = toISODate(new Date(baseDate.getTime() + 24 * 60 * 60 * 1000));

    if (nextDateIso < periodStart || nextDateIso >= periodEndExclusive) {
      return;
    }

    const current = carryInMap.get(employee.id) ?? { work: 0, night: 0 };
    current.work += 1;
    if (shiftType === 'night') {
      current.night += 1;
    }
    carryInMap.set(employee.id, current);
  });

  return carryInMap;
};

const buildSummaryFromRecords = (
  employee: AdminEmployee,
  records: MonthlyRecord[],
  monthKey: string,
  closingDay: string,
  calculateOvertime: (record: MonthlyRecord) => number,
  unconfirmedCount = 0,
  carryIn: { work: number; night: number } | undefined = undefined
): MonthlySummary | null => {
  const { start, end } = computeMonthRange(monthKey, closingDay);
  const totalWorkMinutes = records.reduce((sum, record) => sum + record.totalMinutes, 0);
  const weightedDayBase = records.reduce(
    (sum, record) => sum + getDayWeightForRecord(record, end, closingDay),
    0
  );
  const attendanceCountBase = records.reduce((sum, record) => sum + getShiftAttendanceWeight(record.shiftType), 0);
  const nightShiftCountBase = records.filter(record => record.shiftType === 'night').length;
  const overtimeMinutes = records.reduce(
    (sum, record) => sum + Math.max(0, calculateOvertime(record)),
    0
  );

  const carryWork = carryIn?.work ?? 0;
  const carryNight = carryIn?.night ?? 0;
  const daysWorked = weightedDayBase + carryWork;
  const attendanceCount = attendanceCountBase;
  const nightShiftCount = nightShiftCountBase;

  if (records.length === 0 && daysWorked === 0 && nightShiftCount === 0) {
    return null;
  }

  return {
    employee_code: employee.employeeCode ? String(employee.employeeCode) : '',
    period_start: start,
    period_end: end,
    records_count: records.length,
    days_worked: daysWorked,
    attendance_count: attendanceCount,
    total_work_minutes: totalWorkMinutes,
    total_work_hours: minutesToJa(totalWorkMinutes),
    overtime_minutes: overtimeMinutes,
    night_shift_count: nightShiftCount,
    unconfirmed_count: unconfirmedCount
  };
};

const getTodayString = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};
const getCurrentMonthString = (): string => getTodayString().slice(0, 7);

const getClosingAlignedMonthString = (todayIso: string, closingDay: string): string => {
  const [yearStr, monthStr, dayStr] = todayIso.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) {
    return getCurrentMonthString();
  }

  if (closingDay === 'month-end') {
    return `${yearStr}-${monthStr}`;
  }

  const closingDayNumber = Number(closingDay);
  if (!Number.isFinite(closingDayNumber) || closingDayNumber < 1) {
    return `${yearStr}-${monthStr}`;
  }

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const effectiveClosingDay = Math.min(closingDayNumber, daysInMonth);
  const offset = day > effectiveClosingDay ? 1 : 0;
  const target = new Date(year, monthIndex + offset, 1);
  const targetYear = target.getFullYear();
  const targetMonth = String(target.getMonth() + 1).padStart(2, '0');
  return `${targetYear}-${targetMonth}`;
};

const getShiftDayWeight = (shiftType: AttendanceShiftType | string | null | undefined): number => {
  switch (shiftType) {
    case 'dayoff':
      return 0;
    case 'paid':
      return 1;
    default:
      return 1;
  }
};

const getShiftAttendanceWeight = (shiftType: AttendanceShiftType | string | null | undefined): number => {
  switch (shiftType) {
    case 'dayoff':
      return 0;
    case 'paid':
      return 0;
    default:
      return 1;
  }
};

const getDayWeightForRecord = (
  record: MonthlyRecord,
  periodEndInclusive: string,
  closingDay: string
): number => {
  if (
    record.shiftType === 'night' &&
    record.clockOutDate &&
    record.clockOutDate > record.date
  ) {
    const startPeriod = getClosingAlignedMonthString(record.date, closingDay);
    const endPeriod = getClosingAlignedMonthString(record.clockOutDate, closingDay);
    if (startPeriod !== endPeriod) {
      return 1;
    }
    return record.clockOutDate <= periodEndInclusive ? 2 : 1;
  }
  return getShiftDayWeight(record.shiftType);
};

const toDisplayTime = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  if (value.includes(':')) {
    return value.slice(0, 5);
  }
  return value;
};

const extractISODatePart = (value: unknown): string | null => {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return toISODate(parsed);
    }
    return text.slice(0, 10);
  }
  return null;
};

const addDaysToISODate = (baseDate: string, days: number): string => {
  const base = new Date(`${baseDate}T00:00:00`);
  base.setDate(base.getDate() + days);
  return toISODate(base);
};

const extractTimeComponents = (value: string | null | undefined): { hours: number; minutes: number } | null => {
  if (!value) return null;
  const match = value.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return { hours, minutes };
};

const canClockOutRollOverToNextDay = (
  shiftType: AttendanceShiftType | string | null | undefined,
  clockIn: string | null | undefined,
  clockOut: string | null | undefined
): boolean => {
  if (!clockOut) return false;

  const outComponents = extractTimeComponents(clockOut);
  const inComponents = clockIn ? extractTimeComponents(clockIn) : null;
  const outMinutes = outComponents ? outComponents.hours * 60 + outComponents.minutes : null;
  const inMinutes = inComponents ? inComponents.hours * 60 + inComponents.minutes : null;

  if (outMinutes !== null && inMinutes !== null) return outMinutes < inMinutes;
  return shiftType === 'night' && !!outComponents && outComponents.hours < 12;
};

const determineClockOutDate = (
  baseDate: string,
  shiftType: AttendanceShiftType | string | null | undefined,
  clockIn: string | null | undefined,
  clockOut: string | null | undefined,
  providedDate: string | null | undefined
): string | null => {
  if (!clockOut) return providedDate ?? null;
  if (providedDate) return providedDate;
  return canClockOutRollOverToNextDay(shiftType, clockIn, clockOut) ? addDaysToISODate(baseDate, 1) : baseDate;
};

const normalizeEmployeeKey = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return String(value).trim().toLowerCase();
};

const normalizeEmployeeRow = (row: Record<string, any>): AdminEmployee | null => {
  const idValue = row.id ?? row.employee_id ?? row.employeeId ?? null;
  const nameValue = row.name ?? row.full_name ?? row.fullName ?? idValue;

  if (!idValue || !nameValue) {
    return null;
  }

  return {
    id: String(idValue),
    name: String(nameValue),
    employmentType: String(row.employment_type ?? row.employmentType ?? '未設定'),
    jobType: String(row.job_type ?? row.jobType ?? '未設定'),
    employeeCode: (() => {
      const code =
        row.employee_code ??
        row.employeeCode ??
        row.employeeId ??
        row.employee_id ??
        null;
      return code ? String(code) : undefined;
    })(),
    birthDate:
      row.birth_date ??
      row.birthDate ??
      row.date_of_birth ??
      row.dateOfBirth ??
      row.birthday ??
      undefined,
    hireDate: row.hire_date ?? row.hireDate ?? undefined,
    experienceStartDate: row.experience_start_date ?? row.experienceStartDate ?? undefined,
    retirementDate: row.retirement_date ?? row.retirementDate ?? undefined,
    qualifications: Array.isArray(row.qualifications)
      ? row.qualifications.map((q: unknown) => String(q))
      : [],
    transportation: (row.transportation ?? row.transportation_method) === '公共交通機関'
      ? '公共交通機関'
      : '車',
    nightShiftAvailable: Boolean(row.night_shift_available ?? row.nightShiftAvailable ?? false),
    dayMealProvided: Boolean(row.day_meal_provided ?? row.dayMealProvided ?? false),
    nightMealProvided: Boolean(row.night_meal_provided ?? row.nightMealProvided ?? false),
    status: row.status === 'retired' ? 'retired' : 'active',
    commuteDistanceKm: (() => {
      const value = row.commute_distance_km ?? row.commuteDistanceKm ?? null;
      return value !== null && value !== undefined ? Number(value) : null;
    })(),
    commuteCostYen: (() => {
      const value = row.commute_cost_yen ?? row.commuteCostYen ?? null;
      return value !== null && value !== undefined ? Number(value) : null;
    })(),
    employeeCategory: row.employee_category ?? row.employeeCategory ?? null,
    hourlyRate: row.hourly_rate ?? row.hourlyRate ?? null,
    nightShiftPay: row.night_shift_pay ?? row.nightShiftPay ?? null,
    specialBenefitUnitPrice: row.special_benefit_unit_price ?? row.specialBenefitUnitPrice ?? null,
    specialAllowanceUnitPrice: row.special_allowance_unit_price ?? row.specialAllowanceUnitPrice ?? null,
    partSpecialAllowanceUnitPrice: (() => {
      const value = row.part_special_allowance_unit_price ?? row.partSpecialAllowanceUnitPrice ?? null;
      return value !== null && value !== undefined ? Number(value) : null;
    })(),
    mealDeductionPerDay: Number(row.meal_deduction_per_day ?? row.mealDeductionPerDay ?? 250),
    commuteCostMonthlyFixed: (() => {
      const value = row.commute_cost_monthly_fixed ?? row.commuteCostMonthlyFixed ?? null;
      return value !== null && value !== undefined ? Number(value) : null;
    })(),
    mealCondition: (row.meal_condition ?? row.mealCondition ?? 'always') as 'always' | 'long_shift_only',
  };
};

const generateEmployeeIdentifier = (): string => {
  const now = new Date();
  const timestamp = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1)
    .toString()
    .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now
      .getHours()
      .toString()
      .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
  const random = Math.random().toString(36).toUpperCase().slice(2, 6);
  return `EMP-${timestamp}-${random}`;
};

const combineDateAndTimeToIso = (
  date: string,
  time: string | null,
  shiftType: AttendanceShiftType,
  clockInTime: string | null,
  options?: {
    explicitDate?: string | null;
  }
): string | null => {
  if (!time) return null;
  const targetDate = options?.explicitDate ?? date;
  const base = new Date(`${targetDate}T${time}:00+09:00`);
  if (Number.isNaN(base.getTime())) {
    return null;
  }
  if (!options?.explicitDate && shiftType === 'night' && clockInTime) {
    const clockInDate = new Date(`${date}T${clockInTime}:00+09:00`);
    if (!Number.isNaN(clockInDate.getTime()) && base.getTime() < clockInDate.getTime()) {
      base.setDate(base.getDate() + 1);
    }
  }
  return base.toISOString();
};

const getDailyStatusBadge = (
  record: AdminAttendanceRecord
): { label: string; className: string } => {
  const rawStatus = record.status ? String(record.status).trim() : '';
  const normalizedStatus = rawStatus.toLowerCase();
  const normalizedShift = record.shiftType ? String(record.shiftType).trim().toLowerCase() : '';
  const matchesStatus = (...candidates: string[]) =>
    candidates.some(
      (candidate) => candidate === rawStatus || candidate === normalizedStatus
    );

  if (record.clockIn && !record.clockOut) {
    return { label: '出勤中', className: 'bg-yellow-100 text-yellow-800' };
  }

  if (record.clockIn && record.clockOut) {
    return { label: '退勤済み', className: 'bg-green-100 text-green-800' };
  }

  if (matchesStatus('absent', '欠勤')) {
    return { label: '欠勤', className: 'bg-red-100 text-red-800' };
  }

  if (matchesStatus('pending', '調整中', '確認待ち')) {
    return { label: '確認待ち', className: 'bg-orange-100 text-orange-800' };
  }

  if (matchesStatus('completed', '確定', '退勤済み', 'done', 'approved')) {
    return { label: '退勤済み', className: 'bg-green-100 text-green-800' };
  }

  if (matchesStatus('working', '未確定', '勤務中', '出勤中', 'in_progress')) {
    return { label: '出勤中', className: 'bg-yellow-100 text-yellow-800' };
  }

  if (matchesStatus('dayoff', '公休') || normalizedShift === 'dayoff') {
    return { label: '公休', className: 'bg-blue-100 text-blue-800' };
  }

  if (matchesStatus('paid', '有給', 'paid_leave') || normalizedShift === 'paid') {
    return { label: '有給', className: 'bg-blue-100 text-blue-800' };
  }

  if (normalizedShift === 'holiday_work') {
    return { label: '休日出勤', className: 'bg-purple-100 text-purple-800' };
  }

  if (rawStatus) {
    return { label: rawStatus, className: 'bg-gray-100 text-gray-800' };
  }

  return { label: '未打刻', className: 'bg-gray-100 text-gray-800' };
};

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('employees');
  const [adminReportVisited, setAdminReportVisited] = useState(false);
  useEffect(() => { if (activeMenu === 'adminReport') setAdminReportVisited(true); }, [activeMenu]);
  const [employeeTab, setEmployeeTab] = useState<'active' | 'retired'>('active');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<AdminEmployee | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => getTodayString());
  const [selectedMonth, setSelectedMonth] = useState<string>(() =>
    getClosingAlignedMonthString(getTodayString(), 'month-end')
  );
  const [hasManualMonthSelection, setHasManualMonthSelection] = useState(false);
  const [selectedEmployeeForReport, setSelectedEmployeeForReport] = useState<string>('');
  const [employeeCodeInputForMonthly, setEmployeeCodeInputForMonthly] = useState<string>('');
  const [sortBy, setSortBy] = useState<keyof AdminEmployee | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [jobTypeFilter, setJobTypeFilter] = useState<'all' | 'care' | 'nurse' | 'cook' | 'other'>('all');
  const [showEditAttendanceModal, setShowEditAttendanceModal] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<AdminAttendanceRecord | null>(null);
  const [showEditMonthlyModal, setShowEditMonthlyModal] = useState(false);
  const [editingMonthlyRecord, setEditingMonthlyRecord] = useState<MonthlyRecord | null>(null);
  const [inlineNoteEdit, setInlineNoteEdit] = useState<InlineNoteEditState | null>(null);
  const [showManualAttendanceModal, setShowManualAttendanceModal] = useState(false);
  const [showBulkPunchModal, setShowBulkPunchModal] = useState(false);
  const [manualAttendanceInitialEmployeeId, setManualAttendanceInitialEmployeeId] = useState<string | null>(null);
  const [closingDay, setClosingDay] = useState('month-end');
  const previousClosingDayRef = useRef(closingDay);
  const [roundUpClockIn, setRoundUpClockIn] = useState(true);
  const [dayBreakMinutes, setDayBreakMinutes] = useState<number>(60);
  const [nightBreakMinutes, setNightBreakMinutes] = useState<number>(120);
  const [dayOvertimeBaseHours, setDayOvertimeBaseHours] = useState<number>(8);
  const [nightOvertimeBaseHours, setNightOvertimeBaseHours] = useState<number>(14);
  const [payrollSettingsId, setPayrollSettingsId] = useState<string | null>(null);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [calculationRules, setCalculationRules] = useState<AttendanceCalculationRule[]>([]);
  const [calculationRulesLoading, setCalculationRulesLoading] = useState(true);
  const [calculationRulesError, setCalculationRulesError] = useState<string | null>(null);
  type SettingsSnapshot = {
    closingDay: string;
    roundUpClockIn: boolean;
    dayBreakMinutes: number;
    nightBreakMinutes: number;
    dayOvertimeBaseHours: number;
    nightOvertimeBaseHours: number;
  };
  const initialSettingsRef = useRef<SettingsSnapshot | null>(null);
  const [activeQualificationsEmployeeId, setActiveQualificationsEmployeeId] = useState<string | null>(null);
  const [qualifications, setQualifications] = useState<QualificationMaster[]>([]);
  const [qualificationsLoading, setQualificationsLoading] = useState<boolean>(false);
  const [qualificationsError, setQualificationsError] = useState<string | null>(null);
  const [qualificationMutationLoading, setQualificationMutationLoading] = useState<boolean>(false);
  const [qualificationMutationError, setQualificationMutationError] = useState<string | null>(null);
  const [showQualificationManager, setShowQualificationManager] = useState<boolean>(false);

  const { user, role } = useAuth();
  const isShiftOnly = role === 'shift' || user?.email === 'shift@satsukihome.com';
  useEffect(() => { if (isShiftOnly) setActiveMenu('shift'); }, [isShiftOnly]);
  const selectedMonthRange = useMemo(
    () => computeMonthRange(selectedMonth, closingDay),
    [selectedMonth, closingDay]
  );

  useEffect(() => {
    const alignedMonth = getClosingAlignedMonthString(getTodayString(), closingDay);
    if (previousClosingDayRef.current !== closingDay) {
      previousClosingDayRef.current = closingDay;
      setHasManualMonthSelection(false);
      setSelectedMonth(prev => (prev === alignedMonth ? prev : alignedMonth));
      return;
    }
    if (!hasManualMonthSelection) {
      setSelectedMonth(prev => (prev === alignedMonth ? prev : alignedMonth));
    }
  }, [closingDay, hasManualMonthSelection]);

  const adminDisplayName = useMemo(() => {
    const metadataName = (
      (user?.user_metadata?.full_name as string | undefined) ??
      (user?.user_metadata?.name as string | undefined) ??
      (user?.user_metadata?.display_name as string | undefined) ??
      ''
    ).trim();
    if (metadataName) {
      return metadataName;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return '管理者';
  }, [user]);

  const adminEmail = user?.email ?? 'メールアドレス未設定';

  const adminInitial = useMemo(() => {
    const source = adminDisplayName || adminEmail;
    return source ? source.charAt(0).toUpperCase() : 'A';
  }, [adminDisplayName, adminEmail]);

  const [employees, setEmployees] = useState<AdminEmployee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState<boolean>(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  const [attendanceData, setAttendanceData] = useState<Record<string, AdminAttendanceRecord[]>>({});
  const [attendanceLoading, setAttendanceLoading] = useState<boolean>(true);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [employeeLookupMap, setEmployeeLookupMap] = useState<Map<string, AdminEmployee>>(new Map());

  const [monthlyData, setMonthlyData] = useState<Record<string, MonthlyRecord[]>>({});
  const [monthlySummaries, setMonthlySummaries] = useState<Record<string, MonthlySummary | null>>({});
  const [monthlyCarryInAdjustments, setMonthlyCarryInAdjustments] = useState<Record<string, CarryInAdjustments>>({});
  const [monthlyLoading, setMonthlyLoading] = useState<boolean>(true);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);
  const [employeeMutationLoading, setEmployeeMutationLoading] = useState<boolean>(false);
  const [employeeMutationError, setEmployeeMutationError] = useState<string | null>(null);
  const [settingsSaveNotice, setSettingsSaveNotice] = useState<string | null>(null);
  const settingsSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const monthlyRequestIdRef = useRef(0);
  const attendanceRequestIdRef = useRef(0);
  const [payrollSourceFingerprint, setPayrollSourceFingerprint] = useState<string | undefined>();
  const [payrollSourceError, setPayrollSourceError] = useState<string | undefined>();

  // 有給申請管理
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leaveRequestsLoading, setLeaveRequestsLoading] = useState<boolean>(false);
  const [leaveRequestsError, setLeaveRequestsError] = useState<string | null>(null);
  const [pendingLeaveCount, setPendingLeaveCount] = useState<number>(0);
  const [leaveRequestFilter, setLeaveRequestFilter] = useState<'all' | LeaveRequestStatus>('all');
  const [rejectingRequest, setRejectingRequest] = useState<LeaveRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [selectedLeaveRequestIds, setSelectedLeaveRequestIds] = useState<string[]>([]);
  const DEFAULT_SETTINGS: SettingsSnapshot = {
    closingDay: 'month-end',
    roundUpClockIn: true,
    dayBreakMinutes: 60,
    nightBreakMinutes: 120,
    dayOvertimeBaseHours: 8,
    nightOvertimeBaseHours: 14
  };

  const cutoffToClosingDay = (cutoff: string | null | undefined): string => {
    switch (cutoff) {
      case 'DAY_15':
        return '15';
      case 'DAY_20':
        return '20';
      case 'DAY_25':
        return '25';
      case 'EOM':
      default:
        return 'month-end';
    }
  };

  const closingDayToCutoff = (value: string): string => {
    switch (value) {
      case '15':
        return 'DAY_15';
      case '20':
        return 'DAY_20';
      case '25':
        return 'DAY_25';
      case 'month-end':
      default:
        return 'EOM';
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      setSettingsLoading(true);
      setSettingsError(null);
      try {
        if (!supabaseClient) {
          throw new Error('設定を読み込むための Supabase クライアントが初期化されていません。');
        }

        const { data, error } = await supabaseClient
          .from('payroll_settings')
          .select(
            'id, cutoff, round_clock_in_ceil_30, day_break_minutes, night_break_minutes, day_standard_hours, night_standard_hours'
          )
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (!isMounted) return;

        if (data) {
          const nextSnapshot: SettingsSnapshot = {
            closingDay: cutoffToClosingDay(data.cutoff ?? 'EOM'),
            roundUpClockIn: Boolean(data.round_clock_in_ceil_30),
            dayBreakMinutes: data.day_break_minutes ?? DEFAULT_SETTINGS.dayBreakMinutes,
            nightBreakMinutes: data.night_break_minutes ?? DEFAULT_SETTINGS.nightBreakMinutes,
            dayOvertimeBaseHours: data.day_standard_hours ?? DEFAULT_SETTINGS.dayOvertimeBaseHours,
            nightOvertimeBaseHours:
              data.night_standard_hours ?? DEFAULT_SETTINGS.nightOvertimeBaseHours
          };
          setPayrollSettingsId(data.id ?? null);
          setClosingDay(nextSnapshot.closingDay);
          setRoundUpClockIn(nextSnapshot.roundUpClockIn);
          setDayBreakMinutes(nextSnapshot.dayBreakMinutes);
          setNightBreakMinutes(nextSnapshot.nightBreakMinutes);
          setDayOvertimeBaseHours(nextSnapshot.dayOvertimeBaseHours);
          setNightOvertimeBaseHours(nextSnapshot.nightOvertimeBaseHours);
          initialSettingsRef.current = nextSnapshot;
        } else {
          initialSettingsRef.current = DEFAULT_SETTINGS;
          setClosingDay(DEFAULT_SETTINGS.closingDay);
          setRoundUpClockIn(DEFAULT_SETTINGS.roundUpClockIn);
          setDayBreakMinutes(DEFAULT_SETTINGS.dayBreakMinutes);
          setNightBreakMinutes(DEFAULT_SETTINGS.nightBreakMinutes);
          setDayOvertimeBaseHours(DEFAULT_SETTINGS.dayOvertimeBaseHours);
          setNightOvertimeBaseHours(DEFAULT_SETTINGS.nightOvertimeBaseHours);
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Failed to load payroll settings', err);
        setSettingsError(`設定を読み込めませんでした: ${err?.message ?? String(err)}`);
        if (!initialSettingsRef.current) {
          initialSettingsRef.current = DEFAULT_SETTINGS;
        }
      } finally {
        if (isMounted) {
          setSettingsLoading(false);
        }
      }
    };

    void loadSettings();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseClient]);

  useEffect(() => {
    initialSettingsRef.current = {
      closingDay,
      roundUpClockIn,
      dayBreakMinutes,
      nightBreakMinutes,
      dayOvertimeBaseHours,
      nightOvertimeBaseHours
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSettingsDirty = (() => {
    const initial = initialSettingsRef.current;
    if (!initial) {
      return false;
    }
    return (
      closingDay !== initial.closingDay ||
      roundUpClockIn !== initial.roundUpClockIn ||
      dayBreakMinutes !== initial.dayBreakMinutes ||
      nightBreakMinutes !== initial.nightBreakMinutes ||
      dayOvertimeBaseHours !== initial.dayOvertimeBaseHours ||
      nightOvertimeBaseHours !== initial.nightOvertimeBaseHours
    );
  })();

  const loadCalculationRules = useCallback(async () => {
    setCalculationRulesLoading(true);
    setCalculationRulesError(null);
    try {
      const next = await readCalculationRules();
      setCalculationRules(next);
      return next;
    } catch (cause) {
      setCalculationRulesError('従業員別の集計設定を取得できませんでした。再読み込みしてください。');
      throw cause;
    } finally {
      setCalculationRulesLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!isShiftOnly) void loadCalculationRules().catch(() => undefined);
  }, [isShiftOnly, loadCalculationRules]);
  const saveMinuteTargets = useCallback(async (selectedIds: string[], effectiveFrom: string): Promise<void> => {
    if (!supabaseClient) throw new Error('データベースに接続できません。');
    const { error } = await supabaseClient.rpc('save_attendance_minute_targets', {
      p_employee_ids: selectedIds, p_effective_from: effectiveFrom
    });
    if (error) throw new Error(`対象者を保存できませんでした: ${error.message}`);
    const savedRules = await loadCalculationRules();
    const selected = new Set(selectedIds);
    const ids = new Set([...employees.map(employee => employee.id), ...savedRules.map(rule => rule.employeeId), ...selectedIds]);
    if (Array.from(ids).some(id => resolveMinuteMode(savedRules, id, effectiveFrom) !== selected.has(id))) {
      throw new Error('保存後の対象者が指定内容と一致しません。再読み込みして確認してください。');
    }
  }, [loadCalculationRules, employees]);
  const calculationSettingsError = calculationRulesError ?? settingsError
    ?? (isSettingsDirty ? '基本設定に未保存の変更があります。設定を保存してから集計してください。' : null);
  const calculationReady = !calculationRulesLoading && !settingsLoading && !calculationSettingsError;

  const roundUpTime = useCallback((timeString: string | undefined, employeeId?: string, workDate?: string): string | undefined =>
    roundAttendanceClockIn(timeString, { roundUpClockIn, minuteMode: resolveMinuteMode(calculationRules, employeeId, workDate) }),
  [roundUpClockIn, calculationRules]);

  const calculateActualMinutes = useCallback((
    clockIn: string | undefined,
    clockOut: string | undefined,
    breakMinutes: number,
    shiftType: AttendanceShiftType | string,
    options?: {
      baseDate?: string | null;
      clockInDate?: string | null;
      clockOutDate?: string | null;
      employeeId?: string;
      workDate?: string;
      applyDefaultBreak?: boolean;
    }
  ): number => {
    if (!clockIn || !clockOut) return 0;
    const elapsed = calculateAttendanceMinutes({
      clockIn, clockOut, shiftType, roundUpClockIn, ...options,
      minuteMode: resolveMinuteMode(calculationRules, options?.employeeId, options?.workDate ?? options?.baseDate)
    });
    if (elapsed <= 0) return 0;
    const effectiveBreak = options?.applyDefaultBreak === false ? breakMinutes
      : calculateEffectiveBreakMinutes(elapsed, breakMinutes, shiftType, { dayBreakMinutes, nightBreakMinutes });
    return Math.max(0, elapsed - effectiveBreak);
  }, [roundUpClockIn, calculationRules, dayBreakMinutes, nightBreakMinutes]);

  const calculateOvertime = useCallback((record: MonthlyRecord): number => {
    const breakMinutes = Number.isFinite(record.breakMinutes) ? record.breakMinutes : 0;
    const overtimeBaseMinutes = record.totalMinutes + Math.min(0, breakMinutes);
    const dayBaseMinutes = Math.max(0, Math.round(dayOvertimeBaseHours * 60));
    const nightBaseMinutes = Math.max(0, Math.round(nightOvertimeBaseHours * 60));

    let standardMinutes: number | null = null;
    if (record.shiftType === 'night') {
      standardMinutes = nightBaseMinutes;
    } else if (record.shiftType === 'day' || record.shiftType === 'holiday_work') {
      standardMinutes = dayBaseMinutes;
    }

    if (standardMinutes === null) {
      return 0;
    }

    return Math.max(0, overtimeBaseMinutes - standardMinutes);
  }, [dayOvertimeBaseHours, nightOvertimeBaseHours]);

  const fetchEmployees = useCallback(async (): Promise<AdminEmployee[]> => {
    setEmployeesLoading(true);
    setEmployeesError(null);

    if (!supabaseClient) {
      setEmployees([]);
      setEmployeesError('SupabaseのURLまたは匿名キーが設定されていません。`.env.local` を確認してください。');
      setEmployeesLoading(false);
      return [];
    }

    try {
      const data = await fetchPagedRows(() => supabaseClient!.from('employees')
        .select(EMPLOYEE_COLUMNS).order('name', { ascending: true }).order('id'));

      const normalized = (data ?? [])
        .map(normalizeEmployeeRow)
        .filter((emp): emp is AdminEmployee => emp !== null);

      setEmployees(normalized);
      setSelectedEmployeeForReport(prev => {
        if (prev && normalized.some(emp => emp.id === prev)) {
          return prev;
        }
        const firstActive = normalized.find(emp => emp.status === 'active');
        return firstActive?.id ?? '';
      });
      setEmployeesError(null);
      return normalized;
    } catch (error: any) {
      const message =
        error?.code === '42501'
          ? '従業員データを取得する権限がありません。Supabase の RLS ポリシーを確認してください。'
          : `従業員データの取得に失敗しました: ${error?.message ?? String(error)}`;
      setEmployees([]);
      setEmployeesError(message);
      return [];
    } finally {
      setEmployeesLoading(false);
    }
  }, [supabaseClient]);

  const fetchQualifications = useCallback(async (): Promise<QualificationMaster[]> => {
    setQualificationsLoading(true);
    setQualificationsError(null);

    if (!supabaseClient) {
      setQualifications([]);
      setQualificationsError('SupabaseのURLまたは匿名キーが設定されていません。`.env.local` を確認してください。');
      setQualificationsLoading(false);
      return [];
    }

    try {
      const { data, error } = await supabaseClient
        .from('qualification_master')
        .select('*')
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        if (error.code === '42P01') {
          setQualifications([]);
          setQualificationsError('qualification_master テーブルが見つかりません。Supabase でテーブルを作成してください。');
          return [];
        }
        if (error.code === '42501') {
          setQualifications([]);
          setQualificationsError('資格データを取得する権限がありません。Supabase の RLS ポリシーを確認してください。');
          return [];
        }
        throw error;
      }

      const normalized = (data ?? []).map((row: Record<string, any>) => ({
        id: String(row.id),
        name: String(row.name),
        displayOrder: Number(row.display_order ?? row.displayOrder ?? 0),
        isActive: Boolean(row.is_active ?? row.isActive ?? true)
      })) as QualificationMaster[];

      setQualifications(normalized);
      return normalized;
    } catch (error: any) {
      setQualifications([]);
      setQualificationsError(`資格データの取得に失敗しました: ${error?.message ?? String(error)}`);
      return [];
    } finally {
      setQualificationsLoading(false);
    }
  }, [supabaseClient]);

  useEffect(() => {
    void fetchQualifications();
  }, [fetchQualifications]);

  // 有給申請データを取得
  const fetchLeaveRequests = useCallback(async () => {
    if (!supabaseClient) return;

    setLeaveRequestsLoading(true);
    setLeaveRequestsError(null);

    try {
      const { data, error } = await supabaseClient
        .from('leave_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          setLeaveRequests([]);
          setLeaveRequestsError('leave_requests テーブルが見つかりません。');
          return;
        }
        throw error;
      }

      const mapped: LeaveRequest[] = (data ?? []).map((row: Record<string, any>) => {
        const emp = employees.find(e => e.employeeCode === row.employee_code);
        return {
          id: row.id,
          employeeCode: row.employee_code,
          employeeName: emp?.name ?? row.employee_code,
          startDate: row.start_date,
          endDate: row.end_date,
          status: row.status as LeaveRequestStatus,
          rejectionReason: row.rejection_reason,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          approvedAt: row.approved_at,
          cancelledAt: row.cancelled_at ?? null,
          cancellationReason: row.cancellation_reason ?? null
        };
      });

      setLeaveRequests(mapped);
      setPendingLeaveCount(mapped.filter(r => r.status === 'pending').length);
    } catch (err: any) {
      setLeaveRequestsError(`有給申請データの取得に失敗しました: ${err?.message ?? String(err)}`);
    } finally {
      setLeaveRequestsLoading(false);
    }
  }, [supabaseClient, employees]);

  useEffect(() => {
    if (employees.length > 0) {
      void fetchLeaveRequests();
    }
  }, [fetchLeaveRequests, employees]);

  // 月次レポート用の従業員コード入力ハンドラー
  const handleMonthlyEmployeeCodeChange = (value: string): void => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
    setEmployeeCodeInputForMonthly(digitsOnly);

    if (!digitsOnly) {
      setSelectedEmployeeForReport('');
      return;
    }

    const exactMatch = employees.find((emp) =>
      emp.employeeCode ? emp.employeeCode.toLowerCase() === digitsOnly.toLowerCase() : false
    );

    if (exactMatch) {
      setSelectedEmployeeForReport(exactMatch.id);
    } else {
      setSelectedEmployeeForReport('');
    }
  };

  const buildLeaveDateRange = (startDateIso: string, endDateIso: string): string[] => {
    const start = new Date(`${startDateIso}T00:00:00`);
    const end = new Date(`${endDateIso}T00:00:00`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      throw new Error('有給期間の日付形式が不正です。');
    }
    if (start.getTime() > end.getTime()) {
      throw new Error('有給期間の日付範囲が不正です。');
    }

    const dates: string[] = [];
    const cursor = new Date(start);
    while (cursor.getTime() <= end.getTime()) {
      dates.push(toISODate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  };

  const upsertPaidAttendanceRecordsForLeave = async (request: LeaveRequest): Promise<void> => {
    if (!supabaseClient) {
      throw new Error('Supabaseの設定が見つかりません。');
    }

    const workDates = buildLeaveDateRange(request.startDate, request.endDate);
    const payload = workDates.map(workDate => ({
      employee_code: request.employeeCode,
      work_date: workDate,
      duty_type: 'paid',
      status: '確定',
      clock_in_at: null,
      clock_out_at: null,
      break_minutes: 0
    }));

    const { error } = await supabaseClient
      .from('attendance_records')
      .upsert(payload, { onConflict: 'employee_code,work_date' });

    if (error) {
      throw error;
    }
  };

  const approveLeaveRequestWithAttendance = async (request: LeaveRequest): Promise<void> => {
    if (!supabaseClient) {
      throw new Error('Supabaseの設定が見つかりません。');
    }

    const { error: approveError } = await supabaseClient
      .from('leave_requests')
      .update({
        status: 'approved',
        approved_at: new Date().toISOString(),
        rejection_reason: null
      })
      .eq('id', request.id);

    if (approveError) {
      throw approveError;
    }

    try {
      await upsertPaidAttendanceRecordsForLeave(request);
    } catch (error: any) {
      const { error: rollbackError } = await supabaseClient
        .from('leave_requests')
        .update({
          status: 'pending',
          approved_at: null
        })
        .eq('id', request.id);

      if (rollbackError) {
        throw new Error(
          `有給レコード作成に失敗し、承認状態のロールバックにも失敗しました: ${error?.message ?? String(error)} / ${rollbackError.message}`
        );
      }

      throw new Error(`有給レコード作成に失敗したため、承認状態を元に戻しました: ${error?.message ?? String(error)}`);
    }
  };

  // 有給申請を承認
  const handleApproveLeaveRequest = async (request: LeaveRequest): Promise<void> => {
    if (!supabaseClient) return;

    try {
      await approveLeaveRequestWithAttendance(request);
      await fetchLeaveRequests();
      alert('有給申請を承認しました');
    } catch (err: any) {
      alert(`承認に失敗しました: ${err?.message ?? String(err)}`);
    }
  };

  // 有給申請を却下
  const handleRejectLeaveRequest = async (): Promise<void> => {
    if (!supabaseClient || !rejectingRequest) return;

    if (!rejectionReason.trim()) {
      alert('却下理由を入力してください');
      return;
    }

    try {
      const { error } = await supabaseClient
        .from('leave_requests')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason.trim()
        })
        .eq('id', rejectingRequest.id);

      if (error) throw error;

      await fetchLeaveRequests();
      setRejectingRequest(null);
      setRejectionReason('');
      alert('有給申請を却下しました');
    } catch (err: any) {
      alert(`却下に失敗しました: ${err?.message ?? String(err)}`);
    }
  };

  const cancelLeaveRequestWithAttendance = async (request: LeaveRequest, reason: string): Promise<void> => {
    if (!supabaseClient) {
      throw new Error('Supabaseの設定が見つかりません。');
    }

    const workDates = buildLeaveDateRange(request.startDate, request.endDate);
    const cancelledAt = new Date().toISOString();

    const { error: updateError } = await supabaseClient
      .from('leave_requests')
      .update({
        status: 'cancelled',
        cancellation_reason: reason,
        cancelled_at: cancelledAt,
        updated_at: cancelledAt
      })
      .eq('id', request.id);

    if (updateError) {
      throw updateError;
    }

    if (request.status === 'approved' && workDates.length > 0) {
      const { error: attendanceDeleteError } = await supabaseClient
        .from('attendance_records')
        .delete()
        .eq('employee_code', request.employeeCode)
        .eq('duty_type', 'paid')
        .in('work_date', workDates);

      if (attendanceDeleteError) {
        throw attendanceDeleteError;
      }
    }
  };

  const handleCancelLeaveRequest = async (request: LeaveRequest): Promise<void> => {
    if (!supabaseClient) return;

    const defaultReason =
      request.status === 'approved'
        ? '管理者取消（出勤希望のため）'
        : '管理者取消';
    const reason = window.prompt(
      `${request.employeeName ?? request.employeeCode}さんの有給申請を取消します。\n取消理由を入力してください:`,
      defaultReason
    );

    if (reason === null) {
      return;
    }
    if (!reason.trim()) {
      alert('取消理由を入力してください');
      return;
    }
    if (!window.confirm('この有給申請を取消済みにしますか？承認済みの場合は、有給として作成された勤怠レコードも削除します。')) {
      return;
    }

    try {
      await cancelLeaveRequestWithAttendance(request, reason.trim());
      setSelectedLeaveRequestIds(prev => prev.filter(id => id !== request.id));
      await fetchLeaveRequests();
      await refreshAttendance();
      await fetchMonthly();
      alert('有給申請を取消しました');
    } catch (err: any) {
      alert(`取消に失敗しました: ${err?.message ?? String(err)}`);
    }
  };

  // 有給申請を一括承認
  const handleBulkApproveLeaveRequests = async (): Promise<void> => {
    if (!supabaseClient || selectedLeaveRequestIds.length === 0) return;

    const selectedRequests = leaveRequests.filter(req => selectedLeaveRequestIds.includes(req.id));
    if (selectedRequests.length === 0) return;

    if (!window.confirm(`${selectedRequests.length}件の有給申請を一括承認しますか？`)) {
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;
      let firstErrorMessage: string | null = null;

      for (const request of selectedRequests) {
        try {
          await approveLeaveRequestWithAttendance(request);
          successCount++;
        } catch (error: any) {
          if (!firstErrorMessage) {
            const employeeLabel = request.employeeName ?? request.employeeCode;
            firstErrorMessage = `${employeeLabel}: ${error?.message ?? String(error)}`;
          }
          console.error('Failed to bulk approve leave request', {
            requestId: request.id,
            employeeCode: request.employeeCode,
            error
          });
          errorCount++;
        }
      }

      // 選択状態をクリア
      setSelectedLeaveRequestIds([]);

      // データを再読み込み
      await fetchLeaveRequests();

      if (errorCount > 0) {
        const suffix = firstErrorMessage ? `\n最初のエラー: ${firstErrorMessage}` : '';
        alert(`一括承認完了: ${successCount}件成功, ${errorCount}件失敗${suffix}`);
      } else {
        alert(`${successCount}件の有給申請を一括承認しました`);
      }
    } catch (err: any) {
      alert(`一括承認に失敗しました: ${err?.message ?? String(err)}`);
    }
  };

  // 有給申請を一括却下
  const handleBulkRejectLeaveRequests = async (): Promise<void> => {
    if (!supabaseClient || selectedLeaveRequestIds.length === 0) return;

    const selectedRequests = leaveRequests.filter(req => selectedLeaveRequestIds.includes(req.id));
    if (selectedRequests.length === 0) return;

    const reason = window.prompt(`${selectedRequests.length}件の有給申請を一括却下します。\n却下理由を入力してください:`);
    if (reason === null) {
      // キャンセルされた場合
      return;
    }
    if (!reason.trim()) {
      alert('却下理由を入力してください');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const request of selectedRequests) {
        try {
          const { error } = await supabaseClient
            .from('leave_requests')
            .update({
              status: 'rejected',
              rejection_reason: reason.trim()
            })
            .eq('id', request.id);

          if (error) throw error;
          successCount++;
        } catch {
          errorCount++;
        }
      }

      // 選択状態をクリア
      setSelectedLeaveRequestIds([]);

      // データを再読み込み
      await fetchLeaveRequests();

      if (errorCount > 0) {
        alert(`一括却下完了: ${successCount}件成功, ${errorCount}件失敗`);
      } else {
        alert(`${successCount}件の有給申請を一括却下しました`);
      }
    } catch (err: any) {
      alert(`一括却下に失敗しました: ${err?.message ?? String(err)}`);
    }
  };

  const fetchAttendance = useCallback(async (employeeLookup: Map<string, AdminEmployee>) => {
    const requestId = ++attendanceRequestIdRef.current;
    if (!calculationReady) {
      setAttendanceData({});
      setAttendanceError(calculationSettingsError);
      setAttendanceLoading(!calculationSettingsError);
      return;
    }
    setAttendanceLoading(true);
    setAttendanceError(null);

    if (!supabaseClient) {
      setAttendanceData({});
      setAttendanceError('SupabaseのURLまたは匿名キーが設定されていません。`.env.local` を確認してください。');
      setAttendanceLoading(false);
      return;
    }

    try {
      const baseDateObj = new Date(`${selectedDate}T00:00:00`);
      const rangeStart = toISODate(new Date(baseDateObj.getFullYear(), baseDateObj.getMonth(), 1));
      const rangeEnd = toISODate(new Date(baseDateObj.getFullYear(), baseDateObj.getMonth() + 1, 0));

      const data = await fetchPagedRows(() => supabaseClient!
        .from('attendance_records')
        .select(`
          id,
          work_date,
          duty_type,
          clock_in_at,
          clock_out_at,
          break_minutes,
          status,
          notes,
          employee_code,
          employee:employees!attendance_records_employee_code_fkey (
            employeeId,
            name
          )
        `)
        .gte('work_date', rangeStart)
        .lte('work_date', rangeEnd)
        .order('work_date', { ascending: true })
        .order('clock_in_at', { ascending: true }).order('id'));

      if (attendanceRequestIdRef.current !== requestId) return;
      const groupedAttendance: Record<string, AdminAttendanceRecord[]> = {};

      (data ?? []).forEach((row: Record<string, any>, index: number) => {
        const dateValue = row.work_date ?? row.date ?? row.attendance_date ?? row.workDate;
        const rawEmployeeCode = row.employee_code ?? row.employeeCode ?? null;
        const rawEmployeeId = row.employee?.employeeId ?? row.employee_id ?? row.employeeId ?? null;

        if (!dateValue || (!rawEmployeeId && !rawEmployeeCode)) {
          return;
        }

        const employeeIdKey = normalizeEmployeeKey(rawEmployeeId ? String(rawEmployeeId) : null);
        const employeeCodeKey = normalizeEmployeeKey(rawEmployeeCode ? String(rawEmployeeCode) : null);
        const matchedEmployee =
          (employeeIdKey ? employeeLookup.get(employeeIdKey) : undefined) ??
          (employeeCodeKey ? employeeLookup.get(employeeCodeKey) : undefined);

        const canonicalEmployeeCode = matchedEmployee?.employeeCode ?? (rawEmployeeCode ? String(rawEmployeeCode) : rawEmployeeId ? String(rawEmployeeId) : '');
        if (!canonicalEmployeeCode) {
          return;
        }
        const canonicalEmployeeInternalId = matchedEmployee?.id ?? (rawEmployeeId ? String(rawEmployeeId) : null);
        const relationEmployee = row.employee
          ? {
            employeeId: String(row.employee.employeeId ?? canonicalEmployeeInternalId ?? canonicalEmployeeCode),
            name: String(row.employee.name ?? matchedEmployee?.name ?? canonicalEmployeeCode)
          }
          : matchedEmployee
            ? {
              employeeId: matchedEmployee.id ?? canonicalEmployeeCode,
              name: matchedEmployee.name
            }
            : null;
        const displayName = relationEmployee?.name ?? canonicalEmployeeCode;

        const recordDate = String(dateValue);
        const recordId = String(row.id ?? row.record_id ?? `attendance-${index}`);

        const breakMinutes = Number(row.break_minutes ?? row.breakMinutes ?? 0);
        const clockInRaw = row.clock_in_at ?? row.clock_in_time ?? row.clock_in ?? row.clockIn ?? null;
        const clockOutRaw = row.clock_out_at ?? row.clock_out_time ?? row.clock_out ?? row.clockOut ?? null;
        const clockInDisplay = toDisplayTime(clockInRaw);
        const clockOutDisplay = toDisplayTime(clockOutRaw);
        const shiftType = (row.duty_type ?? row.shift_type ?? row.shiftType ?? 'day') as AdminAttendanceRecord['shiftType'];
        const elapsedMinutes = calculateActualMinutes(
          clockInDisplay ?? undefined,
          clockOutDisplay ?? undefined,
          0,
          shiftType,
          {
            applyDefaultBreak: false,
            employeeId: canonicalEmployeeInternalId ?? undefined,
            workDate: recordDate,
            baseDate: recordDate,
            clockInDate: clockInDisplay ? (extractISODatePart(clockInRaw) ?? recordDate) : null,
            clockOutDate: clockOutDisplay ? (extractISODatePart(clockOutRaw) ?? recordDate) : null
          }
        );

        const effectiveBreakMinutes = calculateEffectiveBreakMinutes(elapsedMinutes, breakMinutes, shiftType, { dayBreakMinutes, nightBreakMinutes });
        const computedTotalMinutes = elapsedMinutes > 0 ? Math.max(0, elapsedMinutes - effectiveBreakMinutes) : 0;
        const record: AdminAttendanceRecord = {
          id: recordId,
          employeeCode: canonicalEmployeeCode,
          employeeName: displayName,
          employeeInternalId: canonicalEmployeeInternalId,
          employee: relationEmployee,
          date: recordDate,
          clockIn: clockInDisplay,
          clockOut: clockOutDisplay,
          rawClockInAt: clockInRaw,
          rawClockOutAt: clockOutRaw,
          rawBreakMinutes: breakMinutes,
          breakMinutes: effectiveBreakMinutes,
          totalMinutes: computedTotalMinutes,
          overtimeMinutes: row.overtime_minutes ?? row.overtimeMinutes ?? undefined,
          shiftType,
          status: (row.status ?? 'working') as AttendanceStatus,
          notes: row.notes ?? null,
          createdAt: row.created_at ?? row.createdAt ?? null,
          updatedAt: row.updated_at ?? row.updatedAt ?? null
        };

        if (!groupedAttendance[recordDate]) {
          groupedAttendance[recordDate] = [];
        }
        groupedAttendance[recordDate].push(record);
      });

      setAttendanceData(groupedAttendance);
    } catch (error: any) {
      if (attendanceRequestIdRef.current !== requestId) return;
      setAttendanceData({});
      setAttendanceError(`勤怠データの取得に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      if (attendanceRequestIdRef.current === requestId) setAttendanceLoading(false);
    }
  }, [calculateActualMinutes, selectedDate, supabaseClient, calculationReady, calculationSettingsError, dayBreakMinutes, nightBreakMinutes]);

  const refreshAttendance = useCallback(
    async (lookup?: Map<string, AdminEmployee>) => {
      const targetLookup = lookup ?? employeeLookupMap;
      if (!supabaseClient || targetLookup.size === 0) return;
      await fetchAttendance(targetLookup);
    },
    [employeeLookupMap, fetchAttendance, supabaseClient]
  );

  const fetchMonthlyFromTable = useCallback(
    async (activeEmployees: AdminEmployee[], targetMonth: Date, requestId: number) => {
      if (!supabaseClient) {
        return;
      }

      const employeeByCode = new Map<string, AdminEmployee>();
      activeEmployees.forEach(emp => {
        if (emp.employeeCode) {
          employeeByCode.set(String(emp.employeeCode), emp);
        }
      });

      const employeeCodes = Array.from(employeeByCode.keys());
      if (employeeCodes.length === 0) {
        if (monthlyRequestIdRef.current !== requestId) {
          return;
        }
        setMonthlyData({});
        setMonthlySummaries({});
        setMonthlyCarryInAdjustments({});
        return;
      }

      const { start: periodStartDate, endExclusive: periodEndExclusiveDate } = getClosingPeriodRange(
        targetMonth,
        closingDay
      );
      const fetchStartDate = new Date(periodStartDate.getTime() - 24 * 60 * 60 * 1000);
      const periodStart = toISODate(periodStartDate);
      const periodEndExclusive = toISODate(periodEndExclusiveDate);
      const targetMonthKey = `${targetMonth.getFullYear()}-${String(targetMonth.getMonth() + 1).padStart(2, '0')}`;

      const fetchStartIso = toISODate(fetchStartDate);

      const data = await fetchPagedRows(() => supabaseClient!
        .from('attendance_records')
        .select('id, employee_code, work_date, duty_type, clock_in_at, clock_out_at, break_minutes, status, notes')
        .in('employee_code', employeeCodes)
        .gte('work_date', fetchStartIso)
        .lt('work_date', periodEndExclusive)
        .order('employee_code', { ascending: true })
        .order('work_date', { ascending: true })
        .order('clock_in_at', { ascending: true }).order('id'));

      const carryInMap = computeCarryInAdjustments(
        data ?? [],
        employeeByCode,
        periodStart,
        periodEndExclusive
      );

      type Accumulator = {
        records: number;
        totalWorkMinutes: number;
        overtimeMinutes: number;
        nightShiftCount: number;
        unconfirmedCount: number;
        uniqueDays: Set<string>;
      };

      const nextMonthlyData: Record<string, MonthlyRecord[]> = {};
      const summaryAccumulators: Record<string, Accumulator> = {};

      const ensureAccumulator = (employeeId: string): Accumulator => {
        if (!summaryAccumulators[employeeId]) {
          summaryAccumulators[employeeId] = {
            records: 0,
            totalWorkMinutes: 0,
            overtimeMinutes: 0,
            nightShiftCount: 0,
            unconfirmedCount: 0,
            uniqueDays: new Set<string>()
          };
        }
        return summaryAccumulators[employeeId];
      };

      const isConfirmedStatus = (status: string | null | undefined): boolean => {
        const normalized = (status ?? '').toString().toLowerCase();
        if (!normalized) return false;
        const confirmedKeywords = ['確定', 'completed', 'approved', '退勤済み', 'done'];
        return confirmedKeywords.some(keyword => normalized.includes(keyword));
      };

      (data ?? []).forEach((row: Record<string, any>) => {
        const employeeCode = row.employee_code ? String(row.employee_code) : null;
        const employee = employeeCode ? employeeByCode.get(employeeCode) : null;
        if (!employee) {
          return;
        }

        const employeeId = employee.id;
        const workDateIso = String(row.work_date);
        const shiftType = (row.duty_type ?? row.shift_type ?? 'day') as MonthlyRecord['shiftType'];
        const clockInRaw = row.clock_in_at ?? row.clock_in ?? null;
        const clockOutRaw = row.clock_out_at ?? row.clock_out ?? null;

        if (workDateIso < periodStart) {
          return;
        }

        const storedBreakMinutes = Number(row.break_minutes ?? 0);
        const rawClockIn = toDisplayTime(clockInRaw);
        const clockOut = toDisplayTime(clockOutRaw);
        const clockIn = (rawClockIn ? roundUpTime(rawClockIn, employeeId, workDateIso) : null) ?? rawClockIn;
        const clockInDateIso = rawClockIn ? (extractISODatePart(clockInRaw) ?? workDateIso) : null;
        const clockOutDateIso = determineClockOutDate(
          workDateIso,
          shiftType,
          rawClockIn,
          clockOut,
          extractISODatePart(clockOutRaw)
        );
        const calcOptions = {
          employeeId, workDate: workDateIso, applyDefaultBreak: false,
          baseDate: workDateIso,
          clockInDate: clockInDateIso,
          clockOutDate: clockOutDateIso
        };
        const rawMinutes = calculateActualMinutes(
          rawClockIn ?? undefined,
          clockOut ?? undefined,
          0,
          shiftType,
          calcOptions
        );
        const effectiveBreakMinutes = calculateEffectiveBreakMinutes(rawMinutes, storedBreakMinutes, shiftType, { dayBreakMinutes, nightBreakMinutes });
        const totalMinutes = rawMinutes > 0 ? Math.max(0, rawMinutes - effectiveBreakMinutes) : 0;

        const record: MonthlyRecord = {
          id: row.id ? String(row.id) : undefined,
          date: workDateIso,
          rawClockIn, rawClockInAt: clockInRaw, rawClockOutAt: clockOutRaw, rawBreakMinutes: storedBreakMinutes,
          clockIn,
          clockOut,
          clockInDate: clockInDateIso,
          clockOutDate: clockOutDateIso,
          breakMinutes: effectiveBreakMinutes,
          totalMinutes,
          shiftType,
          notes: row.notes ?? null
        };

        if (!nextMonthlyData[employeeId]) {
          nextMonthlyData[employeeId] = [];
        }
        nextMonthlyData[employeeId].push(record);

        const acc = ensureAccumulator(employeeId);
        acc.records += 1;
        acc.totalWorkMinutes += totalMinutes;
        acc.overtimeMinutes += Math.max(0, calculateOvertime(record));
        if (shiftType === 'night') {
          acc.nightShiftCount += 1;
        }
        if (!isConfirmedStatus(row.status)) {
          acc.unconfirmedCount += 1;
        }
        if (shiftType !== 'dayoff') {
          acc.uniqueDays.add(record.date);
        }
      });

      Object.keys(nextMonthlyData).forEach(employeeId => {
        nextMonthlyData[employeeId].sort((a, b) => a.date.localeCompare(b.date));
      });

      const nextMonthlySummaries: Record<string, MonthlySummary | null> = {};
      activeEmployees.forEach(emp => {
        const records = nextMonthlyData[emp.id] ?? [];
        const acc = summaryAccumulators[emp.id];
        nextMonthlySummaries[emp.id] = buildSummaryFromRecords(
          emp,
          records,
          targetMonthKey,
          closingDay,
          calculateOvertime,
          acc?.unconfirmedCount ?? 0,
          carryInMap.get(emp.id)
        );
      });

      activeEmployees.forEach(emp => {
        if (!nextMonthlyData[emp.id]) {
          nextMonthlyData[emp.id] = [];
        }
        if (!Object.prototype.hasOwnProperty.call(nextMonthlySummaries, emp.id)) {
          nextMonthlySummaries[emp.id] = null;
        }
      });

      if (monthlyRequestIdRef.current !== requestId) {
        return;
      }

      setMonthlyData(nextMonthlyData);
      setMonthlySummaries(nextMonthlySummaries);
      setMonthlyCarryInAdjustments(Object.fromEntries(carryInMap.entries()));
      setMonthlyError(null);
    },
    [supabaseClient, calculateActualMinutes, calculateOvertime, closingDay, dayBreakMinutes, nightBreakMinutes, roundUpTime]
  );

  const fetchMonthly = useCallback(async () => {
    const requestId = ++monthlyRequestIdRef.current;
    setMonthlyLoading(true);
    setMonthlyError(null);
    setPayrollSourceFingerprint(undefined);
    setPayrollSourceError(undefined);
    if (!supabaseClient || !calculationReady) {
      setMonthlyData({}); setMonthlySummaries({}); setMonthlyCarryInAdjustments({});
      setMonthlyError(calculationSettingsError);
      setPayrollSourceError(calculationSettingsError ?? '集計設定を読み込んでいます。');
      setMonthlyLoading(!calculationSettingsError && !!supabaseClient);
      return;
    }
    const [year, month] = selectedMonth.split('-').map(Number);
    if (!/^\d{4}-\d{2}$/.test(selectedMonth) || month < 1 || month > 12) {
      setMonthlyError('対象月を確認してください。'); setMonthlyLoading(false); return;
    }
    const readSourceState = async () => {
      const { data, error } = await supabaseClient!.rpc('get_payroll_source_state', { p_period_key: selectedMonth, p_closing_day: 15 });
      if (error) throw new Error('集計元の確認に失敗しました。再読み込みしてください。');
      return parsePayrollSourceState(data);
    };
    try {
      let sourceBefore: { fingerprint: string; pendingAttendanceCount: number; pendingOvertimeCount: number; missingScheduleCount: number } | null = null;
      if (closingDay === '15') {
        try { sourceBefore = await readSourceState(); }
        catch { setPayrollSourceError('集計元の確認に失敗しました。再読み込みしてから確定してください。'); }
      } else {
        setPayrollSourceError('社労士Excelへの確定・出力は15日締めに対応しています。');
      }
      // Verify that the cached calculation inputs are the same ones protected by the source fence.
      const [employeeRows, freshRules, settingResponse] = await Promise.all([
        fetchPagedRows(() => supabaseClient!.from('employees').select(EMPLOYEE_COLUMNS).order('name').order('id')),
        readCalculationRules(),
        supabaseClient.from('payroll_settings').select(PAYROLL_SETTINGS_COLUMNS).order('created_at').limit(1).maybeSingle()
      ]);
      if (settingResponse.error) throw settingResponse.error;
      const freshEmployees = employeeRows.map(normalizeEmployeeRow).filter((emp): emp is AdminEmployee => !!emp);
      const fresh = settingResponse.data;
      const freshSettings: SettingsSnapshot = {
        closingDay: cutoffToClosingDay(fresh?.cutoff), roundUpClockIn: fresh ? Boolean(fresh.round_clock_in_ceil_30) : true,
        dayBreakMinutes: fresh?.day_break_minutes ?? 60, nightBreakMinutes: fresh?.night_break_minutes ?? 120,
        dayOvertimeBaseHours: fresh?.day_standard_hours ?? 8, nightOvertimeBaseHours: fresh?.night_standard_hours ?? 14
      };
      if (monthlyRequestIdRef.current !== requestId) return;
      const employeesChanged = JSON.stringify(freshEmployees) !== JSON.stringify(employees);
      const rulesChanged = JSON.stringify(freshRules) !== JSON.stringify(calculationRules);
      const settingsChanged = JSON.stringify(freshSettings) !== JSON.stringify(initialSettingsRef.current);
      if (employeesChanged || rulesChanged || settingsChanged) {
        if (employeesChanged) setEmployees(freshEmployees);
        if (rulesChanged) setCalculationRules(freshRules);
        if (settingsChanged) {
          initialSettingsRef.current = freshSettings;
          setClosingDay(freshSettings.closingDay); setRoundUpClockIn(freshSettings.roundUpClockIn);
          setDayBreakMinutes(freshSettings.dayBreakMinutes); setNightBreakMinutes(freshSettings.nightBreakMinutes);
          setDayOvertimeBaseHours(freshSettings.dayOvertimeBaseHours); setNightOvertimeBaseHours(freshSettings.nightOvertimeBaseHours);
        }
        setMonthlyData({}); setMonthlySummaries({}); setMonthlyCarryInAdjustments({});
        setPayrollSourceError('従業員・集計設定が更新されたため、最新情報で再集計しています。');
        return;
      }
      await fetchMonthlyFromTable(freshEmployees, new Date(year, month - 1, 1), requestId);
      if (monthlyRequestIdRef.current !== requestId || !sourceBefore) return;
      const sourceAfter = await readSourceState();
      if (monthlyRequestIdRef.current !== requestId) return;
      if (sourceBefore.fingerprint !== sourceAfter.fingerprint) {
        setPayrollSourceError('読み込み中に勤怠または設定が更新されました。再読み込みしてから確定してください。');
      } else if (sourceAfter.pendingAttendanceCount || sourceAfter.pendingOvertimeCount || sourceAfter.missingScheduleCount) {
        setPayrollSourceError(`確定前に確認が必要です：未確定勤怠 ${sourceAfter.pendingAttendanceCount ?? 0}件、未確認の時間外報告 ${sourceAfter.pendingOvertimeCount ?? 0}件、勤務予定未設定 ${sourceAfter.missingScheduleCount ?? 0}件。`);
      } else {
        setPayrollSourceFingerprint(sourceAfter.fingerprint);
        setPayrollSourceError(undefined);
      }
    } catch (cause: any) {
      if (monthlyRequestIdRef.current !== requestId) return;
      setMonthlyData({}); setMonthlySummaries({}); setMonthlyCarryInAdjustments({});
      setMonthlyError(`月次データを取得できませんでした: ${cause?.message ?? '再読み込みしてください。'}`);
      setPayrollSourceError('集計元を確認できませんでした。再読み込みしてから確定してください。');
    } finally {
      if (monthlyRequestIdRef.current === requestId) setMonthlyLoading(false);
    }
  }, [selectedMonth, employees, calculationRules, calculationReady, calculationSettingsError, closingDay, fetchMonthlyFromTable]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const normalizedEmployees = await fetchEmployees();
      if (!isMounted) return;

      const employeeLookup = new Map<string, AdminEmployee>();
      normalizedEmployees.forEach(emp => {
        const idKey = normalizeEmployeeKey(emp.id);
        if (idKey) {
          employeeLookup.set(idKey, emp);
        }
        const codeKey = normalizeEmployeeKey(emp.employeeCode ?? null);
        if (codeKey) {
          employeeLookup.set(codeKey, emp);
        }
      });

      setEmployeeLookupMap(employeeLookup);

      await fetchAttendance(employeeLookup);
      if (!isMounted) return;

    };

    load();

    return () => {
      isMounted = false;
    };
  }, [fetchEmployees, fetchAttendance]);

  useEffect(() => {
    if (!supabaseClient) return;
    if (employeeLookupMap.size === 0) return;
    void fetchMonthly();
  }, [employeeLookupMap, fetchMonthly, supabaseClient]);

  useEffect(() => {
    if (employeeLookupMap.size > 0) {
      void refreshAttendance();
    }
  }, [employeeLookupMap, selectedDate, refreshAttendance]);

  useEffect(() => {
    return () => {
      if (settingsSaveTimeoutRef.current) {
        clearTimeout(settingsSaveTimeoutRef.current);
        settingsSaveTimeoutRef.current = null;
      }
    };
  }, []);

  const activeQualificationOptions = useMemo(() => {
    return [...qualifications]
      .filter(option => option.isActive)
      .sort((a, b) => {
        if (a.displayOrder === b.displayOrder) {
          return a.name.localeCompare(b.name, 'ja');
        }
        return a.displayOrder - b.displayOrder;
      });
  }, [qualifications]);

  const employeesByKey = useMemo(() => {
    const map = new Map<string, AdminEmployee>();
    employees.forEach(emp => {
      const idKey = normalizeEmployeeKey(emp.id);
      if (idKey) {
        map.set(idKey, emp);
      }
      const codeKey = normalizeEmployeeKey(emp.employeeCode ?? null);
      if (codeKey) {
        map.set(codeKey, emp);
      }
    });
    return map;
  }, [employees]);

  const findEmployeeByKey = useCallback((key?: string | null): AdminEmployee | undefined => {
    const normalized = normalizeEmployeeKey(key ?? null);
    return normalized ? employeesByKey.get(normalized) : undefined;
  }, [employeesByKey]);

  const applyNoteToMonthlyRecord = useCallback(
    (employeeId: string | null | undefined, date: string, notes: string | null): void => {
      if (!employeeId) return;
      setMonthlyData(prev => {
        const records = prev[employeeId];
        if (!records) return prev;
        let changed = false;
        const nextRecords = records.map(record => {
          if (record.date !== date) {
            return record;
          }
          changed = true;
          return {
            ...record,
            notes
          };
        });
        return changed ? { ...prev, [employeeId]: nextRecords } : prev;
      });
    },
    []
  );

  const applyNoteToDailyRecord = useCallback(
    (date: string, employeeCode: string | null | undefined, employeeId: string | null | undefined, recordId: string | null | undefined, notes: string | null): void => {
      setAttendanceData(prev => {
        const records = prev[date];
        if (!records) return prev;
        let changed = false;
        const normalizedEmployeeId = normalizeEmployeeKey(employeeId ?? null);
        const normalizedEmployeeCode = normalizeEmployeeKey(employeeCode ?? null);
        const nextRecords = records.map(record => {
          const sameRecordId = recordId && record.id === recordId;
          const sameEmployeeId =
            normalizedEmployeeId &&
            normalizeEmployeeKey(record.employeeInternalId ?? null) === normalizedEmployeeId;
          const sameEmployeeCode =
            normalizedEmployeeCode &&
            normalizeEmployeeKey(record.employeeCode ?? null) === normalizedEmployeeCode;
          if (!sameRecordId && !sameEmployeeId && !sameEmployeeCode) {
            return record;
          }
          changed = true;
          return {
            ...record,
            notes,
            updatedAt: new Date().toISOString()
          };
        });
        return changed ? { ...prev, [date]: nextRecords } : prev;
      });
    },
    []
  );

  const saveDailyNote = useCallback(
    async (record: AdminAttendanceRecord, value: string): Promise<void> => {
      const notes = value.trim() ? value.trim() : null;
      if (supabaseClient && record.id && !String(record.id).startsWith('local-')) {
        const { error } = await supabaseClient
          .from('attendance_records')
          .update({ notes })
          .eq('id', record.id);
        if (error) {
          throw error;
        }
      }

      const employee =
        findEmployeeByKey(record.employeeInternalId ?? null) ??
        findEmployeeByKey(record.employeeCode ?? null);
      applyNoteToDailyRecord(record.date, record.employeeCode, record.employeeInternalId ?? null, record.id, notes);
      applyNoteToMonthlyRecord(employee?.id ?? null, record.date, notes);
    },
    [applyNoteToDailyRecord, applyNoteToMonthlyRecord, findEmployeeByKey, supabaseClient]
  );

  const saveMonthlyNote = useCallback(
    async (employeeId: string, record: MonthlyRecord, value: string): Promise<void> => {
      const employee = employees.find(emp => emp.id === employeeId);
      if (!employee) {
        throw new Error('従業員データが見つかりません。');
      }
      if (!employee.employeeCode) {
        throw new Error('この従業員には従業員コードが設定されていません。');
      }

      const notes = value.trim() ? value.trim() : null;
      const employeeCode = String(employee.employeeCode);
      if (supabaseClient) {
        const updateQuery = supabaseClient
          .from('attendance_records')
          .update({ notes });
        const { error } = record.id
          ? await updateQuery.eq('id', record.id)
          : await updateQuery
            .eq('employee_code', employeeCode)
            .eq('work_date', record.date);
        if (error) {
          throw error;
        }
      }

      applyNoteToMonthlyRecord(employee.id, record.date, notes);
      applyNoteToDailyRecord(record.date, employeeCode, employee.id, null, notes);
    },
    [applyNoteToDailyRecord, applyNoteToMonthlyRecord, employees, supabaseClient]
  );

  const startInlineNoteEdit = useCallback((key: string, currentValue?: string | null): void => {
    setInlineNoteEdit({
      key,
      value: currentValue ?? '',
      saving: false,
      error: null
    });
  }, []);

  const commitInlineNoteEdit = useCallback(
    async (key: string, onSave: (value: string) => Promise<void>): Promise<void> => {
      const current = inlineNoteEdit;
      if (!current || current.key !== key || current.saving) {
        return;
      }

      setInlineNoteEdit(prev =>
        prev && prev.key === key ? { ...prev, saving: true, error: null } : prev
      );

      try {
        await onSave(current.value);
        setInlineNoteEdit(prev => (prev && prev.key === key ? null : prev));
      } catch (error: any) {
        setInlineNoteEdit(prev =>
          prev && prev.key === key
            ? {
              ...prev,
              saving: false,
              error: error?.message ?? (error instanceof Error ? error.message : '備考の保存に失敗しました。')
            }
            : prev
        );
      }
    },
    [inlineNoteEdit]
  );

  const renderInlineNoteCell = (
    key: string,
    currentValue: string | null | undefined,
    onSave: (value: string) => Promise<void>
  ) => {
    const isEditing = inlineNoteEdit?.key === key;
    const displayValue = currentValue?.trim();

    if (isEditing && inlineNoteEdit) {
      return (
        <div className="min-w-[14rem] max-w-xs">
          <textarea
            autoFocus
            value={inlineNoteEdit.value}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) =>
              setInlineNoteEdit(prev =>
                prev && prev.key === key ? { ...prev, value: event.target.value } : prev
              )
            }
            onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setInlineNoteEdit(prev => (prev && prev.key === key ? null : prev));
                return;
              }
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
            onBlur={() => {
              void commitInlineNoteEdit(key, onSave);
            }}
            className="w-full rounded-lg border border-blue-300 bg-white px-2 py-1.5 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50"
            rows={2}
            placeholder="例：会議のみ短時間出勤"
            disabled={inlineNoteEdit.saving}
          />
          {inlineNoteEdit.error && (
            <p className="mt-1 text-xs text-red-600">{inlineNoteEdit.error}</p>
          )}
        </div>
      );
    }

    return (
      <button
        type="button"
        onDoubleClick={() => startInlineNoteEdit(key, currentValue)}
        className={`block min-w-[12rem] max-w-xs rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-blue-50 hover:text-blue-700 ${
          displayValue ? 'text-gray-700' : 'text-gray-400'
        }`}
        title="ダブルクリックで備考を編集"
      >
        {displayValue || '未入力'}
      </button>
    );
  };

  type ManualAttendancePayload = {
    employeeId: string;
    date: string;
    shiftType: PunchShiftType;
    clockIn: string | null;
    clockOut: string | null;
    breakMinutes: number;
    clockInDate?: string | null;
    clockOutDate?: string | null;
    notes?: string | null;
  };

  type AttendanceRecordInput = {
    employee: AdminEmployee;
    date: string;
    shiftType: PunchShiftType;
    clockIn: string | null;
    clockOut: string | null;
    breakMinutes: number;
    clockInDate?: string | null;
    clockOutDate?: string | null;
    notes?: string | null;
  };

  type BulkPunchPayload = {
    employeeIds: string[];
    date: string;
    shiftType: PunchShiftType;
    clockIn: string | null;
    clockOut: string | null;
    breakMinutes: number;
    clockInDate?: string | null;
    clockOutDate?: string | null;
  };

  const saveAttendanceRecords = useCallback(
    async (entries: AttendanceRecordInput[]): Promise<void> => {
      if (entries.length === 0) {
        return;
      }

      const processed = entries.map(entry => {
        const { employee, date, shiftType } = entry;
        if (!employee.employeeCode) {
          throw new Error(`従業員コードが設定されていない従業員が含まれています: ${employee.name}`);
        }
        const rawNotes = entry.notes;
        const trimmedNotes = rawNotes?.trim();
        const notes = rawNotes === undefined
          ? undefined
          : trimmedNotes
            ? trimmedNotes
            : null;
        const requiresTime = shiftType !== 'dayoff' && shiftType !== 'paid';
        const clockIn = requiresTime && entry.clockIn ? entry.clockIn : null;
        const clockOut = requiresTime && entry.clockOut ? entry.clockOut : null;
        const breakMinutes = requiresTime ? entry.breakMinutes : 0;
        const clockInDateValue = clockIn ? date : null;
        const clockOutDateValue = determineClockOutDate(
          date,
          shiftType,
          clockIn,
          clockOut,
          entry.clockOutDate ?? null
        );

        const totalMinutes =
          shiftType === 'paid'
            ? 480
            : shiftType === 'dayoff'
              ? 0
              : calculateActualMinutes(clockIn || undefined, clockOut || undefined, breakMinutes, shiftType, {
                employeeId: employee.id, workDate: date,
                baseDate: date,
                clockInDate: clockInDateValue,
                clockOutDate: clockOutDateValue
              });
        const status: AttendanceStatus =
          shiftType === 'paid' || shiftType === 'dayoff' || (clockOut && totalMinutes >= 0)
            ? 'completed'
            : 'working';

        const clockInIso = combineDateAndTimeToIso(date, clockIn, shiftType, clockIn, {
          explicitDate: clockInDateValue
        });
        const clockOutIso = combineDateAndTimeToIso(date, clockOut, shiftType, clockIn, {
          explicitDate: clockOutDateValue
        });

        return {
          employee,
          date,
          shiftType,
          clockIn,
          clockOut,
          clockInDate: clockInDateValue,
          clockOutDate: clockOutDateValue,
          breakMinutes,
          totalMinutes,
          status,
          notes,
          payload: {
            employee_code: String(employee.employeeCode),
            work_date: date,
            duty_type: shiftType,
            clock_in_at: clockInIso,
            clock_out_at: clockOutIso,
            break_minutes: breakMinutes,
            status,
            ...(notes !== undefined ? { notes } : {})
          }
        };
      });

      if (supabaseClient) {
        try {
          const { error } = await supabaseClient
            .from('attendance_records')
            .upsert(processed.map(item => item.payload), {
              onConflict: 'employee_code,work_date'
            });
          if (error) {
            throw error;
          }
          await Promise.all([refreshAttendance(), fetchMonthly()]);
          return;
        } catch (error) {
          console.error('Failed to save attendance records', error);
          throw error;
        }
      }

      const groupedByEmployee = new Map<string, { employee: AdminEmployee; entries: typeof processed }>();
      processed.forEach(entry => {
        const existing = groupedByEmployee.get(entry.employee.id);
        if (existing) {
          existing.entries.push(entry);
        } else {
          groupedByEmployee.set(entry.employee.id, { employee: entry.employee, entries: [entry] });
        }
      });

      setAttendanceData(prev => {
        const next = { ...prev };
        const timestamp = Date.now();
        groupedByEmployee.forEach(({ employee, entries }) => {
          const employeeCode = String(employee.employeeCode);
          entries.forEach((item, index) => {
            const key = item.date;
            const current = next[key] ? [...next[key]] : [];
            const fallbackRecord: AdminAttendanceRecord = {
              id: `local-${employeeCode}-${item.date}-${timestamp}-${index}`,
              employeeCode,
              employeeName: employee.name,
              employeeInternalId: employee.id,
              employee: {
                employeeId: employee.id,
                name: employee.name
              },
              date: item.date,
              clockIn: item.clockIn,
              clockOut: item.clockOut,
              breakMinutes: item.breakMinutes,
              totalMinutes: item.totalMinutes,
              shiftType: item.shiftType,
              status: item.status,
              notes: item.notes ?? null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              overtimeMinutes: undefined
            };

            const existingIndex = current.findIndex(
              record => record.employeeCode === employeeCode
            );
            if (existingIndex >= 0) {
              current[existingIndex] = fallbackRecord;
            } else {
              current.push(fallbackRecord);
            }
            next[key] = current;
          });
        });
        return next;
      });

      const monthlyUpdates = new Map<string, MonthlyRecord[]>();
      setMonthlyData(prev => {
        const next = { ...prev };
        groupedByEmployee.forEach(({ employee, entries }) => {
          const existing = next[employee.id] ?? [];
          const filtered = existing.filter(record =>
            !entries.some(entry => entry.date === record.date)
          );
          const appended = entries.map(entry => ({
            date: entry.date,
            clockIn: entry.clockIn,
            clockOut: entry.clockOut,
            clockInDate: entry.clockIn ? entry.clockInDate ?? entry.date : null,
            clockOutDate: entry.clockOut ? entry.clockOutDate ?? entry.date : null,
            breakMinutes: entry.breakMinutes,
            totalMinutes: entry.totalMinutes,
            shiftType: entry.shiftType as MonthlyRecord['shiftType'],
            notes: entry.notes ?? null
          }));
          const updated = [...filtered, ...appended].sort((a, b) => a.date.localeCompare(b.date));
          next[employee.id] = updated;
          monthlyUpdates.set(employee.id, updated);
        });
        return next;
      });

      setMonthlySummaries(prev => {
        const next = { ...prev };
        groupedByEmployee.forEach(({ employee }) => {
          const records = monthlyUpdates.get(employee.id) ?? [];
          const summary = buildSummaryFromRecords(
            employee,
            records,
            selectedMonth,
            closingDay,
            calculateOvertime,
            0,
            monthlyCarryInAdjustments[employee.id]
          );
          if (summary) {
            next[employee.id] = summary;
          } else {
            delete next[employee.id];
          }
        });
        return next;
      });
    },
    [
      calculateActualMinutes,
      fetchMonthly,
      refreshAttendance,
      selectedMonth,
      closingDay,
      setAttendanceData,
      setMonthlyData,
      setMonthlySummaries,
      calculateOvertime,
      supabaseClient,
      monthlyCarryInAdjustments
    ]
  );

  const removeAttendanceRecords = useCallback(
    async (entries: AttendanceRecordInput[]): Promise<void> => {
      if (entries.length === 0) {
        return;
      }

      const groupedByEmployee = new Map<string, { employee: AdminEmployee; entries: AttendanceRecordInput[] }>();
      entries.forEach(entry => {
        const existing = groupedByEmployee.get(entry.employee.id);
        if (existing) {
          existing.entries.push(entry);
        } else {
          groupedByEmployee.set(entry.employee.id, { employee: entry.employee, entries: [entry] });
        }
      });

      setAttendanceData(prev => {
        const next = { ...prev };
        groupedByEmployee.forEach(({ employee, entries }) => {
          entries.forEach(entry => {
            const key = entry.date;
            const current = next[key] ? [...next[key]] : [];
            const filtered = current.filter(record => {
              const matchesCode = employee.employeeCode
                ? record.employeeCode === String(employee.employeeCode)
                : false;
              const matchesId = record.employeeInternalId === employee.id;
              return !(record.date === entry.date && (matchesCode || matchesId));
            });
            if (filtered.length > 0) {
              next[key] = filtered;
            } else {
              delete next[key];
            }
          });
        });
        return next;
      });

      const monthlyUpdates = new Map<string, MonthlyRecord[] | undefined>();
      setMonthlyData(prev => {
        const next = { ...prev };
        groupedByEmployee.forEach(({ employee, entries }) => {
          const existing = next[employee.id] ?? [];
          const filtered = existing.filter(record =>
            !entries.some(entry => entry.date === record.date)
          );
          if (filtered.length > 0) {
            next[employee.id] = filtered;
            monthlyUpdates.set(employee.id, filtered);
          } else {
            delete next[employee.id];
            monthlyUpdates.set(employee.id, undefined);
          }
        });
        return next;
      });

      setMonthlySummaries(prev => {
        const next = { ...prev };
        groupedByEmployee.forEach(({ employee }) => {
          const records = monthlyUpdates.get(employee.id);
          if (records && records.length > 0) {
            const summary = buildSummaryFromRecords(
              employee,
              records,
              selectedMonth,
              closingDay,
              calculateOvertime,
              0,
              monthlyCarryInAdjustments[employee.id]
            );
            if (summary) {
              next[employee.id] = summary;
            } else {
              delete next[employee.id];
            }
          } else {
            delete next[employee.id];
          }
        });
        return next;
      });
    },
    [
      calculateOvertime,
      selectedMonth,
      closingDay,
      setAttendanceData,
      setMonthlyData,
      setMonthlySummaries,
      monthlyCarryInAdjustments
    ]
  );

  const ManualAttendanceModal = ({
    date,
    employees: employeeOptions,
    initialEmployeeId,
    onClose,
    onSave
  }: {
    date: string;
    employees: AdminEmployee[];
    initialEmployeeId: string | null;
    onClose: () => void;
    onSave: (payload: ManualAttendancePayload) => Promise<void>;
  }) => {
    const activeEmployees = useMemo(
      () => sortEmployeesDefault(employeeOptions.filter(emp => emp.status === 'active' && emp.employeeCode)),
      [employeeOptions]
    );

    const resolveInitialEmployee = useCallback((): string => {
      if (initialEmployeeId && activeEmployees.some(emp => emp.id === initialEmployeeId)) {
        return initialEmployeeId;
      }
      return activeEmployees[0]?.id ?? '';
    }, [activeEmployees, initialEmployeeId]);

    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(resolveInitialEmployee);
    const [shiftType, setShiftType] = useState<PunchShiftType>('day');
    const [clockIn, setClockIn] = useState<string>('09:00');
    const [clockOut, setClockOut] = useState<string>('18:00');
    const [breakMinutes, setBreakMinutes] = useState<number>(60);
    const [notes, setNotes] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      setSelectedEmployeeId(resolveInitialEmployee());
    }, [resolveInitialEmployee]);

    useEffect(() => {
      if (shiftType === 'dayoff' || shiftType === 'paid') {
        setClockIn('');
        setClockOut('');
        setBreakMinutes(0);
        return;
      }
      if (!clockIn || !clockOut) {
        setClockIn('09:00');
        setClockOut(shiftType === 'night' ? '09:00' : '18:00');
        setBreakMinutes(60);
      }
    }, [shiftType, clockIn, clockOut]);

    const selectedShift = SHIFT_TYPE_OPTIONS.find(option => option.value === shiftType);
    const inferredClockOutDate = useMemo(() => {
      if (!selectedShift?.needsTime) return null;
      return determineClockOutDate(date, shiftType, clockIn, clockOut, null);
    }, [selectedShift?.needsTime, shiftType, clockIn, clockOut, date]);

    const computedMinutes =
      shiftType === 'paid'
        ? 480
        : shiftType === 'dayoff'
          ? 0
          : calculateActualMinutes(clockIn || undefined, clockOut || undefined, breakMinutes, shiftType, {
            employeeId: selectedEmployeeId, workDate: date,
            baseDate: date,
            clockInDate: clockIn ? date : null,
            clockOutDate: clockOut ? (inferredClockOutDate ?? date) : null
          });

    const validate = (): string | null => {
      if (!selectedEmployeeId) {
        return '従業員を選択してください。';
      }
      if (!selectedShift) {
        return '勤務区分を選択してください。';
      }
      if (selectedShift.needsTime) {
        if (!clockIn || !clockOut) {
          return '出勤・退勤時刻を入力してください。';
        }
      }
      return null;
    };

    const handleSave = async () => {
      const validationMessage = validate();
      if (validationMessage) {
        setError(validationMessage);
        return;
      }
      setError(null);
      if (!isMountedRef.current) {
        return;
      }
      setSaving(true);
      try {
        await onSave({
          employeeId: selectedEmployeeId,
          date,
          shiftType,
          clockIn: selectedShift?.needsTime ? clockIn : null,
          clockOut: selectedShift?.needsTime ? clockOut : null,
          clockOutDate: selectedShift?.needsTime ? (inferredClockOutDate ?? date) : null,
          breakMinutes: selectedShift?.needsTime ? breakMinutes : 0,
          notes
        });
      } catch (err: any) {
        const message =
          err?.message ??
          (err instanceof Error ? err.message : '勤怠データの保存に失敗しました。');
        if (isMountedRef.current) {
          setError(message);
        }
      } finally {
        if (isMountedRef.current) {
          setSaving(false);
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">手動で勤怠を追加</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" disabled={saving}>
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">対象日</p>
              <p className="text-xs text-gray-600">{date}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                従業員 <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedEmployeeId}
                onChange={(event) => setSelectedEmployeeId(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                disabled={saving || activeEmployees.length === 0}
              >
                {activeEmployees.length === 0 ? (
                  <option value="">従業員データがありません</option>
                ) : (
                  activeEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                勤務区分 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SHIFT_TYPE_OPTIONS.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => {
                      setShiftType(type.value);
                      if (!type.needsTime) {
                        setClockIn('');
                        setClockOut('');
                        setBreakMinutes(0);
                      } else if (!clockIn || !clockOut) {
                        setClockIn('09:00');
                        setClockOut(type.value === 'night' ? '09:00' : '18:00');
                        setBreakMinutes(60);
                      }
                    }}
                    disabled={saving}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${shiftType === type.value
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedShift?.needsTime && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      出勤時刻
                    </label>
                    <input
                      type="time"
                      value={clockIn}
                      onChange={(event) => setClockIn(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      退勤時刻
                      {shiftType === 'night' && (
                        <span className="text-xs text-gray-500 block">(翌日可)</span>
                      )}
                    </label>
                    <input
                      type="time"
                      value={clockOut}
                      onChange={(event) => setClockOut(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    休憩時間(分)
                  </label>
                  <input
                    type="number"
                    value={breakMinutes}
                    onChange={(event) =>
                      setBreakMinutes(Number.isFinite(Number(event.target.value))
                        ? parseInt(event.target.value, 10) || 0
                        : 0)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    min="0"
                    step="15"
                    disabled={saving}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                備考
              </label>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                rows={3}
                placeholder="例：臨時的に食事なし、会議のみ短時間出勤"
                disabled={saving}
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">実働時間</span>
                <span className="font-bold text-blue-600">
                  {shiftType === 'paid' && '8時間00分(有給)'}
                  {shiftType === 'dayoff' && '-'}
                  {['day', 'night', 'holiday_work'].includes(shiftType) &&
                    formatMinutesToTime(Math.max(0, computedMinutes))}
                </span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
              disabled={saving}
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={saving || activeEmployees.length === 0}
            >
              <Save className="w-4 h-4" />
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const BulkPunchModal = ({
    date,
    employees: employeeOptions,
    existingRecords,
    onClose,
    onSave
  }: {
    date: string;
    employees: AdminEmployee[];
    existingRecords: AdminAttendanceRecord[];
    onClose: () => void;
    onSave: (payload: BulkPunchPayload) => Promise<void>;
  }) => {
    const activeEmployees = useMemo(
      () => sortEmployeesDefault(employeeOptions.filter(emp => emp.status === 'active' && emp.employeeCode)),
      [employeeOptions]
    );

    const existingKeys = useMemo(() => {
      const keys = new Set<string>();
      existingRecords.forEach(record => {
        const idKey = normalizeEmployeeKey(record.employeeInternalId ?? null);
        if (idKey) keys.add(idKey);
        const codeKey = normalizeEmployeeKey(record.employeeCode ?? null);
        if (codeKey) keys.add(codeKey);
      });
      return keys;
    }, [existingRecords]);

    const defaultSelectedIds = useMemo(() => {
      return activeEmployees
        .filter(emp => {
          const idKey = normalizeEmployeeKey(emp.id);
          const codeKey = normalizeEmployeeKey(emp.employeeCode ?? null);
          if (idKey && existingKeys.has(idKey)) return false;
          if (codeKey && existingKeys.has(codeKey)) return false;
          return true;
        })
        .map(emp => emp.id);
    }, [activeEmployees, existingKeys]);

    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>(defaultSelectedIds);
    const [shiftType, setShiftType] = useState<PunchShiftType>('day');
    const [clockIn, setClockIn] = useState<string>('09:00');
    const [clockOut, setClockOut] = useState<string>('18:00');
    const [clockOutDate, setClockOutDate] = useState<string | null>(date);
    const [breakMinutes, setBreakMinutes] = useState<number>(60);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      setSelectedEmployeeIds(defaultSelectedIds);
    }, [defaultSelectedIds]);

    useEffect(() => {
      const selected = SHIFT_TYPE_OPTIONS.find(option => option.value === shiftType);
      if (!selected?.needsTime) {
        setClockIn('');
        setClockOut('');
        setBreakMinutes(0);
      } else {
        if (!clockIn || !clockOut) {
          setClockIn('09:00');
          setClockOut(shiftType === 'night' ? '09:00' : '18:00');
          setBreakMinutes(60);
        }
      }
    }, [shiftType, clockIn, clockOut]);

    const toggleEmployee = (id: string) => {
      setSelectedEmployeeIds(prev =>
        prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
      );
    };

    const selectAll = () => {
      setSelectedEmployeeIds(activeEmployees.map(emp => emp.id));
    };

    const clearAll = () => {
      setSelectedEmployeeIds([]);
    };

    const selectedShift = SHIFT_TYPE_OPTIONS.find(option => option.value === shiftType);
    const clockOutMin = date;
    const clockOutMax = useMemo(() => addDaysToISODate(date, 1), [date]);

    const clampClockOutDate = useCallback(
      (value: string | null): string | null => {
        if (!value) return null;
        let next = value;
        if (next < clockOutMin) next = clockOutMin;
        if (next > clockOutMax) next = clockOutMax;
        return next;
      },
      [clockOutMin, clockOutMax]
    );

    useEffect(() => {
      if (!selectedShift?.needsTime) {
        setClockOutDate(null);
        return;
      }
      setClockOutDate(prev => {
        if (!prev) {
          return shiftType === 'night' ? clockOutMax : clockOutMin;
        }
        const normalized = clampClockOutDate(prev) ?? (shiftType === 'night' ? clockOutMax : clockOutMin);
        if (shiftType === 'night' && normalized === clockOutMin) {
          return clockOutMax;
        }
        if (shiftType !== 'night' && normalized === clockOutMax && clockOutMax !== clockOutMin) {
          return clockOutMin;
        }
        return normalized;
      });
    }, [selectedShift?.needsTime, shiftType, clockOutMin, clockOutMax, clampClockOutDate]);

    const validate = (): string | null => {
      if (selectedEmployeeIds.length === 0) {
        return '従業員を選択してください。';
      }
      if (selectedShift?.needsTime) {
        if (!clockIn || !clockOut) {
          return '出勤・退勤時刻を入力してください。';
        }
        const normalizedClockOutDate = clampClockOutDate(clockOutDate);
        if (clockOut && !normalizedClockOutDate) {
          return '退勤日を選択してください。';
        }
      }
      return null;
    };

    const handleSubmit = async () => {
      const validationMessage = validate();
      if (validationMessage) {
        setError(validationMessage);
        return;
      }
      setSaving(true);
      setError(null);
      try {
        const normalizedClockOutDate = clampClockOutDate(clockOutDate);
        await onSave({
          employeeIds: selectedEmployeeIds,
          date,
          shiftType,
          clockIn: selectedShift?.needsTime ? (clockIn || null) : null,
          clockOut: selectedShift?.needsTime ? (clockOut || null) : null,
          breakMinutes: selectedShift?.needsTime ? breakMinutes : 0,
          clockInDate: selectedShift?.needsTime && clockIn ? date : null,
          clockOutDate:
            selectedShift?.needsTime && clockOut
              ? normalizedClockOutDate
              : null
        });
        onClose();
      } catch (err: any) {
        const message = err?.message ?? (err instanceof Error ? err.message : '一括打刻に失敗しました。');
        setError(message);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-bold text-gray-900">一括打刻</h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{date}</p>
                <p className="text-xs text-gray-500">対象日の勤怠をまとめて登録します。</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-100"
                  disabled={saving || activeEmployees.length === 0}
                >
                  全て選択
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-100"
                  disabled={saving}
                >
                  全て解除
                </button>
                <span className="text-gray-400">選択中: {selectedEmployeeIds.length}名</span>
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 p-3 space-y-2 bg-gray-50">
              {activeEmployees.length === 0 ? (
                <p className="text-sm text-gray-500">打刻可能な従業員がいません。</p>
              ) : (
                activeEmployees.map(emp => {
                  const id = emp.id;
                  const idKey = normalizeEmployeeKey(emp.id);
                  const codeKey = normalizeEmployeeKey(emp.employeeCode ?? null);
                  const hasRecord = (idKey && existingKeys.has(idKey)) || (codeKey && existingKeys.has(codeKey));
                  const checked = selectedEmployeeIds.includes(id);
                  return (
                    <label
                      key={id}
                      className={`flex items-center justify-between rounded-lg border p-3 text-sm transition-all ${checked ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={saving}
                          onChange={() => toggleEmployee(id)}
                          className="h-4 w-4 text-blue-500"
                        />
                        <span>
                          <p className="font-medium text-gray-900">{emp.name}</p>
                          <p className="text-xs text-gray-500">{emp.employeeCode ?? '従業員コード未設定'}</p>
                        </span>
                      </span>
                      {hasRecord && (
                        <span className="text-xs text-amber-600">既に登録あり</span>
                      )}
                    </label>
                  );
                })
              )}
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-2">勤務区分 <span className="text-red-500">*</span></p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SHIFT_TYPE_OPTIONS.map(option => {
                  const isSelected = shiftType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setShiftType(option.value)}
                      disabled={saving}
                      className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${isSelected ? 'bg-blue-500 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedShift?.needsTime && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">出勤時刻</label>
                  <input
                    type="time"
                    value={clockIn}
                    onChange={event => setClockIn(event.target.value)}
                    disabled={saving}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">退勤時刻</label>
                  <input
                    type="time"
                    value={clockOut}
                    onChange={event => setClockOut(event.target.value)}
                    disabled={saving}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                  {shiftType === 'night' && (
                    <p className="mt-1 text-xs text-gray-500">翌日の時刻を指定できます。</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">退勤日</label>
                  <input
                    type="date"
                    value={clockOutDate ?? ''}
                    min={clockOutMin}
                    max={clockOutMax}
                    onChange={event => setClockOutDate(clampClockOutDate(event.target.value || null))}
                    disabled={saving}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                  {shiftType === 'night' && (
                    <p className="mt-1 text-xs text-gray-500">必要に応じて翌日を選択してください。</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">休憩</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={breakMinutes}
                      onChange={event => setBreakMinutes(Math.max(0, Number.parseInt(event.target.value, 10) || 0))}
                      disabled={saving}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-600">分</span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              disabled={saving}
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all ${saving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 shadow'}`}
            >
              {saving ? '登録中…' : '一括打刻する'}
            </button>
          </div>
        </div>
      </div>
    );
  };


  const resolveEmployeeName = (
    employeeInternalId?: string | null,
    fallback?: string,
    employeeCode?: string | null
  ): string => {
    const employee =
      findEmployeeByKey(employeeInternalId) ??
      findEmployeeByKey(employeeCode);
    if (employee) {
      return employee.name;
    }
    return fallback ?? (employeeCode ?? employeeInternalId ?? '');
  };

  const resolveEmployeeNameFromRecord = (
    record: Pick<AdminAttendanceRecord, 'employeeInternalId' | 'employeeCode' | 'employeeName' | 'employee'>
  ): string => {
    if (record.employee && record.employee.name) {
      return record.employee.name;
    }
    if (record.employeeName) {
      return record.employeeName;
    }
    return resolveEmployeeName(record.employeeInternalId, undefined, record.employeeCode ?? null);
  };
  const allMenuItems = useMemo(() => [
    { id: 'employees', label: '従業員管理', icon: Users, badge: null as number | null },
    { id: 'shift', label: 'シフト管理', icon: CalendarDays, badge: null as number | null },
    { id: 'daily', label: '日次ビュー', icon: Calendar, badge: null as number | null },
    { id: 'monthly', label: '月次レポート', icon: BarChart3, badge: null as number | null },
    { id: 'monthlyPrint', label: '印刷ページ', icon: Printer, badge: null as number | null },
    { id: 'overtimeReports', label: '時間外報告', icon: Clock, badge: null as number | null },
    { id: 'leaveRequests', label: '有給申請', icon: CalendarCheck, badge: pendingLeaveCount > 0 ? pendingLeaveCount : null },
    { id: 'payroll', label: '給与計算', icon: Calculator, badge: null as number | null },
    { id: 'adminReport', label: '行政報告', icon: FileText, badge: null as number | null },
    { id: 'settings', label: '設定', icon: Settings, badge: null as number | null }
,
    { id: 'subsidySimulator', label: '給付金シミュレーター', icon: DollarSign, badge: null as number | null }
  ], [pendingLeaveCount]);
  const menuItems = useMemo(() => isShiftOnly
    ? allMenuItems.filter(item => item.id === 'shift')
    : allMenuItems, [isShiftOnly, allMenuItems]);

  const formatMinutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}時間${mins}分`;
  };

  const convertToAdminAttendanceRecord = useCallback(
    (record: AttendanceRecord, fallbackId: string): AdminAttendanceRecord => {
      const normalizedShift = (record.shiftType ?? 'day') as AttendanceShiftType;
      const rawBreakMinutes = record.rawBreakMinutes ?? record.breakMinutes ?? 0;
      const elapsedMinutes = calculateActualMinutes(
            record.clockIn ?? undefined,
            record.clockOut ?? undefined,
            0,
            normalizedShift,
            {
              applyDefaultBreak: false,
              employeeId: findEmployeeByKey(record.employeeInternalId ?? record.employeeCode)?.id,
              workDate: record.date,
              baseDate: record.date,
              clockInDate: extractISODatePart(record.rawClockInAt ?? record.clockIn) ?? record.date,
              clockOutDate: determineClockOutDate(record.date, normalizedShift, record.clockIn, record.clockOut, extractISODatePart(record.rawClockOutAt ?? record.clockOut))
            }
          );

      const breakMinutes = calculateEffectiveBreakMinutes(elapsedMinutes, rawBreakMinutes, normalizedShift, { dayBreakMinutes, nightBreakMinutes });
      const computedMinutes = normalizedShift === 'paid' ? 480 : normalizedShift === 'dayoff' ? 0
        : elapsedMinutes > 0 ? Math.max(0, elapsedMinutes - breakMinutes) : 0;
      return {
        id: record.id ?? fallbackId,
        employeeCode: record.employeeCode,
        employeeName: record.employeeName,
        employeeInternalId: record.employeeInternalId ?? null,
        employee: record.employee
          ? {
            employeeId: record.employee.employeeId,
            name: record.employee.name
          }
          : record.employeeInternalId || record.employeeName
            ? {
              employeeId: record.employeeInternalId ?? record.employeeCode,
              name: record.employeeName
            }
            : null,
        date: record.date,
        clockIn: record.clockIn,
        clockOut: record.clockOut,
        rawClockInAt: record.rawClockInAt ?? (/^\d{4}-/.test(record.clockIn ?? '') ? record.clockIn : null),
        rawClockOutAt: record.rawClockOutAt ?? (/^\d{4}-/.test(record.clockOut ?? '') ? record.clockOut : null),
        rawBreakMinutes,
        breakMinutes,
        totalMinutes: computedMinutes,
        shiftType: normalizedShift,
        status: (record.status ?? 'working') as AttendanceStatus,
        notes: record.notes ?? null,
        createdAt: record.createdAt ?? null,
        updatedAt: record.updatedAt ?? null,
        overtimeMinutes: undefined
      };
    },
    [calculateActualMinutes, findEmployeeByKey, dayBreakMinutes, nightBreakMinutes]
  );

  const upsertAttendanceRecord = useCallback(
    (record: AdminAttendanceRecord): void => {
      setAttendanceData(prev => {
        const dateRecords = prev[record.date] ?? [];
        const existingIndex = dateRecords.findIndex(existing =>
          existing.id === record.id ||
          (existing.employeeCode === record.employeeCode && existing.date === record.date)
        );

        let nextDateRecords: AdminAttendanceRecord[];
        if (existingIndex >= 0) {
          nextDateRecords = dateRecords.map((item, idx) =>
            idx === existingIndex ? { ...item, ...record } : item
          );
        } else {
          nextDateRecords = [...dateRecords, record];
        }

        return {
          ...prev,
          [record.date]: nextDateRecords
        };
      });
      setAttendanceLoading(false);
    },
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const cached = window.localStorage.getItem(ATTENDANCE_STORAGE_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached) as Record<string, AttendanceRecord> | null;
      if (!parsed || typeof parsed !== 'object') return;

      const pending: Record<string, AdminAttendanceRecord[]> = {};

      Object.entries(parsed).forEach(([cacheKey, record]) => {
        if (!record || typeof record !== 'object') {
          return;
        }
        const adminRecord = convertToAdminAttendanceRecord(record, record.id ?? cacheKey);
        if (!pending[adminRecord.date]) {
          pending[adminRecord.date] = [];
        }
        const existingIndex = pending[adminRecord.date].findIndex(item => item.id === adminRecord.id);
        if (existingIndex >= 0) {
          pending[adminRecord.date][existingIndex] = { ...pending[adminRecord.date][existingIndex], ...adminRecord };
        } else {
          pending[adminRecord.date].push(adminRecord);
        }
      });

      if (Object.keys(pending).length === 0) {
        return;
      }

      setAttendanceData(prev => {
        const merged = { ...prev };
        Object.entries(pending).forEach(([date, records]) => {
          const current = merged[date] ?? [];
          const combined = [...current];

          records.forEach(record => {
            const idx = combined.findIndex(item =>
              item.id === record.id ||
              (item.employeeCode === record.employeeCode && item.date === record.date)
            );
            if (idx >= 0) {
              combined[idx] = { ...combined[idx], ...record };
            } else {
              combined.push(record);
            }
          });

          merged[date] = combined;
        });
        return merged;
      });
      setAttendanceLoading(false);
    } catch (error) {
      console.warn('Failed to restore cached attendance records', error);
    }
  }, [convertToAdminAttendanceRecord]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleAttendanceBroadcast = (event: Event) => {
      const customEvent = event as CustomEvent<AttendanceBroadcastDetail>;
      if (!customEvent.detail || !customEvent.detail.record) {
        return;
      }
      const adminRecord = convertToAdminAttendanceRecord(
        customEvent.detail.record,
        customEvent.detail.record.id
      );
      upsertAttendanceRecord(adminRecord);
    };

    window.addEventListener(ATTENDANCE_EVENT_NAME, handleAttendanceBroadcast as EventListener);
    return () => {
      window.removeEventListener(ATTENDANCE_EVENT_NAME, handleAttendanceBroadcast as EventListener);
    };
  }, [convertToAdminAttendanceRecord, upsertAttendanceRecord]);

  const calculateStandardHours = (year: number, month: number): number => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const standardHours = (daysInMonth / 7) * 40;
    return Math.round(standardHours * 10) / 10;
  };

  const buildEmployeePayload = (data: Partial<AdminEmployee> & { status?: AdminEmployee['status'] }) => {
    const payload: Record<string, any> = {};

    // デバッグログ
    console.log('buildEmployeePayload input:', {
      employmentType: data.employmentType,
      jobType: data.jobType
    });

    if (data.name !== undefined) payload.name = data.name;
    if (data.employmentType !== undefined) payload.employment_type = data.employmentType;
    if (data.jobType !== undefined) payload.job_type = data.jobType;
    if (data.status !== undefined) payload.status = data.status;
    if (data.employeeCode !== undefined) payload.employeeId = data.employeeCode;
    if ((data as any).employeeId !== undefined) payload.employeeId = (data as any).employeeId;
    if (data.hireDate !== undefined) payload.hire_date = data.hireDate || null;
    if (data.experienceStartDate !== undefined) {
      payload.experience_start_date = data.experienceStartDate || null;
    }
    if (data.retirementDate !== undefined) {
      payload.retirement_date = data.retirementDate || null;
    }
    if (data.qualifications !== undefined) payload.qualifications = data.qualifications;
    if (data.transportation !== undefined) payload.transportation = data.transportation;
    if (data.nightShiftAvailable !== undefined) payload.night_shift_available = data.nightShiftAvailable;
    if (data.dayMealProvided !== undefined) payload.day_meal_provided = data.dayMealProvided;
    if (data.nightMealProvided !== undefined) payload.night_meal_provided = data.nightMealProvided;
    if (data.commuteDistanceKm !== undefined) payload.commute_distance_km = data.commuteDistanceKm;
    if (data.commuteCostYen !== undefined) payload.commute_cost_yen = data.commuteCostYen;
    if (data.employeeCategory !== undefined) payload.employee_category = data.employeeCategory || null;
    if (data.hourlyRate !== undefined) payload.hourly_rate = data.hourlyRate;
    if (data.nightShiftPay !== undefined) payload.night_shift_pay = data.nightShiftPay;
    if (data.specialBenefitUnitPrice !== undefined) payload.special_benefit_unit_price = data.specialBenefitUnitPrice;
    if (data.specialAllowanceUnitPrice !== undefined) payload.special_allowance_unit_price = data.specialAllowanceUnitPrice;
    if (data.partSpecialAllowanceUnitPrice !== undefined) payload.part_special_allowance_unit_price = data.partSpecialAllowanceUnitPrice;
    if (data.mealDeductionPerDay !== undefined) payload.meal_deduction_per_day = data.mealDeductionPerDay;
    if (data.commuteCostMonthlyFixed !== undefined) payload.commute_cost_monthly_fixed = data.commuteCostMonthlyFixed;
    if (data.mealCondition !== undefined) payload.meal_condition = data.mealCondition;

    return payload;
  };

  const handleSort = (field: keyof AdminEmployee): void => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortedEmployees = [...employees]
    .filter(emp => emp.status === employeeTab)
    .filter(emp => {
      if (jobTypeFilter === 'all') return true;
      const jt = emp.jobType ?? '';
      if (jobTypeFilter === 'care') return jt.includes('介護');
      if (jobTypeFilter === 'nurse') return jt.includes('看護');
      if (jobTypeFilter === 'cook') return jt.includes('調理') || jt.includes('厨房');
      if (jobTypeFilter === 'other') {
        return !jt.includes('介護') && !jt.includes('看護') && !jt.includes('調理') && !jt.includes('厨房');
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === null) {
        const empOrder = (et: string): number => {
          if (et === '正社員') return 0;
          if (et === 'その他') return 1;
          return 2;
        };
        const jobOrder = (jt: string): number => {
          if (jt.includes('介護')) return 0;
          if (jt.includes('看護')) return 1;
          if (jt.includes('調理') || jt.includes('厨房')) return 2;
          return 3;
        };
        const eo = empOrder(a.employmentType ?? '') - empOrder(b.employmentType ?? '');
        if (eo !== 0) return eo;
        const jo = jobOrder(a.jobType ?? '') - jobOrder(b.jobType ?? '');
        if (jo !== 0) return jo;
        return (a.name ?? '').localeCompare(b.name ?? '');
      }
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (aVal == null && bVal == null) {
        return 0;
      }

      if (aVal == null) {
        return sortOrder === 'asc' ? 1 : -1;
      }

      if (bVal == null) {
        return sortOrder === 'asc' ? -1 : 1;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        if (sortOrder === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }

      // 型ガードを追加してundefinedでないことを確認
      if (aVal !== undefined && bVal !== undefined) {
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      }

      return 0;
    });

  useEffect(() => {
    if (!activeQualificationsEmployeeId) return;
    const existsInCurrentView = employees.some(
      emp => emp.id === activeQualificationsEmployeeId && emp.status === employeeTab
    );
    if (!existsInCurrentView) {
      setActiveQualificationsEmployeeId(null);
    }
  }, [activeQualificationsEmployeeId, employees, employeeTab]);

  useEffect(() => {
    if (!activeQualificationsEmployeeId) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target) {
        const root = target.closest('[data-qualifications-root]');
        if (root && root.getAttribute('data-employee-id') === activeQualificationsEmployeeId) {
          return;
        }
      }
      setActiveQualificationsEmployeeId(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveQualificationsEmployeeId(null);
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeQualificationsEmployeeId]);

  const handleCreateQualification = useCallback(
    async (name: string, displayOrder?: number, isActive: boolean = true): Promise<boolean> => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        setQualificationMutationError('資格名を入力してください。');
        return false;
      }
      if (!supabaseClient) {
        setQualificationMutationError('Supabaseの設定が見つかりません。`.env.local` を確認してください。');
        return false;
      }

      const maxOrder = qualifications.reduce(
        (max, option) => Math.max(max, Number.isFinite(option.displayOrder) ? option.displayOrder : 0),
        0
      );
      const orderValue =
        typeof displayOrder === 'number' && Number.isFinite(displayOrder)
          ? displayOrder
          : maxOrder + 1;

      setQualificationMutationLoading(true);
      setQualificationMutationError(null);

      try {
        const { error } = await supabaseClient
          .from('qualification_master')
          .insert([
            {
              name: trimmedName,
              display_order: orderValue,
              is_active: isActive
            }
          ]);

        if (error) {
          throw error;
        }

        await fetchQualifications();
        return true;
      } catch (error: any) {
        const message =
          error?.code === '23505'
            ? '同じ名前の資格が既に存在します。'
            : `資格の追加に失敗しました: ${error?.message ?? String(error)}`;
        setQualificationMutationError(message);
        return false;
      } finally {
        setQualificationMutationLoading(false);
      }
    },
    [fetchQualifications, qualifications, supabaseClient]
  );

  const handleUpdateQualification = useCallback(
    async (
      id: string,
      updates: {
        name?: string;
        displayOrder?: number;
        isActive?: boolean;
      }
    ): Promise<boolean> => {
      if (!supabaseClient) {
        setQualificationMutationError('Supabaseの設定が見つかりません。`.env.local` を確認してください。');
        return false;
      }

      const payload: Record<string, any> = {};

      if (updates.name !== undefined) {
        const trimmed = updates.name.trim();
        if (!trimmed) {
          setQualificationMutationError('資格名を入力してください。');
          return false;
        }
        payload.name = trimmed;
      }

      if (updates.displayOrder !== undefined && Number.isFinite(updates.displayOrder)) {
        payload.display_order = updates.displayOrder;
      }

      if (updates.isActive !== undefined) {
        payload.is_active = updates.isActive;
      }

      if (Object.keys(payload).length === 0) {
        return true;
      }

      setQualificationMutationLoading(true);
      setQualificationMutationError(null);

      try {
        const { error } = await supabaseClient
          .from('qualification_master')
          .update(payload)
          .eq('id', id);

        if (error) {
          throw error;
        }

        await fetchQualifications();
        return true;
      } catch (error: any) {
        const message =
          error?.code === '23505'
            ? '同じ名前の資格が既に存在します。'
            : `資格の更新に失敗しました: ${error?.message ?? String(error)}`;
        setQualificationMutationError(message);
        return false;
      } finally {
        setQualificationMutationLoading(false);
      }
    },
    [fetchQualifications, supabaseClient]
  );

  const handleDeleteQualification = useCallback(
    async (id: string): Promise<boolean> => {
      if (!supabaseClient) {
        setQualificationMutationError('Supabaseの設定が見つかりません。`.env.local` を確認してください。');
        return false;
      }

      setQualificationMutationLoading(true);
      setQualificationMutationError(null);

      try {
        const { error } = await supabaseClient
          .from('qualification_master')
          .delete()
          .eq('id', id);

        if (error) {
          throw error;
        }

        await fetchQualifications();
        return true;
      } catch (error: any) {
        setQualificationMutationError(`資格の削除に失敗しました: ${error?.message ?? String(error)}`);
        return false;
      } finally {
        setQualificationMutationLoading(false);
      }
    },
    [fetchQualifications, supabaseClient]
  );

  const handleAddEmployee = async (employeeData: Omit<AdminEmployee, 'id' | 'status'>): Promise<void> => {
    const generatedEmployeeId = employeeData.employeeCode && employeeData.employeeCode.trim()
      ? employeeData.employeeCode.trim()
      : generateEmployeeIdentifier();

    if (!supabaseClient) {
      setEmployeeMutationError(null);
      const newEmployee: AdminEmployee = {
        id: `local-${Date.now()}`,
        ...employeeData,
        employeeCode: generatedEmployeeId,
        status: 'active'
      };
      setEmployees(prev => [...prev, newEmployee]);
      setSelectedEmployeeForReport(prev => prev || newEmployee.id);
      setShowEmployeeModal(false);
      setEditingEmployee(null);
      return;
    }

    setEmployeeMutationLoading(true);
    setEmployeeMutationError(null);

    try {
      const payload = buildEmployeePayload({ ...employeeData, status: 'active', employeeCode: generatedEmployeeId });
      const { error } = await supabaseClient
        .from('employees')
        .insert([payload]);

      if (error) {
        throw error;
      }

      if (supabaseClient) {
        await fetchEmployees();
      }
      alert(`従業員を登録しました。\n従業員コード: ${generatedEmployeeId}`);
      setShowEmployeeModal(false);
      setEditingEmployee(null);
    } catch (error: any) {
      console.error('Failed to add employee', error);
      const rawMessage = error?.message ?? String(error);
      const duplicateEmployeeId =
        error?.code === '23505' ||
        (typeof rawMessage === 'string' &&
          rawMessage.includes('duplicate key value violates unique constraint "employees_employeeId_key"'));

      const message = duplicateEmployeeId
        ? '従業員コードが重複しています。'
        : error?.code === '42501'
          ? '従業員を追加する権限がありません。Supabase の RLS ポリシーを確認してください。'
          : error?.code === '42703'
            ? 'Supabase の employees テーブルに必要なカラムが存在するか確認してください。'
            : `従業員の登録に失敗しました: ${rawMessage}`;
      setEmployeeMutationError(message);
    } finally {
      setEmployeeMutationLoading(false);
    }
  };

  const handleEditEmployee = async (employeeData: Partial<AdminEmployee>): Promise<void> => {
    if (!editingEmployee) return;

    if (!supabaseClient) {
      setEmployeeMutationError(null);
      setEmployees(prev =>
        prev.map(emp => (emp.id === editingEmployee.id ? { ...emp, ...employeeData } as AdminEmployee : emp))
      );
      setShowEmployeeModal(false);
      setEditingEmployee(null);
      return;
    }

    setEmployeeMutationLoading(true);
    setEmployeeMutationError(null);

    try {
      const payload = buildEmployeePayload(employeeData);

      if (Object.keys(payload).length === 0) {
        setShowEmployeeModal(false);
        setEditingEmployee(null);
        return;
      }

      const { error } = await supabaseClient
        .from('employees')
        .update(payload)
        .eq('id', editingEmployee.id);

      if (error) {
        throw error;
      }

      await fetchEmployees();
      setShowEmployeeModal(false);
      setEditingEmployee(null);
    } catch (error: any) {
      console.error('Failed to edit employee', error);
      const message =
        error?.code === '42501'
          ? '従業員データを更新する権限がありません。Supabase の RLS ポリシーを確認してください。'
          : error?.code === '42703'
            ? 'Supabase の employees テーブルに必要なカラムが存在するか確認してください。'
            : `従業員情報の更新に失敗しました: ${error?.message ?? String(error)}`;
      setEmployeeMutationError(message);
    } finally {
      setEmployeeMutationLoading(false);
    }
  };

  const handleRetireEmployee = async (employeeId: string): Promise<void> => {
    if (!window.confirm('この従業員を退職済みにしますか?')) {
      return;
    }

    if (!supabaseClient) {
      setEmployeeMutationError(null);
      setEmployees(prev =>
        prev.map(emp => (emp.id === employeeId ? { ...emp, status: 'retired' } : emp))
      );
      return;
    }

    setEmployeeMutationLoading(true);
    setEmployeeMutationError(null);

    try {
      const payload = buildEmployeePayload({ status: 'retired' });
      const { error } = await supabaseClient
        .from('employees')
        .update(payload)
        .eq('id', employeeId);

      if (error) {
        throw error;
      }

      await fetchEmployees();
    } catch (error: any) {
      console.error('Failed to retire employee', error);
      const message =
        error?.code === '42501'
          ? '従業員データを更新する権限がありません。Supabase の RLS ポリシーを確認してください。'
          : error?.code === '42703'
            ? 'Supabase の employees テーブルに status カラムが存在するか確認してください。'
            : `従業員のステータス更新に失敗しました: ${error?.message ?? String(error)}`;
      setEmployeeMutationError(message);
    } finally {
      setEmployeeMutationLoading(false);
    }
  };

  const handleRestoreEmployee = async (employeeId: string): Promise<void> => {
    if (!window.confirm('この従業員を在籍中に戻しますか?')) {
      return;
    }

    if (!supabaseClient) {
      setEmployeeMutationError(null);
      setEmployees(prev =>
        prev.map(emp => (emp.id === employeeId ? { ...emp, status: 'active' } : emp))
      );
      return;
    }

    setEmployeeMutationLoading(true);
    setEmployeeMutationError(null);

    try {
      const payload = buildEmployeePayload({ status: 'active' });
      const { error } = await supabaseClient
        .from('employees')
        .update(payload)
        .eq('id', employeeId);

      if (error) {
        throw error;
      }

      await fetchEmployees();
    } catch (error: any) {
      console.error('Failed to restore employee', error);
      const message =
        error?.code === '42501'
          ? '従業員データを更新する権限がありません。Supabase の RLS ポリシーを確認してください。'
          : error?.code === '42703'
            ? 'Supabase の employees テーブルに status カラムが存在するか確認してください。'
            : `従業員のステータス更新に失敗しました: ${error?.message ?? String(error)}`;
      setEmployeeMutationError(message);
    } finally {
      setEmployeeMutationLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId: string): Promise<void> => {
    if (!window.confirm('この従業員を完全に削除しますか?この操作は取り消せません。')) {
      return;
    }

    setEmployeeMutationLoading(true);
    setEmployeeMutationError(null);

    try {
      const targetEmployee = employees.find(emp => emp.id === employeeId);
      const employeeCode = targetEmployee?.employeeCode
        ? String(targetEmployee.employeeCode)
        : null;

      if (!supabaseClient) {
        throw new Error('Supabaseの設定が見つかりません。`.env.local` を確認してください。');
      }

      if (employeeCode) {
        const { error: attendanceDeleteError } = await supabaseClient
          .from('attendance_records')
          .delete()
          .eq('employee_code', employeeCode);
        if (attendanceDeleteError) {
          throw attendanceDeleteError;
        }
      }

      const { error: employeeDeleteError } = await supabaseClient
        .from('employees')
        .delete()
        .eq('id', employeeId);

      if (employeeDeleteError) {
        throw employeeDeleteError;
      }

      setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
      setMonthlyData(prev => {
        const next = { ...prev };
        delete next[employeeId];
        return next;
      });
      setMonthlySummaries(prev => {
        const next = { ...prev };
        delete next[employeeId];
        return next;
      });
      setMonthlyCarryInAdjustments(prev => {
        const next = { ...prev };
        delete next[employeeId];
        return next;
      });
      if (employeeCode) {
        setAttendanceData(prev => {
          const next: typeof prev = {};
          Object.entries(prev).forEach(([date, records]) => {
            const filtered = records.filter(record => record.employeeCode !== employeeCode);
            if (filtered.length > 0) {
              next[date] = filtered;
            }
          });
          return next;
        });
      }
      setSelectedEmployeeForReport(prev => (prev === employeeId ? '' : prev));
      await fetchEmployees();
    } catch (error: any) {
      console.error('Failed to delete employee', error);
      const message =
        error?.code === '42501'
          ? '従業員データを削除する権限がありません。Supabase の RLS ポリシーを確認してください。'
          : error?.code === '23503'
            ? '関連する勤怠データを削除できませんでした。対象従業員の勤怠記録を整理してから再度お試しください。'
            : `従業員の削除に失敗しました: ${error?.message ?? String(error)}`;
      setEmployeeMutationError(message);
    } finally {
      setEmployeeMutationLoading(false);
    }
  };

  const handleEditAttendance = async (updatedData: Partial<AdminAttendanceRecord>): Promise<void> => {
    if (!editingAttendance) return;

    const resolveField = <K extends keyof AdminAttendanceRecord>(key: K): AdminAttendanceRecord[K] => {
      return (key in updatedData ? (updatedData as any)[key] : editingAttendance[key]) ?? null;
    };

    const nextClockIn = resolveField('clockIn');
    const nextClockOut = resolveField('clockOut');
    const nextBreakMinutes =
      'breakMinutes' in updatedData
        ? (updatedData.breakMinutes ?? 0)
        : editingAttendance.breakMinutes;
    const nextShiftType =
      ('shiftType' in updatedData ? updatedData.shiftType : editingAttendance.shiftType) ?? 'day';
    const nextStatus =
      'status' in updatedData ? (updatedData.status ?? editingAttendance.status) : editingAttendance.status;
    const nextNotes =
      'notes' in updatedData
        ? (updatedData.notes?.trim() ? updatedData.notes.trim() : null)
        : editingAttendance.notes;
    const nextTotalMinutes =
      'totalMinutes' in updatedData
        ? (updatedData.totalMinutes ?? editingAttendance.totalMinutes)
        : editingAttendance.totalMinutes;

    const targetEmployee =
      findEmployeeByKey(editingAttendance.employeeInternalId ?? editingAttendance.employeeCode ?? null) ?? null;

    if (supabaseClient) {
      try {
        setAttendanceLoading(true);
        setAttendanceError(null);

        const clockOutDate = determineClockOutDate(editingAttendance.date, nextShiftType, nextClockIn, nextClockOut,
          nextClockOut === editingAttendance.clockOut ? extractISODatePart(editingAttendance.rawClockOutAt) : null);
        const clockInIso = preserveUnchangedPunchIso(editingAttendance.rawClockInAt, extractISODatePart(editingAttendance.rawClockInAt) ?? editingAttendance.date, nextClockIn,
          combineDateAndTimeToIso(editingAttendance.date, nextClockIn, nextShiftType, nextClockIn, { explicitDate: editingAttendance.date }));
        const clockOutIso = preserveUnchangedPunchIso(editingAttendance.rawClockOutAt, clockOutDate, nextClockOut,
          combineDateAndTimeToIso(editingAttendance.date, nextClockOut, nextShiftType, nextClockIn, { explicitDate: clockOutDate }));

        const { error } = await supabaseClient
          .from('attendance_records')
          .update({
            duty_type: nextShiftType,
            break_minutes: nextBreakMinutes,
            status: nextStatus,
            clock_in_at: clockInIso,
            clock_out_at: clockOutIso,
            notes: nextNotes
          })
          .eq('id', editingAttendance.id);

        if (error) {
          throw error;
        }
      } catch (error: any) {
        console.error('Failed to update attendance record', error);
        setAttendanceError(
          error?.code === '42501'
            ? '勤怠データを更新する権限がありません。Supabase の RLS ポリシーを確認してください。'
            : `勤怠データの更新に失敗しました: ${error?.message ?? String(error)}`
        );
        return;
      } finally {
        setAttendanceLoading(false);
      }

      await fetchMonthly();
    } else {
      if (targetEmployee) {
        const { start: monthStart, end: monthEnd } = computeMonthRange(selectedMonth, closingDay);
        if (editingAttendance.date >= monthStart && editingAttendance.date <= monthEnd) {
          const normalizedShift = nextShiftType as MonthlyRecord['shiftType'];
          const derivedClockOutDate = determineClockOutDate(
            editingAttendance.date,
            normalizedShift,
            nextClockIn,
            nextClockOut,
            null
          );
          const recalculatedTotalMinutes =
            nextTotalMinutes ??
            (normalizedShift === 'paid'
              ? 480
              : normalizedShift === 'dayoff'
                ? 0
                : calculateActualMinutes(
                  nextClockIn || undefined,
                  nextClockOut || undefined,
                  nextBreakMinutes,
                  normalizedShift,
                  {
                    employeeId: targetEmployee.id, workDate: editingAttendance.date,
                    baseDate: editingAttendance.date,
                    clockInDate: nextClockIn ? editingAttendance.date : null,
                    clockOutDate: nextClockOut ? (derivedClockOutDate ?? editingAttendance.date) : null
                  }
                ));
          const updatedMonthlyRecord: MonthlyRecord = {
            date: editingAttendance.date,
            clockIn: nextClockIn,
            clockOut: nextClockOut,
            clockInDate: nextClockIn ? editingAttendance.date : null,
            clockOutDate: nextClockOut ? (derivedClockOutDate ?? editingAttendance.date) : null,
            breakMinutes: nextBreakMinutes,
            totalMinutes: recalculatedTotalMinutes,
            shiftType: normalizedShift,
            notes: nextNotes
          };

          let nextMonthlyRecords: MonthlyRecord[] | null = null;
          setMonthlyData(prev => {
            const existing = prev[targetEmployee.id] ?? [];
            const filtered = existing.filter(record => record.date !== editingAttendance.date);
            nextMonthlyRecords = [...filtered, updatedMonthlyRecord].sort((a, b) => a.date.localeCompare(b.date));
            return {
              ...prev,
              [targetEmployee.id]: nextMonthlyRecords
            };
          });

          if (nextMonthlyRecords) {
            setMonthlySummaries(prev => {
              const unconfirmed = prev[targetEmployee.id]?.unconfirmed_count ?? 0;
              const summary = buildSummaryFromRecords(
                targetEmployee,
                nextMonthlyRecords!,
                selectedMonth,
                closingDay,
                calculateOvertime,
                unconfirmed,
                monthlyCarryInAdjustments[targetEmployee.id]
              );
              if (!summary) {
                const next = { ...prev };
                delete next[targetEmployee.id];
                return next;
              }
              return {
                ...prev,
                [targetEmployee.id]: summary
              };
            });
          }
        }
      }
    }

    const currentRecords = attendanceData[selectedDate] ?? [];
    const dateData = currentRecords.map(record =>
      record.id === editingAttendance.id
        ? {
          ...record,
          clockIn: nextClockIn,
          clockOut: nextClockOut,
          breakMinutes: nextBreakMinutes,
          shiftType: nextShiftType,
          status: nextStatus,
          totalMinutes: nextTotalMinutes,
          notes: nextNotes
        }
        : record
    );

    setAttendanceData({
      ...attendanceData,
      [selectedDate]: dateData
    });
    setShowEditAttendanceModal(false);
    setEditingAttendance(null);
    void refreshAttendance();
  };

  const handleEditMonthlyRecord = async (updatedData: MonthlyModalSavePayload): Promise<void> => {
    if (!selectedEmployeeForReport) {
      throw new Error('従業員を選択してください。');
    }

    const targetEmployee = employees.find(emp => emp.id === selectedEmployeeForReport);
    if (!targetEmployee) {
      throw new Error('従業員データが見つかりません。');
    }
    if (!targetEmployee.employeeCode) {
      throw new Error('この従業員には従業員コードが設定されていません。');
    }

    const targetDate = updatedData.date;
    const employeeCode = String(targetEmployee.employeeCode);
    const existingRecordsForEmployee = monthlyData[targetEmployee.id] ?? [];
    const originalDate = editingMonthlyRecord?.date ?? targetDate;
    const previousRecord =
      editingMonthlyRecord ??
      existingRecordsForEmployee.find(item => item.date === originalDate) ?? null;
    const shiftType = updatedData.shiftType;
    const breakMinutes =
      shiftType === 'dayoff' || shiftType === 'paid' ? 0 : updatedData.breakMinutes;
    const clockIn =
      shiftType === 'dayoff' || shiftType === 'paid' ? null : updatedData.clockIn;
    const clockOut =
      shiftType === 'dayoff' || shiftType === 'paid' ? null : updatedData.clockOut;
    const notes = updatedData.notes?.trim() ? updatedData.notes.trim() : null;
    const requiresTime = shiftType === 'day' || shiftType === 'night' || shiftType === 'holiday_work';
    const clockOutDateRaw =
      shiftType === 'dayoff' || shiftType === 'paid'
        ? null
        : updatedData.clockOutDate ?? (requiresTime ? targetDate : null);
    const clockOutDate = determineClockOutDate(targetDate, shiftType, clockIn, clockOut, clockOutDateRaw);
    const totalMinutes =
      shiftType === 'paid'
        ? 480
        : shiftType === 'dayoff'
          ? 0
          : updatedData.totalMinutes;
    const nextStatus: AttendanceStatus =
      shiftType === 'paid' || shiftType === 'dayoff' || (clockOut && totalMinutes >= 0)
        ? 'completed'
        : 'working';

    if (supabaseClient) {
      const client = supabaseClient;
      try {
        const payload = {
          employee_code: employeeCode,
          work_date: targetDate,
          duty_type: shiftType,
          break_minutes: breakMinutes,
          status: nextStatus,
          clock_in_at: preserveUnchangedPunchIso(previousRecord?.rawClockInAt,
            previousRecord?.date === targetDate ? (extractISODatePart(previousRecord?.rawClockInAt) ?? targetDate) : targetDate, clockIn,
            combineDateAndTimeToIso(targetDate, clockIn, shiftType, clockIn, { explicitDate: clockIn ? targetDate : null })),
          clock_out_at: preserveUnchangedPunchIso(previousRecord?.rawClockOutAt, clockOutDate, clockOut,
            combineDateAndTimeToIso(targetDate, clockOut, shiftType, clockIn, { explicitDate: clockOut ? clockOutDate : null })),
          notes
        };

        const findRecordIdByDate = async (date: string | null | undefined): Promise<string | null> => {
          if (!date) return null;
          const { data, error } = await client
            .from('attendance_records')
            .select('id')
            .eq('employee_code', employeeCode)
            .eq('work_date', date)
            .order('updated_at', { ascending: false })
            .order('created_at', { ascending: false })
            .limit(1);
          if (error) {
            throw error;
          }
          return data?.[0]?.id ?? null;
        };

        let recordId = previousRecord?.id ?? await findRecordIdByDate(originalDate);
        let conflictingRecordId: string | null = null;
        if (targetDate !== originalDate) {
          conflictingRecordId = await findRecordIdByDate(targetDate);
          if (conflictingRecordId === recordId) {
            conflictingRecordId = null;
          }
        }

        if (!recordId && conflictingRecordId) {
          recordId = conflictingRecordId;
          conflictingRecordId = null;
        }

        if (recordId) {
          if (conflictingRecordId) {
            const { error: deleteError } = await client
              .from('attendance_records')
              .delete()
              .eq('id', conflictingRecordId);
            if (deleteError) {
              throw deleteError;
            }
          }

          const { error: updateError } = await client
            .from('attendance_records')
            .update(payload)
            .eq('id', recordId);
          if (updateError) {
            throw updateError;
          }

          if (targetDate !== originalDate && originalDate) {
            const { error: cleanupError } = await client
              .from('attendance_records')
              .delete()
              .eq('employee_code', employeeCode)
              .eq('work_date', originalDate)
              .neq('id', recordId);
            if (cleanupError && cleanupError.code !== 'PGRST116') {
              throw cleanupError;
            }
          }
        } else {
          const { error: insertError } = await client
            .from('attendance_records')
            .insert([payload]);
          if (insertError) {
            throw insertError;
          }

          if (targetDate !== originalDate && originalDate) {
            const { error: deleteStaleError } = await client
              .from('attendance_records')
              .delete()
              .eq('employee_code', employeeCode)
              .eq('work_date', originalDate);
            if (deleteStaleError && deleteStaleError.code !== 'PGRST116') {
              throw deleteStaleError;
            }
          }
        }

        await Promise.all([fetchMonthly(), refreshAttendance()]);
        setShowEditMonthlyModal(false);
        setEditingMonthlyRecord(null);
        return;
      } catch (error) {
        console.error('Failed to save monthly record', error);
        throw error;
      }
    }

    const normalizedShift = shiftType as MonthlyRecord['shiftType'];
    const nextMonthlyRecord: MonthlyRecord = {
      date: targetDate,
      clockIn,
      clockOut,
      clockInDate: clockIn ? targetDate : null,
      clockOutDate: clockOut ? clockOutDate ?? targetDate : null,
      breakMinutes,
      totalMinutes,
      shiftType: normalizedShift,
      notes
    };

    const periodRange = computeMonthRange(selectedMonth, closingDay);
    const periodStart = periodRange.start;
    const periodEnd = periodRange.end;
    const isWithinSelectedPeriod = targetDate >= periodStart && targetDate <= periodEnd;
    const carriesBoundary = (record: MonthlyRecord | null | undefined): boolean => {
      if (!record) return false;
      if (!record.clockOutDate) return false;
      if (record.clockOutDate === record.date) return false;
      return record.date < periodStart;
    };
    const previousCarry = monthlyCarryInAdjustments[targetEmployee.id] ?? { work: 0, night: 0 };
    let nextCarryWork = previousCarry.work;
    let nextCarryNight = previousCarry.night;
    if (carriesBoundary(previousRecord)) {
      nextCarryWork = Math.max(0, nextCarryWork - 1);
      if ((previousRecord?.shiftType ?? null) === 'night') {
        nextCarryNight = Math.max(0, nextCarryNight - 1);
      }
    }
    if (carriesBoundary(nextMonthlyRecord)) {
      nextCarryWork += 1;
      if (nextMonthlyRecord.shiftType === 'night') {
        nextCarryNight += 1;
      }
    }
    const hasCarry = nextCarryWork > 0 || nextCarryNight > 0;
    const updatedCarryEntry = hasCarry ? { work: nextCarryWork, night: nextCarryNight } : undefined;

    let nextMonthlyRecords: MonthlyRecord[] = [];
    setMonthlyData(prev => {
      const previousRecords = prev[targetEmployee.id] ?? [];
      const filtered = previousRecords.filter(item => item.date !== originalDate);

      if (isWithinSelectedPeriod) {
        nextMonthlyRecords = [...filtered, nextMonthlyRecord].sort((a, b) => a.date.localeCompare(b.date));
        return {
          ...prev,
          [targetEmployee.id]: nextMonthlyRecords
        };
      }

      nextMonthlyRecords = filtered;
      const next = { ...prev };
      if (filtered.length > 0) {
        next[targetEmployee.id] = filtered;
      } else {
        delete next[targetEmployee.id];
      }
      return next;
    });

    setMonthlySummaries(prev => {
      const previous = prev[targetEmployee.id];
      const unconfirmed = previous?.unconfirmed_count ?? 0;
      const summary = buildSummaryFromRecords(
        targetEmployee,
        nextMonthlyRecords,
        selectedMonth,
        closingDay,
        calculateOvertime,
        unconfirmed,
        updatedCarryEntry ?? monthlyCarryInAdjustments[targetEmployee.id]
      );
      if (!summary) {
        const next = { ...prev };
        delete next[targetEmployee.id];
        return next;
      }
      return {
        ...prev,
        [targetEmployee.id]: summary
      };
    });

    if (carriesBoundary(previousRecord) || carriesBoundary(nextMonthlyRecord)) {
      setMonthlyCarryInAdjustments(prev => {
        const next = { ...prev };
        if (updatedCarryEntry) {
          next[targetEmployee.id] = updatedCarryEntry;
        } else {
          delete next[targetEmployee.id];
        }
        return next;
      });
    }

    setAttendanceData(prev => {
      const existing = prev[targetDate] ?? [];
      const index = existing.findIndex(
        item => item.employeeCode === employeeCode
      );
      const updatedRecord: AdminAttendanceRecord = {
        id:
          index >= 0
            ? existing[index].id
            : `local-${employeeCode}-${targetDate}`,
        employeeCode,
        employeeName: targetEmployee.name,
        employeeInternalId: targetEmployee.id,
        employee: {
          employeeId: targetEmployee.id,
          name: targetEmployee.name
        },
        date: targetDate,
        clockIn,
        clockOut,
        breakMinutes,
        totalMinutes,
        shiftType: normalizedShift,
        status: nextStatus,
        notes,
        createdAt: index >= 0 ? existing[index].createdAt : null,
        updatedAt: new Date().toISOString(),
        overtimeMinutes: undefined
      };

      const nextList =
        index >= 0
          ? existing.map((item, idx) => (idx === index ? updatedRecord : item))
          : [...existing, updatedRecord];

      return {
        ...prev,
        [targetDate]: nextList
      };
    });

    setShowEditMonthlyModal(false);
    setEditingMonthlyRecord(null);
  };

  const EditAttendanceModal = ({ attendance, onClose, onSave }: { attendance: AdminAttendanceRecord | null; onClose: () => void; onSave: (data: Partial<AdminAttendanceRecord>) => void }) => {
    const initialShiftType = (attendance?.shiftType ?? 'day') as PunchShiftType;
    const [formData, setFormData] = useState({
      clockIn: attendance?.clockIn || '',
      clockOut: attendance?.clockOut || '',
      breakMinutes: attendance?.rawBreakMinutes ?? attendance?.breakMinutes ?? 60,
      shiftType: initialShiftType,
      notes: attendance?.notes ?? ''
    });

    const calculateTotalMinutes = () => {
      if (!formData.clockIn || !formData.clockOut) return 0;
      return calculateActualMinutes(
        formData.clockIn,
        formData.clockOut,
        formData.breakMinutes,
        formData.shiftType,
        {
          employeeId: attendance ? findEmployeeByKey(attendance.employeeInternalId ?? attendance.employeeCode)?.id : undefined,
          workDate: attendance?.date ?? selectedDate,
          baseDate: attendance?.date ?? selectedDate,
          clockInDate: formData.clockIn ? (attendance?.date ?? selectedDate) : null,
          clockOutDate: determineClockOutDate(attendance?.date ?? selectedDate, formData.shiftType, formData.clockIn, formData.clockOut,
            formData.clockOut === attendance?.clockOut ? extractISODatePart(attendance?.rawClockOutAt) : null)
        }
      );
    };

    return (
      <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4">
        <div className="flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:max-w-md sm:rounded-xl">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-bold text-gray-900">勤怠データ編集</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain p-6">
            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-gray-900">
                {attendance ? resolveEmployeeNameFromRecord(attendance) : ''}
              </p>
              <p className="text-xs text-gray-600">{selectedDate}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                勤務区分
              </label>
              <div className="grid grid-cols-3 gap-2">
                {SHIFT_TYPE_OPTIONS.filter(t => t.needsTime).map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, shiftType: type.value })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      formData.shiftType === type.value
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  出勤時刻
                </label>
                <input
                  type="time"
                  value={formData.clockIn}
                  onChange={(e) => setFormData({ ...formData, clockIn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  退勤時刻
                </label>
                <input
                  type="time"
                  value={formData.clockOut}
                  onChange={(e) => setFormData({ ...formData, clockOut: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                休憩時間(分)
              </label>
              <input
                type="number"
                value={formData.breakMinutes}
                onChange={(e) => setFormData({ ...formData, breakMinutes: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                min="0"
                step="15"
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">実働時間</span>
                <span className="font-bold text-blue-600">
                  {formatMinutesToTime(calculateTotalMinutes())}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                備考
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                rows={3}
                placeholder="例：臨時的に食事なし、会議のみ短時間出勤"
              />
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
            >
              キャンセル
            </button>
            <button
              onClick={() => {
                onSave({
                  clockIn: formData.clockIn,
                  clockOut: formData.clockOut,
                  breakMinutes: formData.breakMinutes,
                  totalMinutes: calculateTotalMinutes(),
                  shiftType: formData.shiftType,
                  status: formData.clockOut ? 'completed' : 'working',
                  notes: formData.notes
                });
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-1"
            >
              <Save className="w-4 h-4" />
              保存
            </button>
          </div>
        </div>
      </div>
    );
  };

  type MonthlyModalSavePayload = {
    date: string;
    shiftType: MonthlyRecord['shiftType'];
    clockIn: string | null;
    clockOut: string | null;
    clockOutDate: string | null;
    breakMinutes: number;
    totalMinutes: number;
    notes: string | null;
  };

  const EditMonthlyModal = ({
    record,
    onClose,
    onSave
  }: {
    record: MonthlyRecord | null;
    onClose: () => void;
    onSave: (data: MonthlyModalSavePayload) => Promise<void>;
  }) => {
    const monthKeyForBounds = useMemo(() => {
      if (record?.date) {
        return getClosingAlignedMonthString(record.date, closingDay);
      }
      if (/^\d{4}-\d{2}$/.test(selectedMonth)) {
        return selectedMonth;
      }
      return null;
    }, [record, closingDay, selectedMonth]);

    const initialDate = useMemo(() => {
      if (record?.date) {
        return record.date;
      }
      if (/^\d{4}-\d{2}$/.test(selectedMonth)) {
        return `${selectedMonth}-01`;
      }
      return getTodayString();
    }, [record, selectedMonth]);

    const monthBounds = useMemo(() => {
      if (monthKeyForBounds) {
        const range = computeMonthRange(monthKeyForBounds, closingDay);
        return { min: range.start, max: range.end };
      }
      return { min: undefined, max: undefined };
    }, [monthKeyForBounds, closingDay]);

    const initialShift = (record?.shiftType ?? 'day') as 'day' | 'night' | 'dayoff' | 'paid' | 'holiday_work';
    const initialClockInValue = record?.rawClockIn ?? record?.clockIn ?? '';
    const initialClockOutValue = record?.clockOut || '';
    const initialClockOutDate = determineClockOutDate(
      initialDate,
      initialShift,
      initialClockInValue || null,
      initialClockOutValue || null,
      record?.clockOutDate ?? null
    );

    const [targetDate, setTargetDate] = useState<string>(initialDate);
    const clockOutMin = targetDate;
    const clockOutMax = useMemo(() => addDaysToISODate(targetDate, 1), [targetDate]);
    const maxSelectableDate = useMemo(
      () => (monthBounds.max ? addDaysToISODate(monthBounds.max, 1) : monthBounds.max),
      [monthBounds.max]
    );

    const clampClockOutDate = useCallback(
      (value: string | null): string | null => {
        if (!value) return null;
        let next = value;
        if (next < clockOutMin) next = clockOutMin;
        if (next > clockOutMax) next = clockOutMax;
        return next;
      },
      [clockOutMin, clockOutMax]
    );

    const [formData, setFormData] = useState<{
      clockIn: string;
      clockOut: string;
      clockOutDate: string | null;
      breakMinutes: number;
      shiftType: 'day' | 'night' | 'dayoff' | 'paid' | 'holiday_work';
      notes: string;
    }>({
      clockIn: initialClockInValue,
      clockOut: initialClockOutValue,
      clockOutDate: clampClockOutDate(initialClockOutDate),
      breakMinutes:
        record?.rawBreakMinutes ?? record?.breakMinutes ??
        ((record?.shiftType ?? 'day') === 'day' ||
          (record?.shiftType ?? 'day') === 'night' ||
          (record?.shiftType ?? 'day') === 'holiday_work'
          ? 60
          : 0),
      shiftType: initialShift,
      notes: record?.notes ?? ''
    });
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const isMountedRef = useRef(true);

    const handleClockOutDateChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        const next = raw ? raw : targetDate;
        setFormData(prev => ({
          ...prev,
          clockOutDate: clampClockOutDate(next)
        }));
      },
      [clampClockOutDate, targetDate]
    );

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      setTargetDate(initialDate);
      const nextShift = (record?.shiftType ?? 'day') as 'day' | 'night' | 'dayoff' | 'paid' | 'holiday_work';
      const requiresTime = nextShift === 'day' || nextShift === 'night' || nextShift === 'holiday_work';
      const defaultClockIn = record?.rawClockIn ?? record?.clockIn ?? (requiresTime ? '09:00' : '');
      const defaultClockOut =
        record?.clockOut ??
        (requiresTime ? (nextShift === 'night' ? '09:00' : '18:00') : '');

      const clampToInitialRange = (value: string | null): string | null => {
        if (!value) return null;
        const min = initialDate;
        const max = addDaysToISODate(initialDate, 1);
        if (value < min) return min;
        if (value > max) return max;
        return value;
      };

      const defaultClockOutDate = requiresTime
        ? clampToInitialRange(
          determineClockOutDate(initialDate, nextShift, defaultClockIn || null, defaultClockOut || null, record?.clockOutDate ?? null)
        )
        : null;

      setFormData({
        clockIn: defaultClockIn,
        clockOut: defaultClockOut,
        clockOutDate: defaultClockOutDate,
        breakMinutes: record?.rawBreakMinutes ?? record?.breakMinutes ?? (requiresTime ? 60 : 0),
        shiftType: nextShift,
        notes: record?.notes ?? ''
      });
      setError(null);
      setSaving(false);
    }, [initialDate, record]);

    useEffect(() => {
      setFormData(prev => {
        if (!prev.clockOutDate || prev.shiftType === 'dayoff' || prev.shiftType === 'paid') {
          return prev;
        }
        let nextDate = prev.clockOutDate;
        if (nextDate < targetDate) {
          nextDate = prev.shiftType === 'night' ? addDaysToISODate(targetDate, 1) : targetDate;
        }
        nextDate = clampClockOutDate(nextDate) ?? nextDate;
        if (nextDate === prev.clockOutDate) {
          return prev;
        }
        return { ...prev, clockOutDate: nextDate };
      });
    }, [clampClockOutDate, targetDate]);

    const calculateTotalMinutes = () => {
      if (formData.shiftType === 'paid') return 480;
      if (formData.shiftType === 'dayoff') return 0;
      if (!formData.clockIn || !formData.clockOut) return 0;

      return calculateActualMinutes(
        formData.clockIn,
        formData.clockOut,
        formData.breakMinutes,
        formData.shiftType,
        {
          employeeId: selectedEmployeeForReport, workDate: targetDate,
          baseDate: targetDate,
          clockInDate: formData.clockIn ? targetDate : null,
          clockOutDate: formData.clockOut ? (formData.clockOutDate ?? targetDate) : null
        }
      );
    };

    const selectedShiftType = SHIFT_TYPE_OPTIONS.find(s => s.value === formData.shiftType);

    const validate = (): string | null => {
      if (!targetDate) {
        return '対象日を選択してください。';
      }
      if (monthBounds.min && targetDate < monthBounds.min) {
        return '選択された月の範囲内の日付を指定してください。';
      }
      if (maxSelectableDate && targetDate > maxSelectableDate) {
        return '選択された月の範囲内の日付を指定してください。';
      }
      if (!selectedShiftType) {
        return '勤務区分を選択してください。';
      }
      if (selectedShiftType.needsTime) {
        if (!formData.clockIn || !formData.clockOut) {
          return '出勤・退勤時刻を入力してください。';
        }
        if (!formData.clockOutDate) {
          return '退勤日を選択してください。';
        }
        if (formData.clockOutDate < targetDate) {
          return '退勤日は対象日以降の日付を指定してください。';
        }
        if (maxSelectableDate && formData.clockOutDate > maxSelectableDate) {
          return '退勤日は選択された月の範囲内で指定してください。';
        }
        if (
          formData.clockOutDate > targetDate &&
          !canClockOutRollOverToNextDay(
            formData.shiftType,
            formData.clockIn || null,
            formData.clockOut || null
          )
        ) {
          return '現在の勤務区分・時刻では翌日退勤にできません。';
        }
      }
      return null;
    };

    const handleSave = async () => {
      const validationMessage = validate();
      if (validationMessage) {
        setError(validationMessage);
        return;
      }
      setError(null);
      if (!isMountedRef.current) {
        return;
      }
      setSaving(true);
      try {
        await onSave({
          date: targetDate,
          shiftType: formData.shiftType,
          clockIn: selectedShiftType?.needsTime ? formData.clockIn : null,
          clockOut: selectedShiftType?.needsTime ? formData.clockOut : null,
          clockOutDate: selectedShiftType?.needsTime ? (formData.clockOutDate ?? targetDate) : null,
          breakMinutes: selectedShiftType?.needsTime ? formData.breakMinutes : 0,
          totalMinutes: calculateTotalMinutes(),
          notes: formData.notes
        });
      } catch (err: any) {
        const message =
          err?.message ??
          (err instanceof Error
            ? err.message
            : '月次データの保存に失敗しました。');
        if (isMountedRef.current) {
          setError(message);
        }
      } finally {
        if (isMountedRef.current) {
          setSaving(false);
        }
      }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4">
        <div className="flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:max-w-md sm:rounded-xl">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-bold text-gray-900">月次データ編集</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain p-6">
            <div className="space-y-2">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {employees.find(e => e.id === selectedEmployeeForReport)?.name ?? '従業員未選択'}
                </p>
                <p className="text-xs text-gray-600">{targetDate}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  対象日 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={targetDate}
                  min={monthBounds.min}
                  max={maxSelectableDate ?? monthBounds.max}
                  onChange={(event) => setTargetDate(event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  disabled={saving}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                勤務区分 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SHIFT_TYPE_OPTIONS.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() =>
                      setFormData(prev => {
                        const nextClockIn = !type.needsTime ? '' : prev.clockIn || '09:00';
                        const nextClockOut = !type.needsTime
                          ? ''
                          : prev.clockOut || (type.value === 'night' ? '09:00' : '18:00');
                        const nextClockOutDate = type.needsTime
                          ? clampClockOutDate(
                            determineClockOutDate(
                              targetDate,
                              type.value,
                              nextClockIn,
                              nextClockOut,
                              prev.clockOutDate
                            )
                          )
                          : null;
                        return {
                          ...prev,
                          shiftType: type.value,
                          clockIn: nextClockIn,
                          clockOut: nextClockOut,
                          clockOutDate: nextClockOutDate,
                          breakMinutes: !type.needsTime ? 0 : (prev.breakMinutes ?? 60)
                        };
                      })
                    }
                    disabled={saving}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${formData.shiftType === type.value
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedShiftType?.needsTime && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      出勤時刻
                    </label>
                    <input
                      type="time"
                      aria-label="出勤時刻"
                      value={formData.clockIn}
                      onChange={(event) =>
                        setFormData(prev => {
                          const nextClockIn = event.target.value;
                          const normalizedDate = clampClockOutDate(
                            determineClockOutDate(targetDate, prev.shiftType, nextClockIn, prev.clockOut, prev.clockOutDate)
                          );
                          return {
                            ...prev,
                            clockIn: nextClockIn,
                            clockOutDate: normalizedDate
                          };
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      退勤時刻
                      {formData.shiftType === 'night' && (
                        <span className="text-xs text-gray-500 block">(翌日可)</span>
                      )}
                    </label>
                    <input
                      type="time"
                      aria-label="退勤時刻"
                      value={formData.clockOut}
                      onChange={(event) =>
                        setFormData(prev => {
                          const nextClockOut = event.target.value;
                          const normalizedDate = clampClockOutDate(
                            determineClockOutDate(targetDate, prev.shiftType, prev.clockIn, nextClockOut, prev.clockOutDate)
                          );
                          return {
                            ...prev,
                            clockOut: nextClockOut,
                            clockOutDate: normalizedDate
                          };
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    退勤日
                  </label>
                  <input
                    type="date"
                    value={formData.clockOutDate ?? targetDate}
                    min={clockOutMin}
                    max={clockOutMax}
                    onChange={handleClockOutDateChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    休憩時間(分)
                  </label>
                  <input
                    type="number"
                    aria-label="休憩時間(分)"
                    value={formData.breakMinutes}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        breakMinutes: Number.isFinite(Number(event.target.value))
                          ? parseInt(event.target.value, 10) || 0
                          : 0
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    min="0"
                    step="15"
                    disabled={saving}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                備考
              </label>
              <textarea
                value={formData.notes}
                onChange={(event) => setFormData(prev => ({ ...prev, notes: event.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                rows={3}
                placeholder="例：臨時的に食事なし、会議のみ短時間出勤"
                disabled={saving}
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">実働時間</span>
                <span className="font-bold text-blue-600">
                  {formData.shiftType === 'paid' && '8時間00分(有給)'}
                  {formData.shiftType === 'dayoff' && '-'}
                  {['day', 'night', 'holiday_work'].includes(formData.shiftType) &&
                    formatMinutesToTime(calculateTotalMinutes())}
                </span>
              </div>
            </div>

            {formData.shiftType === 'night' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <p>💡 夜勤の場合、退勤時刻が翌日の午前中でも、この出勤日の行に記録されます。</p>
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
              disabled={saving}
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={saving}
            >
              <Save className="w-4 h-4" />
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const QualificationManagerModal = ({ onClose }: { onClose: () => void }) => {
    const [newName, setNewName] = useState('');
    const defaultNewOrder = useMemo(() => {
      if (qualifications.length === 0) return 1;
      return (
        qualifications.reduce(
          (max, option) => Math.max(max, Number.isFinite(option.displayOrder) ? option.displayOrder : 0),
          0
        ) + 1
      );
    }, [qualifications]);
    const [newOrder, setNewOrder] = useState<number>(defaultNewOrder);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [editingOrder, setEditingOrder] = useState<number>(1);
    const [editingActive, setEditingActive] = useState<boolean>(true);

    useEffect(() => {
      setNewOrder(defaultNewOrder);
    }, [defaultNewOrder]);

    useEffect(() => {
      if (!editingId) return;
      const target = qualifications.find(option => option.id === editingId);
      if (!target) {
        setEditingId(null);
        return;
      }
      setEditingName(target.name);
      setEditingOrder(Number.isFinite(target.displayOrder) ? target.displayOrder : 1);
      setEditingActive(target.isActive);
    }, [editingId, qualifications]);

    const handleAddQualification = async (event: React.FormEvent) => {
      event.preventDefault();
      const success = await handleCreateQualification(newName, newOrder, true);
      if (success) {
        setNewName('');
      }
    };

    const handleSaveEdit = async () => {
      if (!editingId) return;
      const success = await handleUpdateQualification(editingId, {
        name: editingName,
        displayOrder: editingOrder,
        isActive: editingActive
      });
      if (success) {
        setEditingId(null);
      }
    };

    const handleDeleteClick = async (qualification: QualificationMaster) => {
      if (!window.confirm(`「${qualification.name}」を削除しますか？\n従業員の資格データからも手動で削除する必要があります。`)) {
        return;
      }
      const success = await handleDeleteQualification(qualification.id);
      if (success && editingId === qualification.id) {
        setEditingId(null);
      }
    };

    const closeModal = () => {
      setQualificationMutationError(null);
      onClose();
    };

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={closeModal}
      >
        <div
          className="flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl max-h-[80vh]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-bold text-gray-900">資格マスター管理</h3>
            <button onClick={closeModal} className="rounded p-1 hover:bg-gray-100">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            <p className="text-xs text-gray-500">
              資格名を追加・編集すると、従業員編集画面に反映されます。名称変更や削除を行うと、既存の従業員データの資格名と不整合が生じる可能性があるためご注意ください。
            </p>

            {qualificationsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {qualificationsError}
              </div>
            )}
            {qualificationMutationError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {qualificationMutationError}
              </div>
            )}

            <form
              onSubmit={handleAddQualification}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <h4 className="text-sm font-semibold text-gray-800">新しい資格を追加</h4>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="資格名を入力"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  disabled={qualificationMutationLoading}
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">表示順</label>
                  <input
                    type="number"
                    min={1}
                    value={newOrder}
                    onChange={(e) => setNewOrder(Math.max(1, Number(e.target.value) || 1))}
                    className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    disabled={qualificationMutationLoading}
                  />
                </div>
                <button
                  type="submit"
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                  disabled={qualificationMutationLoading}
                >
                  追加
                </button>
              </div>
            </form>

            <div>
              <h4 className="mb-3 text-sm font-semibold text-gray-800">登録済みの資格</h4>
              {qualificationsLoading ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                  資格データを読み込み中です…
                </div>
              ) : qualifications.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                  資格が登録されていません。上部フォームから追加してください。
                </div>
              ) : (
                <ul className="grid gap-3 max-[1024px]:grid-cols-2 grid-cols-3">
                  {qualifications
                    .slice()
                    .sort((a, b) => {
                      if (a.displayOrder === b.displayOrder) {
                        return a.name.localeCompare(b.name, 'ja');
                      }
                      return a.displayOrder - b.displayOrder;
                    })
                    .map(qualification => (
                      <li
                        key={qualification.id}
                        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                      >
                        {editingId === qualification.id ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">資格名</label>
                                <input
                                  type="text"
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                  disabled={qualificationMutationLoading}
                                />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">表示順</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={editingOrder}
                                  onChange={(e) => setEditingOrder(Math.max(1, Number(e.target.value) || 1))}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                  disabled={qualificationMutationLoading}
                                />
                              </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={editingActive}
                                onChange={(e) => setEditingActive(e.target.checked)}
                                className="h-4 w-4 text-blue-500"
                                disabled={qualificationMutationLoading}
                              />
                              表示対象として有効にする
                            </label>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(null);
                                  setQualificationMutationError(null);
                                }}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                disabled={qualificationMutationLoading}
                              >
                                キャンセル
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                                disabled={qualificationMutationLoading}
                              >
                                更新
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{qualification.name}</p>
                                <p className="mt-1 text-xs text-gray-600">表示順: {qualification.displayOrder}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setQualificationMutationError(null);
                                  setEditingId(qualification.id);
                                }}
                                className="rounded-full border border-blue-200 px-4 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70"
                                disabled={qualificationMutationLoading}
                              >
                                編集
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${qualification.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                                  }`}
                              >
                                {qualification.isActive ? '表示中' : '非表示'}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(qualification)}
                                className="rounded-full border border-red-200 px-4 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
                                disabled={qualificationMutationLoading}
                              >
                                削除
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  type EmployeeFormData = {
    name: string;
    employmentType: string;
    jobType: string;
    hireDate: string;
    experienceStartDate: string;
    retirementDate: string;
    employeeCode: string;
    qualifications: string[];
    transportation: '車' | '公共交通機関';
    commuteDistanceKm: string;
    commuteCostYen: string;
    nightShiftAvailable: boolean;
    dayMealProvided: boolean;
    nightMealProvided: boolean;
    employeeCategory: string;
    hourlyRate: string;
    nightShiftPay: string;
    specialBenefitUnitPrice: string;
    specialAllowanceUnitPrice: string;
    partSpecialAllowanceUnitPrice: string;
    mealDeductionPerDay: string;
    commuteCostMonthlyFixed: string;
    mealCondition: string;
  };

  // 有効な選択肢の定義
  const validEmploymentTypes = ['正社員', 'パート', '契約社員', '嘱託', 'その他'];
  const validJobTypes = ['介護', '看護', '栄養士', '厨房', '調理', '清掃', '庶務', '生活相談員', '介護支援専門員', '管理者'];

  const createInitialEmployeeForm = (target: AdminEmployee | null): EmployeeFormData => {
    // 選択肢にない値の場合はデフォルト値に変換
    const employmentType = target?.employmentType && validEmploymentTypes.includes(target.employmentType)
      ? target.employmentType
      : '正社員';
    const jobType = target?.jobType && validJobTypes.includes(target.jobType)
      ? target.jobType
      : '介護';

    return {
      name: target?.name ?? '',
      employmentType,
      jobType,
      hireDate: target?.hireDate ?? '',
      experienceStartDate: target?.experienceStartDate ?? '',
      retirementDate: target?.retirementDate ?? '',
      employeeCode: target?.employeeCode ?? '',
      qualifications: target?.qualifications ?? [],
      transportation: target?.transportation ?? '車',
      commuteDistanceKm:
        target?.commuteDistanceKm !== null && target?.commuteDistanceKm !== undefined
          ? String(target.commuteDistanceKm)
          : '',
      commuteCostYen:
        target?.commuteCostYen !== null && target?.commuteCostYen !== undefined
          ? String(target.commuteCostYen)
          : '',
      nightShiftAvailable: target?.nightShiftAvailable ?? true,
      dayMealProvided: target?.dayMealProvided ?? true,
      nightMealProvided: target?.nightMealProvided ?? true,
      employeeCategory: target?.employeeCategory ?? '',
      hourlyRate: target?.hourlyRate != null ? String(target.hourlyRate) : '',
      nightShiftPay: target?.nightShiftPay != null ? String(target.nightShiftPay) : '',
      specialBenefitUnitPrice: target?.specialBenefitUnitPrice != null ? String(target.specialBenefitUnitPrice) : '',
      specialAllowanceUnitPrice: target?.specialAllowanceUnitPrice != null ? String(target.specialAllowanceUnitPrice) : '',
      partSpecialAllowanceUnitPrice: target?.partSpecialAllowanceUnitPrice != null ? String(target.partSpecialAllowanceUnitPrice) : '',
      mealDeductionPerDay: target?.mealDeductionPerDay != null ? String(target.mealDeductionPerDay) : '250',
      commuteCostMonthlyFixed: target?.commuteCostMonthlyFixed != null ? String(target.commuteCostMonthlyFixed) : '',
      mealCondition: target?.mealCondition ?? 'always',
    };
  };

  // 行政報告セクション
  const AdminReportSection = ({ employees }: { employees: AdminEmployee[] }) => {
    const [reportDate, setReportDate] = useState<string>(() => getTodayString());

    // 職種カテゴリマッピング
    const JOB_CATEGORIES: Record<string, string[]> = {
      '生活相談員': ['生活相談員'],
      '看護職員': ['看護'],
      '介護職員': ['介護'],
      '計画作成担当者': ['管理者', '介護支援専門員'],
      '栄養士': ['栄養士'],
      '調理師': ['厨房', '調理'],
      'その他従業員': ['清掃', '庶務']
    };

    // 介護資格リスト
    const CARE_QUALIFICATIONS = [
      { key: '介護福祉士', label: '介護福祉士' },
      { key: '実務者研修', label: '実務者研修' },
      { key: '初任者研修', label: '介護職員初任者研修' },
      { key: '介護支援専門員', label: '介護支援専門員' },
      { key: 'ケアマネージャー', label: '介護支援専門員' } // alias
    ];

    // 常勤/非常勤の判定
    const isFullTime = (emp: AdminEmployee) => emp.employmentType === '正社員';

    // 基準日時点で在籍しているか判定
    const isActiveAsOf = (emp: AdminEmployee, asOfDate: string): boolean => {
      // 入社日が基準日より後 → まだ入社していない
      if (emp.hireDate && emp.hireDate > asOfDate) return false;
      // 退職日が基準日以前 → 既に退職済み
      if (emp.retirementDate && emp.retirementDate <= asOfDate) return false;
      // statusがretiredで退職日がない場合は除外（退職日未設定の退職者）
      if (emp.status === 'retired' && !emp.retirementDate) return false;
      return true;
    };

    // 集計データを計算
    const reportData = useMemo(() => {
      const activeEmployees = employees.filter(emp => isActiveAsOf(emp, reportDate));

      // 常勤/非常勤別の総人数
      const fullTimeCount = activeEmployees.filter(isFullTime).length;
      const partTimeCount = activeEmployees.filter(emp => !isFullTime(emp)).length;

      // 職種別集計
      const jobCounts: Record<string, { fullTime: number; partTime: number }> = {};
      Object.keys(JOB_CATEGORIES).forEach(category => {
        jobCounts[category] = { fullTime: 0, partTime: 0 };
      });

      activeEmployees.forEach(emp => {
        let matched = false;
        for (const [category, jobTypes] of Object.entries(JOB_CATEGORIES)) {
          if (jobTypes.some(jt => emp.jobType?.includes(jt))) {
            if (isFullTime(emp)) {
              jobCounts[category].fullTime++;
            } else {
              jobCounts[category].partTime++;
            }
            matched = true;
            break;
          }
        }
        if (!matched) {
          // マッチしない場合は「その他従業員」に
          if (isFullTime(emp)) {
            jobCounts['その他従業員'].fullTime++;
          } else {
            jobCounts['その他従業員'].partTime++;
          }
        }
      });

      // 介護職員の資格集計
      const careEmployees = activeEmployees.filter(emp =>
        JOB_CATEGORIES['介護職員'].some(jt => emp.jobType?.includes(jt))
      );

      const qualificationCounts: Record<string, { fullTime: number; partTime: number }> = {};
      CARE_QUALIFICATIONS.forEach(q => {
        if (!qualificationCounts[q.label]) {
          qualificationCounts[q.label] = { fullTime: 0, partTime: 0 };
        }
      });

      careEmployees.forEach(emp => {
        const empQuals = emp.qualifications || [];
        CARE_QUALIFICATIONS.forEach(q => {
          if (empQuals.some(eq => eq.includes(q.key))) {
            if (isFullTime(emp)) {
              qualificationCounts[q.label].fullTime++;
            } else {
              qualificationCounts[q.label].partTime++;
            }
          }
        });
      });

      return {
        totalFullTime: fullTimeCount,
        totalPartTime: partTimeCount,
        total: fullTimeCount + partTimeCount,
        jobCounts,
        qualificationCounts
      };
    }, [employees, reportDate]);

    // PDF出力
    const handleExportPDF = async () => {
      const element = document.getElementById('admin-report-content');
      if (!element) return;

      try {
        // @ts-ignore - html2pdf is loaded dynamically
        const html2pdf = (await import('html2pdf.js')).default;
        html2pdf()
          .set({
            margin: 10,
            filename: `行政報告_${reportDate}.pdf`,
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
          })
          .from(element)
          .save();
      } catch (error) {
        console.error('PDF export failed:', error);
        alert('PDF出力に失敗しました。');
      }
    };

    return (
      <div className="space-y-6">
        {/* コントロール */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">基準日:</label>
              <input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              PDF出力
            </button>
          </div>
        </div>

        {/* レポート本体 */}
        <div id="admin-report-content" className="space-y-6">
          {/* 職種別従業員数 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">職種別従業員数</h3>
              <p className="text-sm text-gray-500 mt-1">{reportDate} 時点</p>
            </div>
            <div className="p-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">区分</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">常勤</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">非常勤</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">合計</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="font-bold bg-blue-50">
                    <td className="border border-gray-300 px-4 py-2">実人数</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{reportData.totalFullTime}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{reportData.totalPartTime}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{reportData.total}</td>
                  </tr>
                  {Object.entries(reportData.jobCounts).map(([category, counts]) => (
                    <tr key={category}>
                      <td className="border border-gray-300 px-4 py-2">{category}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">{counts.fullTime || '-'}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">{counts.partTime || '-'}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {counts.fullTime + counts.partTime || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 介護職員の資格保有状況 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">従業者である介護職員が有してる資格</h3>
              <p className="text-sm text-gray-500 mt-1">{reportDate} 時点</p>
            </div>
            <div className="p-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">区分</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">常勤</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">非常勤</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-100">
                    <td className="border border-gray-300 px-4 py-2 font-medium">延べ人数</td>
                    <td className="border border-gray-300 px-4 py-2 text-center"></td>
                    <td className="border border-gray-300 px-4 py-2 text-center"></td>
                  </tr>
                  {Object.entries(reportData.qualificationCounts).map(([qual, counts]) => (
                    <tr key={qual}>
                      <td className="border border-gray-300 px-4 py-2">{qual}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">{counts.fullTime}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">{counts.partTime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EmployeeModal = ({ employee, onClose }: { employee: AdminEmployee | null; onClose: () => void }) => {
    const [formData, setFormData] = useState<EmployeeFormData>(() => createInitialEmployeeForm(employee));

    useEffect(() => {
      setFormData(createInitialEmployeeForm(employee));
    }, [employee]);

    const activeQualificationNames = useMemo(
      () => activeQualificationOptions.map(option => option.name),
      [activeQualificationOptions]
    );

    const missingQualifications = useMemo(
      () => formData.qualifications.filter(qual => !activeQualificationNames.includes(qual)),
      [formData.qualifications, activeQualificationNames]
    );

    const toggleQualification = (qual: string) => {
      if (formData.qualifications.includes(qual)) {
        setFormData({
          ...formData,
          qualifications: formData.qualifications.filter(q => q !== qual)
        });
      } else {
        setFormData({
          ...formData,
          qualifications: [...formData.qualifications, qual]
        });
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // フォームデータの検証
      if (!formData.name.trim()) {
        alert('従業員名を入力してください');
        return;
      }

      const sanitizedEmployeeCode = formData.employeeCode ? formData.employeeCode.replace(/\D/g, '').slice(0, 5) : '';

      const {
        commuteDistanceKm, commuteCostYen,
        commuteCostMonthlyFixed: commuteCostMonthlyFixedStr,
        mealCondition: mealConditionStr,
        hourlyRate: hourlyRateStr, nightShiftPay: nightShiftPayStr,
        specialBenefitUnitPrice: specialBenefitStr,
        specialAllowanceUnitPrice: specialAllowanceStr,
        partSpecialAllowanceUnitPrice: partSpecialAllowanceStr,
        mealDeductionPerDay: mealDeductionStr,
        employeeCategory: employeeCategoryStr,
        ...rest
      } = formData;

      const commuteDistanceText = commuteDistanceKm.trim();
      const commuteCostText = commuteCostYen.trim();

      const parsedDistance = formData.transportation === '車' && commuteDistanceText !== ''
        ? Number(commuteDistanceText)
        : null;
      if (
        formData.transportation === '車' &&
        commuteDistanceText !== '' &&
        (parsedDistance === null || Number.isNaN(parsedDistance) || parsedDistance < 0)
      ) {
        alert('往復距離には0以上の数値を入力してください');
        return;
      }

      const parsedCost = commuteCostText !== '' ? Number(commuteCostText) : null;
      if (commuteCostText !== '' && (parsedCost === null || Number.isNaN(parsedCost) || parsedCost < 0)) {
        alert('1\u65E5\u306E\u4EA4\u901A\u8CBB\u306B\u306F0\u4EE5\u4E0A\u306E\u6570\u5024\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044');
        return;
      }

      const parsedMonthlyFixed = commuteCostMonthlyFixedStr.trim() !== '' ? Number(commuteCostMonthlyFixedStr) : null;

      const submissionData: Omit<AdminEmployee, 'id' | 'status'> = {
        ...(rest as Omit<AdminEmployee, 'id' | 'status'>),
        employeeCode: sanitizedEmployeeCode,
        commuteDistanceKm: formData.transportation === '車' ? parsedDistance : null,
        commuteCostYen: parsedCost
      };

      const validCategories: EmployeeCategory[] = ['fulltime', 'parttime'];
      const parsedEmployeeCategory = validCategories.includes(employeeCategoryStr as EmployeeCategory)
        ? (employeeCategoryStr as EmployeeCategory) : null;
      const parsedHourlyRate = hourlyRateStr.trim() !== '' ? Number(hourlyRateStr) : null;
      const parsedNightShiftPay = nightShiftPayStr.trim() !== '' ? Number(nightShiftPayStr) : null;
      const parsedSpecialBenefit = specialBenefitStr.trim() !== '' ? Number(specialBenefitStr) : null;
      const parsedSpecialAllowance = specialAllowanceStr.trim() !== '' ? Number(specialAllowanceStr) : null;
      const parsedPartSpecialAllowance = partSpecialAllowanceStr.trim() !== '' ? Number(partSpecialAllowanceStr) : null;
      if (parsedPartSpecialAllowance !== null && (!Number.isInteger(parsedPartSpecialAllowance) || parsedPartSpecialAllowance < 0)) {
        alert('\u30D1\u30FC\u30C8\u7279\u5225\u624B\u5F53\u306E\u6642\u9593\u5358\u4FA1\u306B\u306F0\u4EE5\u4E0A\u306E\u6574\u6570\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044');
        return;
      }
      const parsedMealDeduction = mealDeductionStr.trim() !== '' ? Number(mealDeductionStr) : 250;
      const parsedMealCondition = (mealConditionStr === 'long_shift_only' ? 'long_shift_only' : 'always') as 'always' | 'long_shift_only';

      Object.assign(submissionData, {
        employeeCategory: parsedEmployeeCategory,
        hourlyRate: parsedHourlyRate,
        nightShiftPay: parsedNightShiftPay,
        specialBenefitUnitPrice: parsedSpecialBenefit,
        specialAllowanceUnitPrice: parsedSpecialAllowance,
        partSpecialAllowanceUnitPrice: parsedPartSpecialAllowance,
        mealDeductionPerDay: parsedMealDeduction,
        commuteCostMonthlyFixed: parsedMonthlyFixed,
        mealCondition: parsedMealCondition,
      });

      if (employee) {
        await handleEditEmployee(submissionData);
      } else {
        await handleAddEmployee(submissionData as Omit<AdminEmployee, 'id' | 'status'>);
      }
    };

    // Payroll treats an unset category as part-time unless the employee is a regular employee (\u305D\u306E\u4ED6 is not on payroll),
    // so the part-time unit price must be editable for those employees too.
    const showsPartSpecialAllowance = formData.employeeCategory === 'parttime'
      || (formData.employeeCategory === '' && formData.employmentType !== '\u6B63\u793E\u54E1' && formData.employmentType !== '\u305D\u306E\u4ED6');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">
              {employee ? '従業員情報編集' : '新規従業員登録'}
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {employeeMutationError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
                  {employeeMutationError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    氏名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    placeholder="例: 山田 太郎"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    従業員コード
                  </label>
                  <input
                    type="text"
                    value={formData.employeeCode}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employeeCode: e.target.value.replace(/\D/g, '').slice(0, 5)
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    placeholder="例: 00001"
                    maxLength={5}
                    inputMode="numeric"
                    pattern="\d*"
                  />
                  <p className="mt-1 text-xs text-gray-500">入力しない場合は自動で発番されます。</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    雇用形態
                  </label>
                  <select
                    value={formData.employmentType}
                    onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="正社員">正社員</option>
                    <option value="パート">パート</option>
                    <option value="契約社員">契約社員</option>
                    <option value="嘱託">嘱託</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    職種
                  </label>
                  <select
                    value={formData.jobType}
                    onChange={(e) => setFormData({ ...formData, jobType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="介護">介護</option>
                    <option value="看護">看護</option>
                    <option value="栄養士">栄養士</option>
                    <option value="厨房">厨房</option>
                    <option value="調理">調理</option>
                    <option value="清掃">清掃</option>
                    <option value="庶務">庶務</option>
                    <option value="生活相談員">生活相談員</option>
                    <option value="介護支援専門員">介護支援専門員</option>
                    <option value="管理者">管理者</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    入社日
                  </label>
                  <input
                    type="date"
                    value={formData.hireDate}
                    onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    実務経験開始日
                  </label>
                  <input
                    type="date"
                    value={formData.experienceStartDate}
                    onChange={(e) => setFormData({ ...formData, experienceStartDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    退職日
                  </label>
                  <input
                    type="date"
                    value={formData.retirementDate}
                    onChange={(e) => setFormData({ ...formData, retirementDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    通勤手段
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="車"
                        checked={formData.transportation === '車'}
                        onChange={(e) =>
                          setFormData(prev => ({
                            ...prev,
                            transportation: e.target.value as '車' | '公共交通機関',
                            commuteDistanceKm: prev.commuteDistanceKm
                          }))
                        }
                        className="mr-2"
                      />
                      <Car className="w-4 h-4 mr-1" />
                      車
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="公共交通機関"
                        checked={formData.transportation === '公共交通機関'}
                        onChange={(e) =>
                          setFormData(prev => ({
                            ...prev,
                            transportation: e.target.value as '車' | '公共交通機関',
                            commuteDistanceKm: ''
                          }))
                        }
                        className="mr-2"
                      />
                      <Train className="w-4 h-4 mr-1" />
                      公共交通機関
                    </label>
                  </div>
                  {formData.transportation === '車' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        往復距離 (km)
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        value={formData.commuteDistanceKm}
                        onChange={(e) => setFormData({ ...formData, commuteDistanceKm: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                        placeholder="例: 12.5"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                  <label className="text-sm font-medium text-gray-700">
                    資格
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setQualificationMutationError(null);
                      setShowQualificationManager(true);
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    資格を編集
                  </button>
                </div>
                {qualificationsLoading ? (
                  <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
                    資格データを読み込み中です…
                  </div>
                ) : (
                  <>
                    {activeQualificationOptions.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {activeQualificationOptions.map(option => (
                          <label
                            key={option.id}
                            className={`flex items-center rounded-lg border-2 p-3 text-sm transition-all ${formData.qualifications.includes(option.name)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                              }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.qualifications.includes(option.name)}
                              onChange={() => toggleQualification(option.name)}
                              className="mr-2 h-4 w-4 text-blue-500"
                            />
                            <span>{option.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
                        利用可能な資格が登録されていません。「資格を編集」ボタンから追加してください。
                      </div>
                    )}
                  </>
                )}
                {qualificationsError && (
                  <p className="mt-2 text-xs text-red-600">{qualificationsError}</p>
                )}
                {missingQualifications.length > 0 && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold text-amber-700">
                      非表示になっている資格
                    </p>
                    <p className="mt-1 text-xs text-amber-700">
                      現在の従業員データに含まれています。必要に応じて「資格を編集」から再度有効化するか、以下のボタンで削除してください。
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {missingQualifications.map(qual => (
                        <button
                          key={qual}
                          type="button"
                          onClick={() => toggleQualification(qual)}
                          className="rounded-full border border-amber-400 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
                        >
                          {qual} を削除
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="nightShift"
                    checked={formData.nightShiftAvailable}
                    onChange={(e) => setFormData({ ...formData, nightShiftAvailable: e.target.checked })}
                    className="w-4 h-4 text-blue-500 mr-2"
                  />
                  <label htmlFor="nightShift" className="text-sm text-gray-700">
                    夜勤可
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dayMeal"
                    checked={formData.dayMealProvided}
                    onChange={(e) => setFormData({ ...formData, dayMealProvided: e.target.checked })}
                    className="w-4 h-4 text-blue-500 mr-2"
                  />
                  <label htmlFor="dayMeal" className="text-sm text-gray-700">
                    食事支給(昼食)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="nightMeal"
                    checked={formData.nightMealProvided}
                    onChange={(e) => setFormData({ ...formData, nightMealProvided: e.target.checked })}
                    className="w-4 h-4 text-blue-500 mr-2"
                  />
                  <label htmlFor="nightMeal" className="text-sm text-gray-700">
                    食事支給(夜食)
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-bold text-gray-800 mb-4">{'\u7D66\u4E0E\u8A2D\u5B9A'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {'\u7D66\u4E0E\u30AB\u30C6\u30B4\u30EA'}
                    </label>
                    <select
                      value={formData.employeeCategory}
                      onChange={(e) => setFormData({ ...formData, employeeCategory: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="">{'\u672A\u8A2D\u5B9A'}</option>
                      <option value="fulltime">{'\u6B63\u8077\u54E1'}</option>
                      <option value="parttime">{'\u975E\u5E38\u52E4'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {'\u98DF\u4E8B\u63A7\u9664\u984D (\u5186/\u65E5)'}
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={formData.mealDeductionPerDay}
                      onChange={(e) => setFormData({ ...formData, mealDeductionPerDay: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      placeholder="250"
                    />
                  </div>
                </div>
                {formData.employeeCategory === 'fulltime' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {'\u7279\u5225\u7D66\u4ED8\u91D1 (\u56FA\u5B9A\u984D/\u5186)'}
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={formData.specialBenefitUnitPrice}
                        onChange={(e) => setFormData({ ...formData, specialBenefitUnitPrice: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {'\u7279\u5225\u624B\u5F53 (\u56FA\u5B9A\u984D/\u5186)'}
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={formData.specialAllowanceUnitPrice}
                        onChange={(e) => setFormData({ ...formData, specialAllowanceUnitPrice: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}
                {showsPartSpecialAllowance && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label htmlFor="partSpecialAllowanceUnitPrice" className="block text-sm font-medium text-gray-700 mb-2">
                        {'\u30D1\u30FC\u30C8\u7279\u5225\u624B\u5F53 \u6642\u9593\u5358\u4FA1 (\u5186)'}
                      </label>
                      <input
                        id="partSpecialAllowanceUnitPrice"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        value={formData.partSpecialAllowanceUnitPrice}
                        onChange={(e) => setFormData({ ...formData, partSpecialAllowanceUnitPrice: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                        placeholder="0"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {'2026\u5E749\u670816\u65E5\u4EE5\u964D\u306E\u7D66\u4E0E\u8A08\u7B97\u3067\u4F7F\u7528\u3002\u52E4\u52D9\u6642\u9593\u306E\u5408\u8A08\uFF0829\u5206\u4EE5\u4E0B\u5207\u308A\u6368\u3066\u300130\u5206\u4EE5\u4E0A\u5207\u308A\u4E0A\u3052\uFF09\u00D7\u6642\u9593\u5358\u4FA1'}
                      </p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {'1\u65E5\u306E\u4EA4\u901A\u8CBB (\u5186)'}
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={formData.commuteCostYen}
                      onChange={(e) => setFormData({ ...formData, commuteCostYen: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {'\u6708\u984D\u56FA\u5B9A\u4EA4\u901A\u8CBB (\u5186)'}
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={formData.commuteCostMonthlyFixed}
                      onChange={(e) => setFormData({ ...formData, commuteCostMonthlyFixed: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      placeholder={'\u672A\u8A2D\u5B9A\u306E\u5834\u5408\u306F1\u65E5\u00D7\u51FA\u52E4\u65E5\u6570'}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {'\u98DF\u4E8B\u63A7\u9664\u6761\u4EF6'}
                    </label>
                    <select
                      value={formData.mealCondition}
                      onChange={(e) => setFormData({ ...formData, mealCondition: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="always">{'\u5E38\u306B\u63A7\u9664'}</option>
                      <option value="long_shift_only">{'8\u6642\u9593\u4EE5\u4E0A\u306E\u307F'}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={employeeMutationLoading}
                className={`px-4 py-2 rounded-lg text-sm flex items-center gap-1 ${employeeMutationLoading
                  ? 'bg-blue-300 text-white cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                <Save className="w-4 h-4" />
                {employeeMutationLoading ? '保存中...' : employee ? '更新' : '登録'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 月次レポートの集計データを計算
  const monthlyReportData = employees
    .filter(emp => {
      const hasRecords = (monthlyData[emp.id] ?? []).length > 0;
      const hasSummary = monthlySummaries[emp.id] !== undefined && monthlySummaries[emp.id] !== null;
      return emp.status === 'active' || hasRecords || hasSummary;
    })
    .map(emp => {
      const empMonthlyData = monthlyData[emp.id] || [];
      const summary = monthlySummaries[emp.id] ?? null;
      const carryAdjust = monthlyCarryInAdjustments[emp.id];
      const carryWorkDays = carryAdjust?.work ?? 0;
      const hasMonthlyRows = empMonthlyData.length > 0;
      const weightedDaysFromRecords = empMonthlyData.reduce(
        (sum, record) => sum + getDayWeightForRecord(record, selectedMonthRange.end, closingDay),
        0
      );
      const attendanceCountFromRecords = empMonthlyData.reduce(
        (sum, record) => sum + getShiftAttendanceWeight(record.shiftType),
        0
      );
      const nightShiftDaysFromRecords = empMonthlyData.filter(r => r.shiftType === 'night').length;
      const totalWorkMinutesFromRecords = empMonthlyData.reduce((sum, record) => sum + record.totalMinutes, 0);
      const totalOvertimeFromRecords = empMonthlyData.reduce((sum, record) => sum + calculateOvertime(record), 0);

      const totalWorkDays = hasMonthlyRows
        ? weightedDaysFromRecords + carryWorkDays
        : summary?.days_worked ?? (weightedDaysFromRecords + carryWorkDays);
      const totalAttendanceCount = hasMonthlyRows
        ? attendanceCountFromRecords
        : summary?.attendance_count ?? attendanceCountFromRecords;
      const totalPaidLeave = empMonthlyData.filter(r => r.shiftType === 'paid').length;
      const totalOvertimeMinutes = hasMonthlyRows
        ? totalOvertimeFromRecords
        : summary?.overtime_minutes ?? 0;
      const grossWorkMinutes = hasMonthlyRows
        ? totalWorkMinutesFromRecords
        : summary?.total_work_minutes ?? 0;
      const totalActualMinutes = Math.max(0, grossWorkMinutes - totalOvertimeMinutes);
      const unconfirmedCount = summary?.unconfirmed_count ?? 0;
      const totalNightShifts = hasMonthlyRows
        ? nightShiftDaysFromRecords
        : summary?.night_shift_count ?? nightShiftDaysFromRecords;

      return {
        employee: emp,
        totalWorkDays,
        attendanceCount: totalAttendanceCount,
        totalPaidLeave,
        totalActualMinutes,
        totalOvertimeMinutes,
        unconfirmedCount,
        totalNightShifts,
        averageDailyMinutes: totalWorkDays > 0 ? Math.round(totalActualMinutes / totalWorkDays) : 0
      };
    });

  // 月次レポートの対象期間(締め日基準: 前月16日〜当月15日)
  const monthlyReportPeriod = useMemo(() => {
    const closing = parseInt(closingDay, 10) || 15;
    const [y, m] = selectedMonth.split('-').map(Number);
    if (!y || !m) return { start: '', end: '' };
    const prevMonth = m === 1 ? 12 : m - 1;
    const prevYear = m === 1 ? y - 1 : y;
    const start = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(closing + 1).padStart(2, '0')}`;
    const end = `${y}-${String(m).padStart(2, '0')}-${String(closing).padStart(2, '0')}`;
    return { start, end };
  }, [selectedMonth, closingDay]);

  // 月次レポートのドロップダウン対象(期間内に在籍していた人: 現役+対象期間中の退職者)
  const monthlyReportEmployees = useMemo(() => {
    const hasMonthlyRecord = (emp: AdminEmployee): boolean =>
      (monthlyData[emp.id] ?? []).length > 0 ||
      (monthlySummaries[emp.id] !== undefined && monthlySummaries[emp.id] !== null);

    if (!monthlyReportPeriod.start || !monthlyReportPeriod.end) {
      return sortEmployeesDefault(employees.filter(emp => emp.status === 'active' || hasMonthlyRecord(emp)));
    }
    return sortEmployeesDefault(
      employees.filter(emp =>
        isActiveInPeriod(emp, monthlyReportPeriod.start, monthlyReportPeriod.end) ||
        hasMonthlyRecord(emp)
      )
    );
  }, [employees, monthlyData, monthlyReportPeriod, monthlySummaries]);

  useEffect(() => {
    if (monthlyReportEmployees.length === 0) {
      if (selectedEmployeeForReport) {
        setSelectedEmployeeForReport('');
        setEmployeeCodeInputForMonthly('');
      }
      return;
    }

    const currentStillSelectable =
      selectedEmployeeForReport &&
      monthlyReportEmployees.some(emp => emp.id === selectedEmployeeForReport);

    if (!currentStillSelectable) {
      const firstEmployee = monthlyReportEmployees[0];
      setSelectedEmployeeForReport(firstEmployee.id);
      setEmployeeCodeInputForMonthly(firstEmployee.employeeCode ?? '');
    }
  }, [monthlyReportEmployees, selectedEmployeeForReport]);

  // 選択された従業員の月次データ
  const selectedEmployeeMonthlyData = selectedEmployeeForReport
    ? monthlyData[selectedEmployeeForReport] || []
    : [];

  // 選択された従業員の集計データ
  const selectedEmployeeReport = monthlyReportData.find(data => data.employee.id === selectedEmployeeForReport);
  const selectedEmployeeName = selectedEmployeeForReport
    ? resolveEmployeeName(selectedEmployeeForReport, '従業員未選択')
    : '従業員未選択';

  const monthlyDailyRowsForCsv = useMemo(() => {
    const rows: Array<{
      employeeId: string;
      employeeCode: string;
      employeeName: string;
      record: MonthlyRecord;
    }> = [];

    monthlyReportEmployees.forEach(emp => {
      const records = monthlyData[emp.id] ?? [];
      const code = emp.employeeCode ? String(emp.employeeCode) : '';
      records.forEach(record => {
        rows.push({
          employeeId: emp.id,
          employeeCode: code,
          employeeName: emp.name,
          record
        });
      });
    });

    return rows;
  }, [monthlyData, monthlyReportEmployees]);

  const exportMonthlyCSV = (): void => {
    if (!selectedEmployeeReport || !selectedEmployeeForReport) {
      return;
    }

    const { start: periodStart, end: periodEnd } = computeMonthRange(selectedMonth, closingDay);
    const targetEmployee = employees.find(emp => emp.id === selectedEmployeeForReport);
    if (!targetEmployee) {
      return;
    }

    const targetName = targetEmployee.name || '従業員未設定';
    const safeEmployeeName = targetName.replace(/[\\/:*?"<>|]/g, '_');
    const employeeCode = targetEmployee.employeeCode ? String(targetEmployee.employeeCode) : '';
    const displayActualMinutes = formatMinutesToTime(selectedEmployeeReport.totalActualMinutes ?? 0);
    const displayOvertimeMinutes = formatMinutesToTime(selectedEmployeeReport.totalOvertimeMinutes ?? 0);
    const displayAverageMinutes = formatMinutesToTime(selectedEmployeeReport.averageDailyMinutes ?? 0);

    const csvLines: string[] = [];
    csvLines.push(
      toCsvLine([
        '社員コード',
        '従業員名',
        '対象期間開始',
        '対象期間終了',
        '出勤日数',
        '出勤回数',
        '夜勤回数',
        '有給日数',
        '実働時間(分)',
        '実働時間',
        '残業時間(分)',
        '残業時間',
        '平均実働時間(分)',
        '平均実働時間',
        '未承認件数'
      ])
    );
    csvLines.push(
      toCsvLine([
        employeeCode,
        targetName,
        periodStart,
        periodEnd,
        selectedEmployeeReport.totalWorkDays ?? 0,
        selectedEmployeeReport.attendanceCount ?? 0,
        selectedEmployeeReport.totalNightShifts ?? 0,
        selectedEmployeeReport.totalPaidLeave ?? 0,
        selectedEmployeeReport.totalActualMinutes ?? 0,
        displayActualMinutes,
        selectedEmployeeReport.totalOvertimeMinutes ?? 0,
        displayOvertimeMinutes,
        selectedEmployeeReport.averageDailyMinutes ?? 0,
        displayAverageMinutes,
        selectedEmployeeReport.unconfirmedCount ?? 0
      ])
    );

    csvLines.push('');
    csvLines.push('日次データ');
    csvLines.push(
      toCsvLine([
        '社員コード',
        '従業員名',
        '日付',
        '勤務区分',
        '出勤',
        '退勤',
        '休憩(分)',
        '実働時間(分)',
        '実働時間',
        '残業時間(分)',
        '残業時間',
        '備考'
      ])
    );

    selectedEmployeeMonthlyData.forEach(record => {
      const shiftLabel = SHIFT_TYPE_LABELS[record.shiftType] ?? record.shiftType ?? '';
      const clockIn = record.clockIn ?? '';
      const clockOut = record.clockOut ?? '';
      const breakMinutes = Number.isFinite(record.breakMinutes) ? record.breakMinutes : 0;
      const overtimeMinutes = calculateOvertime(record);
      const displayOvertime = overtimeMinutes > 0 ? formatMinutesToTime(overtimeMinutes) : '-';
      const actualMinutes = Number.isFinite(record.totalMinutes) ? record.totalMinutes : 0;
      const displayActual =
        record.shiftType === 'paid'
          ? '8時間00分(有給)'
          : record.shiftType === 'dayoff'
            ? '-'
            : formatMinutesToTime(actualMinutes);

      csvLines.push(
        toCsvLine([
          employeeCode,
          targetName,
          record.date,
          shiftLabel,
          clockIn,
          clockOut,
          breakMinutes,
          actualMinutes,
          displayActual,
          overtimeMinutes,
          displayOvertime,
          record.notes ?? ''
        ])
      );
    });

    const csvContent = csvLines.join('\r\n');
    const encoder = new TextEncoder();
    const csvBuffer = encoder.encode(`\uFEFF${csvContent}`);
    const blob = new Blob([csvBuffer], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `月次集計_${safeEmployeeName}_${selectedMonth}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 0);
  };

  const exportDailyCSV = (): void => {
    if (monthlyDailyRowsForCsv.length === 0) {
      return;
    }

    const sortedRows = [...monthlyDailyRowsForCsv].sort((a, b) => {
      const codeCompare = compareEmployeeCode(a.employeeCode, b.employeeCode, a.employeeName, b.employeeName);
      if (codeCompare !== 0) {
        return codeCompare;
      }
      const dateCompare = a.record.date.localeCompare(b.record.date);
      if (dateCompare !== 0) {
        return dateCompare;
      }
      return a.employeeName.localeCompare(b.employeeName, 'ja');
    });

    const csvLines: string[] = [
      toCsvLine([
        '社員コード',
        '従業員名',
        '日付',
        '勤務区分',
        '出勤',
        '退勤',
        '休憩(分)',
        '実働時間(分)',
        '実働時間',
        '残業時間(分)',
        '残業時間',
        '備考'
      ])
    ];

    sortedRows.forEach(row => {
      const { record } = row;
      const actualMinutes = Number.isFinite(record.totalMinutes) ? record.totalMinutes : 0;
      const breakMinutes = Number.isFinite(record.breakMinutes) ? record.breakMinutes : 0;
      const overtimeMinutes = calculateOvertime(record);
      const displayActual =
        record.shiftType === 'paid'
          ? '8時間00分(有給)'
          : record.shiftType === 'dayoff'
            ? '-'
            : formatMinutesToTime(actualMinutes);
      const displayOvertime = overtimeMinutes > 0 ? formatMinutesToTime(overtimeMinutes) : '-';

      csvLines.push(
        toCsvLine([
          row.employeeCode,
          row.employeeName,
          record.date,
          SHIFT_TYPE_LABELS[record.shiftType] ?? record.shiftType ?? '',
          record.clockIn ?? '',
          record.clockOut ?? '',
          breakMinutes,
          actualMinutes,
          displayActual,
          overtimeMinutes,
          displayOvertime,
          record.notes ?? ''
        ])
      );
    });

    const csvContent = csvLines.join('\r\n');
    const encoder = new TextEncoder();
    const csvBuffer = encoder.encode(`\uFEFF${csvContent}`);
    const blob = new Blob([csvBuffer], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `日次データ_${selectedMonth}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 0);
  };

  const handleSelectDate = useCallback(
    (date: string) => {
      setSelectedDate(date);
      void refreshAttendance();
    },
    [refreshAttendance]
  );

  const handleSelectToday = useCallback(() => {
    handleSelectDate(getTodayString());
  }, [handleSelectDate]);

  const shiftSelectedDate = useCallback(
    (days: number) => {
      if (!selectedDate) return;
      const base = new Date(`${selectedDate}T00:00:00`);
      base.setDate(base.getDate() + days);
      const shifted = new Date(base.getTime() - base.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];
      handleSelectDate(shifted);
    },
    [selectedDate, handleSelectDate]
  );

  const handleBulkPunchClick = useCallback(() => {
    setShowBulkPunchModal(true);
  }, []);

  const handleManualAddClick = useCallback(() => {
    const preferred =
      (selectedEmployeeForReport &&
        employees.some(emp => emp.id === selectedEmployeeForReport && emp.employeeCode)
        ? selectedEmployeeForReport
        : null) ??
      (employees.find(emp => emp.status === 'active' && emp.employeeCode)?.id ?? null);
    setManualAttendanceInitialEmployeeId(preferred ?? null);
    setShowManualAttendanceModal(true);
  }, [employees, selectedEmployeeForReport]);

  const handleManualAttendanceSave = useCallback(
    async (input: ManualAttendancePayload): Promise<void> => {
      const employee = employees.find(emp => emp.id === input.employeeId);
      if (!employee) {
        throw new Error('従業員データが見つかりません。');
      }

      await saveAttendanceRecords([
        {
          employee,
          date: input.date,
          shiftType: input.shiftType,
          clockIn: input.clockIn,
          clockOut: input.clockOut,
          breakMinutes: input.breakMinutes,
          clockInDate: input.clockIn ? input.date : null,
          clockOutDate: input.clockOut ? (input.clockOutDate ?? input.date) : null,
          notes: input.notes
        }
      ]);

      setShowManualAttendanceModal(false);
      setManualAttendanceInitialEmployeeId(null);
    },
    [employees, saveAttendanceRecords]
  );

  const handleDeleteAttendanceRecord = useCallback(
    async (record: AdminAttendanceRecord): Promise<void> => {
      const displayName = resolveEmployeeNameFromRecord(record);
      if (!window.confirm(`${displayName} の ${record.date} の勤怠を削除しますか？`)) {
        return;
      }

      const employee = findEmployeeByKey(record.employeeInternalId ?? record.employeeCode ?? null);

      if (supabaseClient && record.id && !String(record.id).startsWith('local-')) {
        try {
          setAttendanceLoading(true);
          setAttendanceError(null);

          const { error } = await supabaseClient
            .from('attendance_records')
            .delete()
            .eq('id', record.id);

          if (error) {
            throw error;
          }

          await Promise.all([refreshAttendance(), fetchMonthly()]);
        } catch (error: any) {
          console.error('Failed to delete attendance record', error);
          setAttendanceError(
            error?.code === '42501'
              ? '勤怠データを削除する権限がありません。Supabase の RLS ポリシーを確認してください。'
              : `勤怠データの削除に失敗しました: ${error?.message ?? String(error)}`
          );
          return;
        } finally {
          setAttendanceLoading(false);
        }

        if (editingAttendance && editingAttendance.id === record.id) {
          setEditingAttendance(null);
        }

        return;
      }

      if (employee) {
        removeAttendanceRecords([
          {
            employee,
            date: record.date,
            shiftType: (record.shiftType as PunchShiftType) ?? 'day',
            clockIn: record.clockIn,
            clockOut: record.clockOut,
            breakMinutes: record.breakMinutes
          }
        ]);
      } else {
        setAttendanceData(prev => {
          const next = { ...prev };
          const current = next[record.date] ?? [];
          const filtered = current.filter(item => item.id !== record.id);
          if (filtered.length > 0) {
            next[record.date] = filtered;
          } else {
            delete next[record.date];
          }
          return next;
        });
      }
    },
    [
      fetchMonthly,
      findEmployeeByKey,
      removeAttendanceRecords,
      resolveEmployeeNameFromRecord,
      refreshAttendance,
      supabaseClient,
      editingAttendance
    ]
  );

  const handleDeleteMonthlyRecord = useCallback(
    async (record: MonthlyRecord): Promise<void> => {
      if (!selectedEmployeeForReport) {
        setMonthlyError('従業員を選択してください。');
        return;
      }

      const employee = employees.find(emp => emp.id === selectedEmployeeForReport);
      if (!employee) {
        setMonthlyError('従業員データが見つかりません。');
        return;
      }

      if (!window.confirm(`${record.date} の月次データを削除しますか？`)) {
        return;
      }

      const employeeCode = employee.employeeCode ? String(employee.employeeCode) : null;

      if (supabaseClient && (record.id || employeeCode)) {
        try {
          setMonthlyLoading(true);
          setMonthlyError(null);

          const deleteQuery = supabaseClient
            .from('attendance_records')
            .delete();
          const { error } = record.id
            ? await deleteQuery.eq('id', record.id)
            : await deleteQuery
              .eq('employee_code', employeeCode)
              .eq('work_date', record.date);

          if (error) {
            throw error;
          }

          await Promise.all([fetchMonthly(), refreshAttendance()]);
        } catch (error: any) {
          console.error('Failed to delete monthly record', error);
          setMonthlyError(
            error?.code === '42501'
              ? '月次データを削除する権限がありません。Supabase の RLS ポリシーを確認してください。'
              : `月次データの削除に失敗しました: ${error?.message ?? String(error)}`
          );
          return;
        } finally {
          setMonthlyLoading(false);
        }

        return;
      }

      removeAttendanceRecords([
        {
          employee,
          date: record.date,
          shiftType: record.shiftType as PunchShiftType,
          clockIn: record.clockIn ?? null,
          clockOut: record.clockOut ?? null,
          breakMinutes: record.breakMinutes,
          clockInDate: record.clockIn ? record.date : null,
          clockOutDate: record.clockOut ? (record.clockOutDate ?? record.date) : null
        }
      ]);
    },
    [
      employees,
      fetchMonthly,
      refreshAttendance,
      removeAttendanceRecords,
      selectedEmployeeForReport,
      supabaseClient
    ]
  );

  const handleBulkPunchSave = useCallback(
    async (input: BulkPunchPayload): Promise<void> => {
      const targets = employees.filter(emp => input.employeeIds.includes(emp.id));
      if (targets.length === 0) {
        throw new Error('従業員が選択されていません。');
      }

      const inferredClockOutDate = (() => {
        if (!input.clockOut) return null;
        if (input.shiftType === 'night') {
          const [outHour] = input.clockOut.split(':').map(Number);
          if (Number.isFinite(outHour) && outHour < 12) {
            return addDaysToISODate(input.date, 1);
          }
        }
        return input.date;
      })();

      await saveAttendanceRecords(
        targets.map(employee => ({
          employee,
          date: input.date,
          shiftType: input.shiftType,
          clockIn: input.clockIn,
          clockOut: input.clockOut,
          breakMinutes: input.breakMinutes,
          clockInDate: input.clockIn ? (input.clockInDate ?? input.date) : null,
          clockOutDate: input.clockOut ? (input.clockOutDate ?? inferredClockOutDate ?? input.date) : null
        }))
      );

      setShowBulkPunchModal(false);
    },
    [employees, saveAttendanceRecords]
  );


  const handleSaveSettings = useCallback(async () => {
    if (!isSettingsDirty) {
      return;
    }

    if (!supabaseClient) {
      setSettingsError('Supabaseの設定が見つかりません。`.env.local` を確認してください。');
      return;
    }

    setSettingsLoading(true);
    setSettingsError(null);

    const payload = {
      cutoff: closingDayToCutoff(closingDay),
      round_clock_in_ceil_30: roundUpClockIn,
      day_break_minutes: dayBreakMinutes,
      night_break_minutes: nightBreakMinutes,
      day_standard_hours: dayOvertimeBaseHours,
      night_standard_hours: nightOvertimeBaseHours,
      updated_at: new Date().toISOString()
    };

    try {
      if (payrollSettingsId) {
        const { error } = await supabaseClient
          .from('payroll_settings')
          .update(payload)
          .eq('id', payrollSettingsId);
        if (error) {
          throw error;
        }
      } else {
        const { data, error } = await supabaseClient
          .from('payroll_settings')
          .insert([payload])
          .select('id')
          .single();
        if (error) {
          throw error;
        }
        setPayrollSettingsId(data?.id ?? null);
      }

      initialSettingsRef.current = {
        closingDay,
        roundUpClockIn,
        dayBreakMinutes,
        nightBreakMinutes,
        dayOvertimeBaseHours,
        nightOvertimeBaseHours
      };

      if (settingsSaveTimeoutRef.current) {
        clearTimeout(settingsSaveTimeoutRef.current);
      }
      setSettingsSaveNotice('設定内容を保存しました。');
      settingsSaveTimeoutRef.current = setTimeout(() => {
        setSettingsSaveNotice(null);
        settingsSaveTimeoutRef.current = null;
      }, 3000);
    } catch (err: any) {
      console.error('Failed to save payroll settings', err);
      setSettingsError(`設定の保存に失敗しました: ${err?.message ?? String(err)}`);
    } finally {
      setSettingsLoading(false);
    }
  }, [
    closingDay,
    closingDayToCutoff,
    dayBreakMinutes,
    dayOvertimeBaseHours,
    isSettingsDirty,
    nightBreakMinutes,
    nightOvertimeBaseHours,
    payrollSettingsId,
    roundUpClockIn,
    supabaseClient
  ]);

  return (
    <div className="flex h-screen min-h-0 overflow-hidden bg-gray-50">
      {/* サイドバー */}
      <div
        className={`hidden h-full bg-white shadow-lg transition-all duration-300 min-[1024px]:flex min-[1024px]:flex-col ${sidebarOpen ? 'min-[1024px]:w-64' : 'min-[1024px]:w-20'
          }`}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {sidebarOpen && (
            <h1 className="text-xl font-bold text-gray-800 whitespace-nowrap">勤怠管理システム</h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {menuItems.map(item => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => setActiveMenu(item.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${activeMenu === item.id
                      ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <div className="flex items-center">
                      <Icon className="w-5 h-5" />
                      {sidebarOpen && (
                        <span className="ml-3 font-medium whitespace-nowrap">{item.label}</span>
                      )}
                    </div>
                    {item.badge !== null && sidebarOpen && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {!isShiftOnly && (
          <div className="px-4 pb-2">
            <button
              onClick={() => window.location.assign('/care')}
              className="w-full flex items-center rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-cyan-800 transition-all hover:bg-cyan-100"
              title="ケア業務へ"
            >
              <UsersRound className="w-5 h-5" />
              {sidebarOpen && (
                <span className="ml-3 font-medium whitespace-nowrap">ケア業務へ</span>
              )}
            </button>
          </div>
        )}

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
              {adminInitial}
            </div>
            {sidebarOpen && (
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{adminDisplayName}</p>
                <p className="text-xs text-gray-500 whitespace-nowrap">{adminEmail}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isMobileSidebarOpen && (
        <div className="fixed inset-0 z-40 flex justify-end lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMobileSidebarOpen(false)}
            aria-hidden="true"
          />
          <div className="relative ml-auto h-full w-64 bg-white shadow-xl">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <h1 className="text-xl font-bold text-gray-800 whitespace-nowrap">勤怠管理システム</h1>
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="rounded-lg p-2 hover:bg-gray-100"
                  aria-label="メニューを閉じる"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto p-4">
                <ul className="space-y-2">
                  {menuItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => {
                            setActiveMenu(item.id);
                            setIsMobileSidebarOpen(false);
                          }}
                          className={`flex w-full items-center justify-between rounded-lg p-3 transition-all ${activeMenu === item.id
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          <div className="flex items-center">
                            <Icon className="h-5 w-5" />
                            <span className="ml-3 font-medium whitespace-nowrap">{item.label}</span>
                          </div>
                          {item.badge !== null && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
              {!isShiftOnly && (
                <div className="px-4 pb-2">
                  <button
                    onClick={() => window.location.assign('/care')}
                    className="flex w-full items-center rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-cyan-800 transition-all hover:bg-cyan-100"
                  >
                    <UsersRound className="h-5 w-5" />
                    <span className="ml-3 font-medium whitespace-nowrap">ケア業務へ</span>
                  </button>
                </div>
              )}
              <div className="border-t border-gray-200 p-4">
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                    {adminInitial}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{adminDisplayName}</p>
                    <p className="text-xs text-gray-500 whitespace-nowrap">{adminEmail}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* メインコンテンツ */}
      <main className="min-h-0 min-w-0 flex-1 overflow-auto">
        {/* ヘッダー */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
                  {activeMenu === 'employees' && '従業員管理'}
                  {activeMenu === 'daily' && '日次ビュー'}
                  {activeMenu === 'monthly' && '月次レポート'}
                  {activeMenu === 'monthlyPrint' && '印刷ページ'}
                  {activeMenu === 'inventory' && '在庫管理'}
                  {activeMenu === 'overtimeReports' && '時間外報告'}
                  {activeMenu === 'leaveRequests' && '有給申請'}
                  {activeMenu === 'payroll' && '給与計算'}
                  {activeMenu === 'adminReport' && '行政報告'}
                  {activeMenu === 'settings' && '設定'}
                  {activeMenu === 'shift' && 'シフト管理'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {activeMenu === 'employees' && '従業員情報の管理と編集'}
                  {activeMenu === 'daily' && '日次の勤怠状況確認'}
                  {activeMenu === 'monthly' && '月次の勤怠集計と分析'}
                  {activeMenu === 'monthlyPrint' && '行政提出用の勤怠印刷'}
                  {activeMenu === 'inventory' && '消耗品の残日数と発注候補を管理'}
                  {activeMenu === 'overtimeReports' && '時間外勤務の申告と紙の届出の照合'}
                  {activeMenu === 'leaveRequests' && '有給申請の確認と承認'}
                  {activeMenu === 'payroll' && '月次給与計算と出力'}
                  {activeMenu === 'adminReport' && '行政提出用の統計レポート'}
                  {activeMenu === 'settings' && 'システム設定'}
                  {activeMenu === 'shift' && '月次シフト表の作成と管理'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileSidebarOpen(prev => !prev)}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:hidden"
                  aria-label={isMobileSidebarOpen ? 'メニューを閉じる' : 'メニューを開く'}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* コンテンツ */}
        <div className="p-6">
          {!isShiftOnly && calculationSettingsError && (
            <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <p>{calculationSettingsError}</p>
              <button type="button" className="mt-2 underline" onClick={() => isSettingsDirty ? setActiveMenu('settings') : window.location.reload()}>
                {isSettingsDirty ? '設定を確認する' : '再読み込み'}
              </button>
            </div>
          )}
          {!isShiftOnly && !calculationReady && !calculationSettingsError && ['daily', 'monthly', 'payroll', 'monthlyPrint'].includes(activeMenu) && (
            <p role="status" className="p-4 text-sm text-gray-600">従業員別の集計設定を読み込んでいます...</p>
          )}
          {/* 従業員管理 */}
          {activeMenu === 'employees' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 whitespace-nowrap">従業員一覧</h3>
                  <div className="flex items-center gap-3">
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setEmployeeTab('active')}
                        className={`px-3 py-1 text-sm rounded-md whitespace-nowrap transition-all ${employeeTab === 'active'
                          ? 'bg-white shadow-sm text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        在籍中
                      </button>
                      <button
                        onClick={() => setEmployeeTab('retired')}
                        className={`px-3 py-1 text-sm rounded-md whitespace-nowrap transition-all ${employeeTab === 'retired'
                          ? 'bg-white shadow-sm text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        退職者
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setEmployeeMutationError(null);
                        setEditingEmployee(null);
                        setShowEmployeeModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      新規登録
                    </button>
                  </div>
                </div>

                {employeesError && (
                  <div className="px-6 py-4 text-sm text-red-700 bg-red-50 border-b border-red-100">
                    {employeesError}
                  </div>
                )}
                {employeesLoading && !employeesError && (
                  <div className="px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    従業員データを読み込み中です...
                  </div>
                )}
                {employeeMutationError && !showEmployeeModal && (
                  <div className="px-6 py-4 text-sm text-red-700 bg-red-50 border-b border-red-100">
                    {employeeMutationError}
                  </div>
                )}
                {qualificationsError && (
                  <div className="px-6 py-4 text-sm text-amber-700 bg-amber-50 border-b border-amber-100">
                    {qualificationsError}
                  </div>
                )}

                <div className="px-6 py-3 flex items-center gap-2 flex-wrap border-b border-gray-200 bg-gray-50">
                  <span className="text-sm text-gray-600 mr-2">職種フィルタ:</span>
                  {([
                    { key: 'all', label: '全て' },
                    { key: 'care', label: '介護' },
                    { key: 'nurse', label: '看護' },
                    { key: 'cook', label: '調理' },
                    { key: 'other', label: 'その他' }
                  ] as const).map(f => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setJobTypeFilter(f.key)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        jobTypeFilter === f.key
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                  {sortBy !== null && (
                    <button
                      type="button"
                      onClick={() => setSortBy(null)}
                      className="ml-2 px-3 py-1 text-xs text-gray-600 rounded-md hover:bg-gray-200"
                    >
                      並び替えリセット
                    </button>
                  )}
                  <span className="ml-auto text-xs text-gray-500">
                    {sortBy === null ? '並び順: 正社員→その他→パート / 介護→看護→調理→他' : '列ヘッダで並び替え中'}
                  </span>
                </div>
                <div className="max-h-[calc(100dvh-18rem)] overflow-auto overscroll-contain">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20 bg-white shadow-sm border-b border-gray-200">
                      <tr>
                        <th
                          onClick={() => handleSort('employeeCode')}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-1">
                            従業員コード
                            <ArrowUpDown className="w-4 h-4" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('name')}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-1">
                            氏名
                            <ArrowUpDown className="w-4 h-4" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('employmentType')}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-1">
                            雇用形態
                            <ArrowUpDown className="w-4 h-4" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('jobType')}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-1">
                            職種
                            <ArrowUpDown className="w-4 h-4" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('hireDate')}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-1">
                            入社日
                            <ArrowUpDown className="w-4 h-4" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          資格
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 overflow-visible">
                      {sortedEmployees.map(employee => {
                        const visibleQualifications = employee.qualifications.slice(0, 2);
                        const extraQualifications = employee.qualifications.slice(2);
                        const popoverId = `qualifications-popover-${employee.id}`;
                        const triggerId = `qualifications-trigger-${employee.id}`;
                        const isPopoverOpen = activeQualificationsEmployeeId === employee.id;

                        return (
                          <tr key={employee.id} className="relative hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.employeeCode ?? '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                  {employee.name.charAt(0)}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                                  <div className="text-sm text-gray-500">
                                    {employee.transportation === '車' ? (
                                      <span className="flex items-center">
                                        <Car className="w-4 h-4 mr-1" />
                                        車通勤
                                        {typeof employee.commuteDistanceKm === 'number' && !Number.isNaN(employee.commuteDistanceKm)
                                          ? ` (往復${formatDistanceKm(employee.commuteDistanceKm)}km)`
                                          : ''}
                                      </span>
                                    ) : (
                                      <span className="flex items-center">
                                        <Train className="w-4 h-4 mr-1" />
                                        公共交通機関
                                        {typeof employee.commuteCostYen === 'number' && !Number.isNaN(employee.commuteCostYen)
                                          ? ` (往復${yenFormatter.format(employee.commuteCostYen)})`
                                          : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.employmentType}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.jobType}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.hireDate || '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              <div className="hidden flex-wrap gap-1 lg:flex">
                                {visibleQualifications.map(qual => (
                                  <span key={qual} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                    {qual}
                                  </span>
                                ))}
                                {extraQualifications.length > 0 && (
                                  <div className="relative inline-block group">
                                    <span
                                      className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs"
                                      aria-label={`資格: ${extraQualifications.join('、')}`}
                                    >
                                      +{extraQualifications.length}
                                    </span>
                                    <div
                                      role="tooltip"
                                      className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-48 -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 opacity-0 shadow-lg ring-1 ring-black/5 transition-opacity duration-150 group-hover:opacity-100"
                                    >
                                      <p className="mb-1 text-[11px] font-semibold text-gray-700">その他の資格</p>
                                      <ul className="space-y-1">
                                        {extraQualifications.map(qual => (
                                          <li key={qual} className="text-[11px]">{qual}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div
                                className="relative mt-1 lg:hidden"
                                data-qualifications-root="1"
                                data-employee-id={employee.id}
                              >
                                {employee.qualifications.length > 0 ? (
                                  <>
                                    <button
                                      type="button"
                                      id={triggerId}
                                      aria-controls={popoverId}
                                      aria-expanded={isPopoverOpen}
                                      aria-haspopup="dialog"
                                      onClick={() =>
                                        setActiveQualificationsEmployeeId(prev =>
                                          prev === employee.id ? null : employee.id
                                        )
                                      }
                                      className="rounded border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
                                    >
                                      +{employee.qualifications.length}
                                    </button>
                                    {isPopoverOpen && (
                                      <div
                                        id={popoverId}
                                        role="dialog"
                                        aria-modal="false"
                                        aria-labelledby={triggerId}
                                        className="absolute right-0 z-20 mt-2 max-h-48 w-52 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-lg"
                                      >
                                        <p className="mb-2 font-semibold text-gray-700">資格</p>
                                        <ul className="space-y-1 text-gray-800">
                                          {employee.qualifications.map(qual => (
                                            <li key={qual} className="truncate">{qual}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-500">-</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <button
                                  disabled={employeeMutationLoading}
                                  onClick={() => {
                                    setEmployeeMutationError(null);
                                    setEditingEmployee(employee);
                                    setShowEmployeeModal(true);
                                  }}
                                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                {employeeTab === 'active' ? (
                                  <button
                                    disabled={employeeMutationLoading}
                                    onClick={() => handleRetireEmployee(employee.id)}
                                    className="p-2 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <UserX className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    disabled={employeeMutationLoading}
                                    onClick={() => handleRestoreEmployee(employee.id)}
                                    className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  disabled={employeeMutationLoading}
                                  onClick={() => handleDeleteEmployee(employee.id)}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {!employeesLoading && sortedEmployees.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-6 py-8 text-center text-sm text-gray-500"
                          >
                            従業員データが登録されていません。
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 日次ビュー */}
          {activeMenu === 'daily' && calculationReady && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => handleSelectDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={() => shiftSelectedDate(-1)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    &laquo;
                  </button>
                  <button
                    onClick={handleSelectToday}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  >
                    今日
                  </button>
                  <button
                    onClick={() => shiftSelectedDate(1)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    &raquo;
                  </button>
                </div>
                <button
                  onClick={handleBulkPunchClick}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  <Clock className="w-4 h-4" />
                  一括打刻
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {selectedDate} の勤怠状況
                  </h3>
                  <button
                    onClick={handleManualAddClick}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    手動追加
                  </button>
                </div>

                {attendanceError && (
                  <div className="px-6 py-4 text-sm text-red-700 bg-red-50 border-b border-red-100">
                    {attendanceError}
                  </div>
                )}
                {attendanceLoading && !attendanceError && (
                  <div className="px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    勤怠データを読み込み中です...
                  </div>
                )}

                <div className="max-h-[calc(100dvh-18rem)] overflow-auto overscroll-contain">
                  <table className="w-full">
                    <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          従業員
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          勤務区分
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          出勤
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          退勤
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          休憩
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          実働
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          状態
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          備考
                        </th>
                        <th className="sticky right-0 z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-lg">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {attendanceData[selectedDate]?.map(record => {
                        const displayName = resolveEmployeeNameFromRecord(record);
                        const statusBadge = getDailyStatusBadge(record);
                        return (
                          <tr key={record.id} className="group hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                  {displayName.charAt(0)}
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900">{displayName}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.shiftType === 'day' && '日勤'}
                              {record.shiftType === 'night' && '夜勤'}
                              {record.shiftType === 'dayoff' && '公休'}
                              {record.shiftType === 'paid' && '有給'}
                              {record.shiftType === 'holiday_work' && '休日出勤'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.clockIn || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.clockOut || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.breakMinutes > 0 ? `${record.breakMinutes}分` : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatMinutesToTime(record.totalMinutes)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}
                              >
                                {statusBadge.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 max-w-xs whitespace-normal">
                              {renderInlineNoteCell(
                                `daily:${record.id}`,
                                record.notes,
                                (value) => saveDailyNote(record, value)
                              )}
                            </td>
                            <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-sm font-medium shadow-lg group-hover:bg-gray-50">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingAttendance(record);
                                    setShowEditAttendanceModal(true);
                                  }}
                                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAttendanceRecord(record)}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {!attendanceLoading && (!attendanceData[selectedDate] || attendanceData[selectedDate]?.length === 0) && (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-6 py-8 text-center text-sm text-gray-500"
                          >
                            この日の勤怠データは登録されていません。
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 月次レポート */}
          {activeMenu === 'monthly' && calculationReady && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => {
                      setHasManualMonthSelection(true);
                      setSelectedMonth(e.target.value);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={employeeCodeInputForMonthly}
                    onChange={(e) => handleMonthlyEmployeeCodeChange(e.target.value)}
                    placeholder="従業員コード"
                    maxLength={5}
                    inputMode="numeric"
                    pattern="\d*"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 w-32"
                  />
                  <select
                    value={selectedEmployeeForReport}
                    onChange={(e) => {
                      setSelectedEmployeeForReport(e.target.value);
                      const emp = employees.find(emp => emp.id === e.target.value);
                      setEmployeeCodeInputForMonthly(emp?.employeeCode ?? '');
                    }}
                    disabled={monthlyReportEmployees.length === 0}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    {monthlyReportEmployees.length === 0 && (
                      <option value="">従業員データなし</option>
                    )}
                    {monthlyReportEmployees.map(emp => {
                      const label = emp.status === 'retired' && emp.retirementDate
                        ? `${emp.name}（退職 ${emp.retirementDate}）`
                        : emp.name;
                      return (
                        <option key={emp.id} value={emp.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportMonthlyCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                    disabled={!selectedEmployeeReport || monthlyLoading}
                  >
                    <Download className="w-4 h-4" />
                    CSV出力
                  </button>
                  {/* <button
                    onClick={exportDailyCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                    disabled={monthlyDailyRowsForCsv.length === 0 || monthlyLoading}
                  >
                    <Download className="w-4 h-4" />
                    日次CSV出力
                  </button> */}
                </div>
              </div>

              {monthlyError && (
                <div className="px-6 py-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg">
                  {monthlyError}
                </div>
              )}
              {monthlyLoading && !monthlyError && (
                <div className="px-6 py-4 text-sm text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                  月次データを読み込み中です...
                </div>
              )}

              {/* 集計カード */}
              {selectedEmployeeReport && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">出勤日数 (出勤回数)</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {selectedEmployeeReport.totalWorkDays}
                          <span className="text-lg text-gray-500 ml-1">
                            ({selectedEmployeeReport.attendanceCount ?? 0})
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="p-3 bg-indigo-100 rounded-lg">
                        <Clock className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">夜勤回数</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {selectedEmployeeReport.totalNightShifts ?? 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Calendar className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">有給</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {selectedEmployeeReport.totalPaidLeave ?? 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="p-3 bg-amber-100 rounded-lg">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">実働時間</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatMinutesToTime(selectedEmployeeReport.totalActualMinutes)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <BarChart3 className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">残業時間</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatMinutesToTime(selectedEmployeeReport.totalOvertimeMinutes)}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* 詳細テーブル */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {selectedEmployeeName} の月次データ
                  </h3>
                  <button
                    onClick={() => {
                      setShowEditMonthlyModal(true);
                      setEditingMonthlyRecord(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    手動追加
                  </button>
                </div>

                <div
                  data-testid="monthly-report-scroll"
                  className="max-h-[calc(100dvh-26rem)] overflow-auto"
                >
                  <table className="w-full">
                    <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          日付
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          勤務区分
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          出勤
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          退勤
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          休憩
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          実働
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          残業
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          備考
                        </th>
                        <th className="sticky right-0 z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-lg">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedEmployeeMonthlyData.map(record => {
                        const overtimeMinutes = calculateOvertime(record);
                        return (
                          <tr key={record.id ?? record.date} className="group hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.shiftType === 'day' && '日勤'}
                              {record.shiftType === 'night' && '夜勤'}
                              {record.shiftType === 'dayoff' && '公休'}
                              {record.shiftType === 'paid' && '有給'}
                              {record.shiftType === 'holiday_work' && '休日出勤'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.clockIn || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.clockOut || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.breakMinutes > 0 ? `${record.breakMinutes}分` : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.shiftType === 'paid'
                                ? '8時間00分(有給)'
                                : record.shiftType === 'dayoff'
                                  ? '-'
                                  : formatMinutesToTime(record.totalMinutes)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {overtimeMinutes > 0 ? formatMinutesToTime(overtimeMinutes) : '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 max-w-xs whitespace-normal">
                              {renderInlineNoteCell(
                                `monthly:${selectedEmployeeForReport}:${record.id ?? record.date}`,
                                record.notes,
                                (value) => saveMonthlyNote(selectedEmployeeForReport, record, value)
                              )}
                            </td>
                            <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-sm font-medium shadow-lg group-hover:bg-gray-50">
                              <div className="flex items-center gap-2">
                                <button
                                  aria-label={`${record.date}の勤怠を編集`}
                                  disabled={monthlyLoading}
                                  onClick={() => {
                                    setEditingMonthlyRecord(record);
                                    setShowEditMonthlyModal(true);
                                  }}
                                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMonthlyRecord(record)}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {!monthlyLoading && selectedEmployeeMonthlyData.length === 0 && (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-6 py-8 text-center text-sm text-gray-500"
                          >
                            この従業員の月次データは登録されていません。
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 有給申請 */}
          {activeMenu === 'leaveRequests' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {(['all', 'pending', 'approved', 'rejected', 'cancelled'] as const).map(filter => (
                      <button
                        key={filter}
                        onClick={() => setLeaveRequestFilter(filter)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          leaveRequestFilter === filter
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {filter === 'all' && '全て'}
                        {filter === 'pending' && `未処理 (${leaveRequests.filter(r => r.status === 'pending').length})`}
                        {filter === 'approved' && '承認済み'}
                        {filter === 'rejected' && '却下済み'}
                        {filter === 'cancelled' && '取消済み'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 一括操作ボタン */}
                {selectedLeaveRequestIds.length > 0 && (
                  <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-4">
                    <span className="text-sm font-medium text-blue-700">
                      {selectedLeaveRequestIds.length}件選択中
                    </span>
                    <button
                      onClick={handleBulkApproveLeaveRequests}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                    >
                      選択した申請を一括承認
                    </button>
                    <button
                      onClick={handleBulkRejectLeaveRequests}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                    >
                      選択した申請を一括却下
                    </button>
                  </div>
                )}

                <div className="p-6">
                  {leaveRequestsLoading ? (
                    <div className="text-center py-8 text-gray-500">読み込み中...</div>
                  ) : leaveRequestsError ? (
                    <div className="text-red-600 bg-red-50 border border-red-100 rounded-lg p-4">{leaveRequestsError}</div>
                  ) : (
                    <div className="max-h-[calc(100dvh-22rem)] overflow-auto overscroll-contain">
                      <table className="w-full">
                        <thead className="sticky top-0 z-30 bg-white shadow-sm">
                          <tr className="border-b border-gray-200">
                            <th className="w-10 py-3 px-4">
                              <input
                                type="checkbox"
                                checked={
                                  leaveRequests
                                    .filter(req => (leaveRequestFilter === 'all' || req.status === leaveRequestFilter) && req.status === 'pending')
                                    .length > 0 &&
                                  leaveRequests
                                    .filter(req => (leaveRequestFilter === 'all' || req.status === leaveRequestFilter) && req.status === 'pending')
                                    .every(req => selectedLeaveRequestIds.includes(req.id))
                                }
                                onChange={(e) => {
                                  const pendingIds = leaveRequests
                                    .filter(req => (leaveRequestFilter === 'all' || req.status === leaveRequestFilter) && req.status === 'pending')
                                    .map(req => req.id);
                                  if (e.target.checked) {
                                    setSelectedLeaveRequestIds(prev => Array.from(new Set([...prev, ...pendingIds])));
                                  } else {
                                    setSelectedLeaveRequestIds(prev => prev.filter(id => !pendingIds.includes(id)));
                                  }
                                }}
                                className="h-4 w-4 text-blue-500 rounded"
                                disabled={leaveRequests.filter(req => (leaveRequestFilter === 'all' || req.status === leaveRequestFilter) && req.status === 'pending').length === 0}
                              />
                            </th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">従業員</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">申請日</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">有給期間</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">日数</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">ステータス</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">アクション</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaveRequests
                            .filter(req => leaveRequestFilter === 'all' || req.status === leaveRequestFilter)
                            .map(req => {
                              const startDate = new Date(req.startDate);
                              const endDate = new Date(req.endDate);
                              const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                              const createdDate = new Date(req.createdAt);

                              return (
                                <tr key={req.id} className={`border-b border-gray-100 ${req.status === 'pending' ? 'bg-yellow-50' : ''}`}>
                                  <td className="py-3 px-4">
                                    {req.status === 'pending' ? (
                                      <input
                                        type="checkbox"
                                        checked={selectedLeaveRequestIds.includes(req.id)}
                                        onChange={() => {
                                          setSelectedLeaveRequestIds(prev =>
                                            prev.includes(req.id)
                                              ? prev.filter(id => id !== req.id)
                                              : [...prev, req.id]
                                          );
                                        }}
                                        className="h-4 w-4 text-blue-500 rounded"
                                      />
                                    ) : (
                                      <span className="block w-4" />
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900">{req.employeeName}</td>
                                  <td className="py-3 px-4 text-sm text-gray-600">
                                    {createdDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600">
                                    {req.startDate === req.endDate
                                      ? startDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
                                      : `${startDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} 〜 ${endDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}`}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-center text-gray-600">{days}日</td>
                                  <td className="py-3 px-4 text-center">
                                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                      req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      req.status === 'approved' ? 'bg-green-100 text-green-800' :
                                      req.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {req.status === 'pending' && '未処理'}
                                      {req.status === 'approved' && '承認済み'}
                                      {req.status === 'rejected' && '却下済み'}
                                      {req.status === 'cancelled' && '取消済み'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    {req.status === 'pending' ? (
                                      <div className="flex justify-center gap-2">
                                        <button
                                          onClick={() => handleApproveLeaveRequest(req)}
                                          className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 flex items-center gap-1"
                                        >
                                          <Check className="w-4 h-4" />
                                          承認
                                        </button>
                                        <button
                                          onClick={() => setRejectingRequest(req)}
                                          className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 flex items-center gap-1"
                                        >
                                          <XCircle className="w-4 h-4" />
                                          却下
                                        </button>
                                        <button
                                          onClick={() => handleCancelLeaveRequest(req)}
                                          className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 flex items-center gap-1"
                                        >
                                          <X className="w-4 h-4" />
                                          取消
                                        </button>
                                      </div>
                                    ) : req.status === 'approved' ? (
                                      <button
                                        onClick={() => handleCancelLeaveRequest(req)}
                                        className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 inline-flex items-center gap-1"
                                      >
                                        <X className="w-4 h-4" />
                                        取消
                                      </button>
                                    ) : req.status === 'rejected' && req.rejectionReason ? (
                                      <span className="text-xs text-gray-500" title={req.rejectionReason}>
                                        理由: {req.rejectionReason.length > 10 ? req.rejectionReason.slice(0, 10) + '...' : req.rejectionReason}
                                      </span>
                                    ) : req.status === 'cancelled' && req.cancellationReason ? (
                                      <span className="text-xs text-gray-500" title={req.cancellationReason}>
                                        理由: {req.cancellationReason.length > 10 ? req.cancellationReason.slice(0, 10) + '...' : req.cancellationReason}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          {leaveRequests.filter(req => leaveRequestFilter === 'all' || req.status === leaveRequestFilter).length === 0 && (
                            <tr>
                              <td colSpan={7} className="py-8 text-center text-gray-500">
                                {leaveRequestFilter === 'all' ? '有給申請がありません' : `${leaveRequestFilter === 'pending' ? '未処理' : leaveRequestFilter === 'approved' ? '承認済み' : leaveRequestFilter === 'cancelled' ? '取消済み' : '却下済み'}の申請がありません`}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'overtimeReports' && !isShiftOnly && (
            <OvertimeReviewTab supabaseClient={supabaseClient} employees={employees} selectedMonth={selectedMonth} closingDay={closingDay}
              onMonthChange={month => { setSelectedMonth(month); setHasManualMonthSelection(true); }} />
          )}

          {/* 給与計算 */}
          {activeMenu === 'payroll' && calculationReady && (
            <PayrollTab
              employees={employees}
              monthlyData={monthlyData}
              selectedMonth={selectedMonth}
              closingDay={closingDay}
              calculateOvertime={calculateOvertime}
              supabaseClient={supabaseClient}
              monthlyLoading={monthlyLoading}
              payrollSourceFingerprint={payrollSourceFingerprint}
              payrollSourceError={payrollSourceError ?? monthlyError ?? undefined}
              onReloadMonthly={() => { void fetchMonthly(); }}
              onMonthChange={(month: string) => {
                setSelectedMonth(month);
                setHasManualMonthSelection(true);
              }}
            />
          )}

          {/* 行政報告 */}
          {!isShiftOnly && (activeMenu === 'adminReport' || adminReportVisited) && (
            <div hidden={activeMenu !== 'adminReport'}><AdminReportTabbed employees={employees} /></div>
          )}

          {activeMenu === 'monthlyPrint' && calculationReady && (
            <MonthlyPrintTab
              employees={employees}
              supabaseClient={supabaseClient}
              selectedMonth={selectedMonth}
              dayBreakMinutes={dayBreakMinutes}
              nightBreakMinutes={nightBreakMinutes}
              roundUpTime={roundUpTime}
              calculateActualMinutes={calculateActualMinutes}
              calculateOvertime={calculateOvertime}
            />
          )}

          {activeMenu === 'inventory' && (
            <InventoryPage storageMode="supabase" embedded />
          )}

          {activeMenu === 'subsidySimulator' && (
            <SubsidySimulatorTab employees={employees} />
          )}

          {/* シフト管理 */}
          {activeMenu === 'shift' && (
            <ShiftManagementTab employees={employees} />
          )}

          {/* 設定 */}
          {activeMenu === 'settings' && (
            <div className="space-y-6">
              <MinuteEmployeeSettings employees={employees} rules={calculationRules}
                loading={calculationRulesLoading || employeesLoading || !!calculationRulesError} onSave={saveMinuteTargets} />
              <OvertimeScheduleSettings supabaseClient={supabaseClient} employees={employees} />
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900">基本設定</h3>
                </div>

                <div className="p-6 space-y-6">
                  {settingsError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {settingsError}
                    </div>
                  )}
                  {settingsLoading && !settingsError && (
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-xs text-blue-700">
                      設定を処理しています…
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">締日設定</h3>
                    <div className="flex items-center gap-4">
                      <select
                        value={closingDay}
                        onChange={(e) => setClosingDay(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      >
                        <option value="month-end">月末</option>
                        <option value="15">15日</option>
                        <option value="20">20日</option>
                        <option value="25">25日</option>
                      </select>
                      <span className="text-sm text-gray-600">
                        {closingDay === 'month-end'
                          ? '毎月月末に締め処理を行います'
                          : `毎月${closingDay}日に締め処理を行います`}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">出勤時間設定</h3>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={roundUpClockIn}
                          onChange={(e) => setRoundUpClockIn(e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-600">
                          出勤時間を30分単位で切り上げ
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">休憩時間設定</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">日勤</h4>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">8時間以上勤務</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={dayBreakMinutes}
                              onChange={event =>
                                setDayBreakMinutes(
                                  Math.max(0, Number.parseInt(event.target.value, 10) || 0)
                                )
                              }
                              className="w-20 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">分</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">夜勤</h4>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">8時間以上勤務</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={nightBreakMinutes}
                              onChange={event =>
                                setNightBreakMinutes(
                                  Math.max(0, Number.parseInt(event.target.value, 10) || 0)
                                )
                              }
                              className="w-20 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">分</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">残業計算設定</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-900">日勤基準時間</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={dayOvertimeBaseHours}
                            onChange={event =>
                              setDayOvertimeBaseHours(
                                Math.max(0, Number.parseInt(event.target.value, 10) || 0)
                              )
                            }
                            className="w-20 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                          />
                          <span className="text-sm text-gray-600">時間</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-900">夜勤基準時間</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={nightOvertimeBaseHours}
                            onChange={event =>
                              setNightOvertimeBaseHours(
                                Math.max(0, Number.parseInt(event.target.value, 10) || 0)
                              )
                            }
                            className="w-20 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                          />
                          <span className="text-sm text-gray-600">時間(2日分)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveSettings}
                      disabled={!isSettingsDirty || settingsLoading}
                      className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${isSettingsDirty && !settingsLoading
                        ? 'bg-blue-600 text-white shadow hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2'
                        : 'bg-blue-50/95 text-blue-600 border border-blue-100 cursor-not-allowed'
                        }`}
                    >
                      {settingsLoading ? '保存中...' : '設定を保存'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {showEmployeeModal && (
        <EmployeeModal
          employee={editingEmployee}
          onClose={() => {
            setShowEmployeeModal(false);
            setEditingEmployee(null);
          }}
        />
      )}

      {showQualificationManager && (
        <QualificationManagerModal
          onClose={() => {
            setShowQualificationManager(false);
          }}
        />
      )}

      {showManualAttendanceModal && (
        <ManualAttendanceModal
          date={selectedDate}
          employees={employees}
          initialEmployeeId={manualAttendanceInitialEmployeeId}
          onClose={() => {
            setShowManualAttendanceModal(false);
            setManualAttendanceInitialEmployeeId(null);
          }}
          onSave={handleManualAttendanceSave}
        />
      )}

      {showBulkPunchModal && (
        <BulkPunchModal
          date={selectedDate}
          employees={employees}
          existingRecords={attendanceData[selectedDate] ?? []}
          onClose={() => setShowBulkPunchModal(false)}
          onSave={handleBulkPunchSave}
        />
      )}

      {showEditAttendanceModal && (
        <EditAttendanceModal
          attendance={editingAttendance}
          onClose={() => {
            setShowEditAttendanceModal(false);
            setEditingAttendance(null);
          }}
          onSave={handleEditAttendance}
        />
      )}

      {showEditMonthlyModal && (
        <EditMonthlyModal
          record={editingMonthlyRecord}
          onClose={() => {
            setShowEditMonthlyModal(false);
            setEditingMonthlyRecord(null);
          }}
          onSave={handleEditMonthlyRecord}
        />
      )}

      {/* 有給申請却下モーダル */}
      {rejectingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">有給申請の却下</h3>
              <button
                onClick={() => {
                  setRejectingRequest(null);
                  setRejectionReason('');
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">{rejectingRequest.employeeName}</p>
                <p className="text-xs text-gray-600">
                  {(() => {
                    const startDate = new Date(rejectingRequest.startDate);
                    const endDate = new Date(rejectingRequest.endDate);
                    const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    return rejectingRequest.startDate === rejectingRequest.endDate
                      ? `${startDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} (${days}日間)`
                      : `${startDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} 〜 ${endDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} (${days}日間)`;
                  })()}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  却下理由 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="却下理由を入力してください"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setRejectingRequest(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
              >
                キャンセル
              </button>
              <button
                onClick={handleRejectLeaveRequest}
                disabled={!rejectionReason.trim()}
                className={`px-4 py-2 rounded-lg text-sm flex items-center gap-1 ${
                  rejectionReason.trim()
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <XCircle className="w-4 h-4" />
                却下する
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsSaveNotice && (
        <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none px-4 pb-10 sm:pb-16">
          <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/95 px-6 py-4 shadow-lg backdrop-blur text-sm text-blue-900">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-white shadow inner-shadow">
              ✓
            </div>
            <div className="space-y-1">
              <p className="font-semibold tracking-wide">{settingsSaveNotice}</p>
              <p className="text-xs text-blue-700/80">設定が最新の状態になりました。</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
